import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, query, where, onSnapshot, doc, getDoc, getDocs, setDoc, arrayUnion, deleteDoc } from 'firebase/firestore';
import { COMPANY_INFO, DISCLAIMER_FULL } from '../constants/constants';
import { onAuthStateChanged, deleteUser } from 'firebase/auth';
import { db, auth, app } from '../services/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { gerarParecerIA } from '../services/parecerIAService'; // mesma Cloud Function que o app usa
import { nomeArquivoRelatorio } from '../services/nomeArquivoPdf';
import { cancelarAssinaturaAsaas } from '../services/asaasService';
import BlocoCancelarAssinatura from '../components/BlocoCancelarAssinatura';
import AvisoCustoNaoRecorrente from '../components/AvisoCustoNaoRecorrente';
import CheckoutAsaasCard from '../components/CheckoutAsaasCard';
import html2pdf from 'html2pdf.js';
import {
  METODO_PARECER,
  calculateCoreMetrics, calculateHealthStatus,
  calculateExtendedMetrics, calculateProjection, calculateStochasticProjection
} from '../components/financeEngine'; // ajuste o caminho pro seu projeto
import { gerarInsights } from '../components/parecerEngine'; // ajuste o caminho pro seu projeto
import ReportEngine from '../components/ReportEngine'; // ajuste o caminho pro seu projeto
import {
  Activity, Landmark, Briefcase, Lock, LogOut, User,
  ShieldCheck, Settings, Shield,
  Download, Sliders, Sun, Moon, Pencil,
  Save, X, FileText, Building2, Check, CheckCircle2, Circle, Trash2, Plus, Bot,
  HelpCircle, Wallet, Scale, Target, ClipboardList, StickyNote, Tag,
  Coins, RefreshCw, AlertTriangle, CreditCard, BarChart3, Zap, FileSignature, Info, WifiOff,
  Sparkles, Crown, ChevronDown
} from 'lucide-react';

// ===================================================================
// 🏛️ ESTILOS GLOBAIS (mesmo padrão do DashboardInterface do Consultor,
// incluindo o tema claro — .theme-light é o MESMO bloco CSS usado lá,
// pra manter os dois dashboards com a cara idêntica quando alternados)
// ===================================================================
const GestorDashboardStyles = () => (
  <style dangerouslySetInnerHTML={{
    __html: `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;700;900&display=swap');

    .font-dashboard {
      font-family: 'Inter', sans-serif !important;
    }

    .custom-scroll::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    .custom-scroll::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.02);
      border-radius: 10px;
    }
    .custom-scroll::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 10px;
    }
    .custom-scroll::-webkit-scrollbar-thumb:hover {
      background: #bef264;
    }

    /* ============== TEMA CLARO ============== */
    /* Sobrescreve as cores escuras via seletor de atributo, ativado */
    /* quando a classe .theme-light está ativa no container raiz.     */
    .theme-light { background-color: #f8fafc !important; color: #1e293b !important; }
    .theme-light [class*="bg-[#010208]"],
    .theme-light [class*="bg-[#020617]"],
    .theme-light [class*="bg-[#1a1a1a]"],
    .theme-light [class*="from-[#"],
    .theme-light [class*="to-[#"],
    .theme-light [class*="via-[#"] { background-color: #ffffff !important; background-image: none !important; }
    .theme-light [class*="from-slate-900"],
    .theme-light [class*="to-slate-900"] { background-color: #f1f5f9 !important; background-image: none !important; }
    .theme-light [class*="bg-slate-900/"],
    .theme-light [class*="bg-black/"] { background-color: #f1f5f9 !important; }
    .theme-light .phoenix-caixa-check { background-color: #ffffff !important; border-color: #94a3b8 !important; }
    .theme-light [class*="bg-white/5"],
    .theme-light [class*="bg-white/10"],
    .theme-light [class*="bg-white/20"] { background-color: #e2e8f0 !important; }
    .theme-light [class*="text-white"] { color: #0f172a !important; }
    .theme-light [class*="text-slate-200"],
    .theme-light [class*="text-slate-300"] { color: #334155 !important; }
    .theme-light [class*="text-slate-400"] { color: #4b5a70 !important; }
    .theme-light [class*="text-slate-500"],
    .theme-light [class*="text-slate-600"],
    .theme-light [class*="text-slate-700"] { color: #5f6e84 !important; }
    .theme-light [class*="border-white/"] { border-color: #e2e8f0 !important; }
    .theme-light input,
    .theme-light select,
    .theme-light textarea { color: #0f172a !important; background-color: #ffffff; }
    .theme-light ::placeholder { color: #94a3b8 !important; }
    .theme-light .custom-scroll::-webkit-scrollbar-track { background: rgba(0,0,0,0.03); }
    .theme-light .custom-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); }

    /* ============== CORREÇÃO DE CONTRASTE: VERDE-LIMÃO NO TEMA CLARO ============== */
    /* text-lime-400/300 foram calibrados pra brilhar num fundo escuro —
       no tema claro (fundo quase branco), esse mesmo verde-limão fica
       claro demais e difícil de ler. Escurece pra um verde mais forte só
       no modo claro, sem mexer no tema escuro. */
    .theme-light [class*="text-lime-300"],
    .theme-light [class*="text-lime-400"] { color: #4d7c0f !important; }
    .theme-light [class*="text-lime-500"] { color: #3f6212 !important; }
    .theme-light [class*="text-red-300"],
    .theme-light [class*="text-red-400"] { color: #dc2626 !important; }
    .theme-light [class*="text-green-300"],
    .theme-light [class*="text-green-400"],
    .theme-light [class*="text-emerald-300"],
    .theme-light [class*="text-emerald-400"] { color: #15803d !important; }
    .theme-light [class*="text-amber-300"],
    .theme-light [class*="text-amber-400"] { color: #b45309 !important; }
    .theme-light [class*="border-lime-400/"],
    .theme-light [class*="border-lime-500/"] { border-color: #84cc16 !important; }
    .theme-light [class*="bg-lime-400/"] { background-color: #ecfccb !important; }

    /* ============== CORREÇÃO DE CONTRASTE: ROXO NO TEMA CLARO ============== */
    /* Mesmo problema do verde-limão: text-purple-300/400/500 foram
       calibrados pra fundo escuro (usados no bloco de Projeção
       Estocástica) — no tema claro ficam quase ilegíveis. Escurece só no
       modo claro, sem mexer no tema escuro. Idêntico ao já corrigido no
       Dashboard Consultor, de propósito (mesma cor nos dois lugares). */
    .theme-light [class*="text-purple-300"],
    .theme-light [class*="text-purple-400"] { color: #7c3aed !important; }
    .theme-light [class*="text-purple-500"] { color: #6d28d9 !important; }
    .theme-light [class*="border-purple-500/"] { border-color: #c4b5fd !important; }
    .theme-light [class*="bg-purple-500/"],
    .theme-light [class*="bg-purple-900/"] { background-color: #ede9fe !important; }
    .theme-light [class*="from-purple-900/"] { background-color: #f5f3ff !important; background-image: none !important; }

    /* ============== EXCEÇÃO: MODAIS SEMPRE ESCUROS ============== */
    /* Os modais (editar empresa, excluir tarefa) têm fundo escuro fixo,
       não mudam com o tema — mas as regras gerais acima escureciam o
       texto branco deles do mesmo jeito, achando que o fundo era claro.
       Essa classe restaura as cores certas só dentro desses modais,
       mesmo com o tema claro ativo lá fora. */
    .theme-light .phoenix-modal-dark,
    .theme-light .phoenix-modal-dark [class*="text-white"] { color: #ffffff !important; }
    .theme-light .phoenix-modal-dark [class*="text-slate-200"] { color: #e2e8f0 !important; }
    .theme-light .phoenix-modal-dark [class*="text-slate-300"] { color: #cbd5e1 !important; }
    .theme-light .phoenix-modal-dark [class*="text-slate-400"] { color: #a5b2c5 !important; }
    .theme-light .phoenix-modal-dark [class*="text-slate-500"],
    .theme-light .phoenix-modal-dark [class*="text-slate-600"] { color: #7a8aa0 !important; }
    .theme-light .phoenix-modal-dark [class*="border-white/"] { border-color: rgba(255,255,255,0.1) !important; }
    .theme-light .phoenix-modal-dark [class*="bg-white/5"],
    .theme-light .phoenix-modal-dark [class*="bg-white/10"] { background-color: rgba(255,255,255,0.06) !important; }
    .theme-light .phoenix-modal-dark [class*="bg-black/"],
    .theme-light .phoenix-modal-dark [class*="bg-slate-900/"] { background-color: rgba(0,0,0,0.25) !important; }
    .theme-light .phoenix-modal-dark [class*="text-lime-300"],
    .theme-light .phoenix-modal-dark [class*="text-lime-400"] { color: #a3e635 !important; }
    .theme-light .phoenix-modal-dark [class*="text-purple-300"],
    .theme-light .phoenix-modal-dark [class*="text-purple-400"] { color: #c4b5fd !important; }
    .theme-light .phoenix-modal-dark [class*="text-red-300"],
    .theme-light .phoenix-modal-dark [class*="text-red-400"] { color: #f87171 !important; }
    .theme-light .phoenix-modal-dark [class*="text-green-400"],
    .theme-light .phoenix-modal-dark [class*="text-emerald-400"] { color: #4ade80 !important; }
    .theme-light .phoenix-modal-dark [class*="text-amber-400"] { color: #fbbf24 !important; }
    .theme-light .phoenix-modal-dark input,
    .theme-light .phoenix-modal-dark select,
    .theme-light .phoenix-modal-dark textarea { color: #ffffff !important; background-color: rgba(255,255,255,0.06) !important; }

    /* ============== EXCEÇÃO: ÍCONES DECORATIVOS DE FUNDO ============== */
    /* Os ícones grandes e sutis no canto dos cards (Wallet, Target, etc.)
       foram calibrados pra ficar bem discretos num fundo escuro — no tema
       claro, a mesma opacidade baixa contra um fundo quase branco fica
       praticamente invisível. Aumenta a opacidade só no tema claro pra
       manter o mesmo efeito visual "presente, mas discreto" nos dois temas. */
    .theme-light .phoenix-watermark-icon { opacity: 0.13 !important; }
    .theme-light .group:hover .phoenix-watermark-icon { opacity: 0.22 !important; }
  `}} />
);

const formatCurrency = (value) => {
  return Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ==========================================
// 📅 HORIZONTES LIBERADOS POR PLANO — mesmo mapa usado no app
// (MobileInterface.jsx), copiado aqui de propósito (App e Dashboard são
// mundos separados). Usado pra FILTRAR o que é mandado pra IA — sem
// isso, o texto corrido da "Análise" comentava todos os 5 horizontes
// mesmo pra planos que só desbloqueiam 3/6 meses nos cards numéricos.
// ==========================================
const HORIZONTES_POR_PLANO_GESTOR = {
  tactical: [3, 6],
  plus: [3, 6, 12],
  total: [3, 6, 12, 24, 36],
};

const parseValorBR = (valor) => {
  if (!valor && valor !== 0) return 0;
  if (typeof valor === 'number') return valor;
  return Number(valor.toString().replace(/\./g, '').replace(',', '.')) || 0;
};

// Mesma máscara de moeda usada no app (MobileInterface.jsx / handleCurrencyChange):
// digita só números, e a pontuação (milhar/centavos) aparece sozinha.
// Ex: digitando "150000" vira "1.500,00" ao vivo.
const aplicarMascaraMoeda = (valorDigitado) => {
  const apenasNumeros = valorDigitado.replace(/\D/g, '');
  if (apenasNumeros === '') return '';
  return (parseInt(apenasNumeros, 10) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// ==========================================
// 🤖 FORMATAÇÃO DO TEXTO DA IA — mesma lógica usada no app
// (MobileInterface.jsx), copiada aqui de propósito: Dashboard e App são
// mundos totalmente separados (sem import cruzado), mas o texto que a IA
// gera é o MESMO — só lido daqui, nunca reprocessado.
// ==========================================
function formatarParecerIA(texto) {
  if (!texto) return null;
  const linhas = texto.split('\n').filter(l => l.trim() !== '');

  const formatarNegrito = (linha) => {
    const partes = linha.split(/(\*\*[^*]+\*\*)/g);
    return partes.map((parte, i) =>
      parte.startsWith('**') && parte.endsWith('**')
        ? <strong key={i} className="text-lime-300 font-black">{parte.slice(2, -2)}</strong>
        : parte
    );
  };

  return linhas.map((linha, i) => {
    const l = linha.trim();
    if (l === '---') return <div key={i} className="h-px bg-white/10 my-6" />;
    if (l.startsWith('## ')) {
      return <h4 key={i} className="text-[11px] sm:text-xs font-black text-lime-400 uppercase italic tracking-wide mt-7 mb-2 leading-snug text-left">{l.replace(/^##\s*/, '')}</h4>;
    }
    if (l.startsWith('# ')) {
      return <h3 key={i} className="text-base sm:text-lg font-black text-white uppercase italic tracking-wide mb-3 leading-snug text-left">{l.replace(/^#\s*/, '')}</h3>;
    }
    return <p key={i} className="text-[13px] sm:text-sm text-slate-300 font-medium leading-relaxed tracking-tight text-justify mb-4">{formatarNegrito(l)}</p>;
  });
}

// Mesma escala do Mobile: Entry(1) não acessa o Dashboard, Tactical(2)/Plus(3)/Total(4) liberam features progressivas
// 🔥 CORRIGIDO: DISCLAIMER_FULL agora vem de constants.js (fonte única) —
// antes era uma cópia manual que dependia de lembrar de manter sincronizada
// com o app e o PDF toda vez que o texto mudasse, e acabou divergindo.

// Mesmos planos e preços já usados no app (MobileInterface.jsx) — nunca
// deixar esses valores divergirem entre os dois lugares.
const PLANOS_GESTOR = [
  { id: 'tactical', name: 'Protocolo Tactical', price: '189,00', type: 'ASSINATURA MENSAL' },
  { id: 'plus', name: 'Protocolo Plus', price: '499,00', type: 'ASSINATURA MENSAL' },
  { id: 'total', name: 'Protocolo Total', price: '799,00', type: 'ASSINATURA MENSAL' },
];

const planLevelMap = { 'ENTRY': 1, 'TACTICAL': 2, 'PLUS': 3, 'TOTAL': 4, 'BOARDROOM': 4 };

// ==========================================
// 🏢 RESTRIÇÃO DE DUPLICIDADE (lado Gestor) — o Gestor pode ter várias
// empresas INDEPENDENTES, mas não pode cadastrar a mesma empresa duas
// vezes nem uma filial dela disfarçada de empresa nova (isso é conceito
// exclusivo do Consultor, com plano próprio). Duas checagens:
// 1) Nome igual OU CNPJ raiz igual → BLOQUEIA o cadastro.
// 2) Só o segmento igual (nome diferente) → não bloqueia, só avisa.
// ==========================================
const normalizarNomeEmpresa = (nome) => (nome || '')
  .toUpperCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove acentos
  .replace(/\b(LTDA|ME|EPP|S\/A|SA|EIRELI|MEI)\b\.?/g, '') // remove sufixos comuns
  .replace(/[^A-Z0-9]/g, '') // remove pontuação/espaços
  .trim();

// 🔥 BUG CORRIGIDO: antes era só `.replace(/\D/g,'').slice(0,8)`. O problema:
// o sistema usa '00.000.000/0000-00' como CNPJ padrão quando a pessoa não
// preenche. Isso virava a raiz '00000000' — e como DUAS empresas sem CNPJ
// preenchido geravam a MESMA raiz, o sistema achava que eram matriz e filial
// uma da outra e BLOQUEAVA o cadastro da segunda. Ou seja: quem não digitava
// CNPJ não conseguia cadastrar a segunda empresa, sem entender o motivo.
// Agora exige um CNPJ completo (14 dígitos) e ignora os zerados.
const raizCnpjEmpresa = (cnpj) => {
  const nums = (cnpj || '').replace(/\D/g, '');
  if (nums.length !== 14) return '';      // incompleto/ausente → não compara
  if (/^0+$/.test(nums)) return '';        // placeholder zerado → não compara
  return nums.slice(0, 8);
};

// Retorna { bloqueado: true, empresaConflitante } se nome ou CNPJ raiz baterem
// (essa é uma restrição — não dá pra prosseguir), ou
// { avisoSegmento: true, empresaConflitante } se só o segmento bater (não bloqueia).
const validarDuplicidadeEmpresa = (nomeNovo, cnpjNovo, segmentoNovo, idAtual, listaExistente) => {
  const nomeNormalizado = normalizarNomeEmpresa(nomeNovo);
  const raizNova = raizCnpjEmpresa(cnpjNovo);

  for (const existente of listaExistente) {
    if (existente.id === idAtual) continue; // não compara com ela mesma (caso de edição)

    const raizExistente = raizCnpjEmpresa(existente.cnpj);
    const nomeExistenteNormalizado = normalizarNomeEmpresa(existente.name);

    const cnpjBateu = raizNova && raizExistente && raizNova === raizExistente;
    const nomeBateu = nomeNormalizado && nomeExistenteNormalizado && nomeNormalizado === nomeExistenteNormalizado;

    if (cnpjBateu || nomeBateu) {
      // 🔥 Distingue os dois casos, que ANTES eram tratados com a mesma
      // mensagem genérica ("Nome ou CNPJ igual"). O problema: numa filial o
      // CNPJ NÃO é igual — só a raiz é. Dizer "CNPJ igual" pra quem digitou
      // um CNPJ visivelmente diferente parecia um bug do sistema.
      const motivo = (cnpjBateu && !nomeBateu) ? 'filial' : 'duplicada';
      return { bloqueado: true, empresaConflitante: existente, motivo };
    }
  }

  // Segunda passada: só segmento igual (sem nome/CNPJ batendo) → aviso leve
  for (const existente of listaExistente) {
    if (existente.id === idAtual) continue;
    if (segmentoNovo && existente.sector && segmentoNovo === existente.sector) {
      return { avisoSegmento: true, empresaConflitante: existente };
    }
  }

  return {};
};

// Mesmo formato de ID usado nas tarefas do Consultor — só um identificador
// simples e único o suficiente pra uma lista pessoal.
const gerarIdTarefa = () => `${Date.now()}${Math.random().toString(36).slice(2, 8)}`;

// Cores/ícone conforme a saúde financeira (health.colorKey vindo do
// financeEngine.js) — usado em 3 lugares (painel da lateral, lista de
// relatórios e painel do topo), centralizado aqui pra não duplicar.
const obterCorStatus = (colorKey) => ({
  lime400: { bg: 'bg-lime-400/10', border: 'border-lime-400/30', texto: 'text-lime-400', bola: 'bg-lime-400', Icone: ShieldCheck },
  orange500: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', texto: 'text-orange-400', bola: 'bg-orange-400', Icone: AlertTriangle },
}[colorKey] || { bg: 'bg-red-500/10', border: 'border-red-500/30', texto: 'text-red-500', bola: 'bg-red-500', Icone: AlertTriangle });

const SETORES = [
  'Varejo', 'Alimentação', 'Tech/SaaS', 'Indústria', 'Serviços',
  'Saúde/Clínicas', 'Logística/Frota', 'Holding / Grupo',
  'Atacado/Distrib.', 'Agronegócio', 'Construção/Eng.', 'Beleza/Estética'
];

// ===================================================================
// ❓ MODO "EXPLICA FÁCIL" — ícone pequeno ao lado de termos técnicos
// (Alavancagem, Capital de Giro, Equity Value, etc.) que abre uma
// explicação curta em português simples ao clicar. Não precisa de
// nível de plano nem tela própria — fica embutido nos rótulos, em
// qualquer aba onde o termo aparece.
// ===================================================================
const InfoTooltip = ({ texto, align = 'center' }) => {
  const [aberto, setAberto] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const botaoRef = useRef(null);

  // 🔥 CORREÇÃO: o tooltip era filho do mesmo card que usa overflow-hidden
  // (pra cortar o ícone de fundo grande), então o tooltip também ficava
  // cortado sempre que a caixa de texto era mais alta que o espaço restante
  // do card. Agora ele calcula a posição na TELA (position: fixed) e é
  // renderizado via portal direto no <body> — assim nenhum container pai
  // (overflow-hidden, z-index, altura fixa) consegue mais cortá-lo.
  const abrir = () => {
    if (botaoRef.current) {
      const rect = botaoRef.current.getBoundingClientRect();
      const larguraCaixa = 256; // w-64
      let left = rect.left;
      if (align === 'end') left = rect.right - larguraCaixa;
      else if (align === 'center') left = rect.left + rect.width / 2 - larguraCaixa / 2;
      left = Math.max(12, Math.min(left, window.innerWidth - larguraCaixa - 12));
      setCoords({ top: rect.bottom + 8, left });
    }
    setAberto(a => !a);
  };

  return (
    <span className="relative inline-flex items-center ml-1.5 align-middle">
      <button
        ref={botaoRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); abrir(); }}
        onBlur={() => setTimeout(() => setAberto(false), 150)}
        className="text-slate-500 hover:text-lime-400 transition-colors"
        aria-label="O que significa isso?"
      >
        <HelpCircle size={13} />
      </button>
      {aberto && createPortal(
        <span
          role="tooltip"
          style={{ position: 'fixed', top: coords.top, left: coords.left }}
          className="phoenix-modal-dark z-[999] w-64 max-w-[80vw] p-4 rounded-xl bg-[#0a0a12] border border-white/10 text-[12.5px] leading-relaxed text-slate-200 normal-case font-medium tracking-normal shadow-2xl block"
        >
          {texto}
        </span>,
        document.body
      )}
    </span>
  );
};

// ===================================================================
// 📈 GRÁFICO DE EVOLUÇÃO REAL — usa o campo `historico` salvo no
// Firestore toda vez que o app ou o dashboard salvam a empresa.
// Sem historico suficiente (menos de 2 pontos), mostra aviso em vez
// de inventar números.
// ===================================================================
const GraficoEvolucao = ({ historico, campo, cor }) => {
  const historicoValido = (historico || []).filter(h => h[campo] !== undefined);
  if (historicoValido.length < 2) {
    return (
      <div className="w-full h-16 mt-2 flex items-center">
        <p className="text-[11px] text-slate-600">Salve o diagnóstico mais uma vez para começar o histórico</p>
      </div>
    );
  }
  const valores = historicoValido.map(h => h[campo] || 0);
  const dataMax = Math.max(...valores);
  const dataMin = Math.min(...valores);
  const padding = (dataMax - dataMin) * 0.2;
  const max = dataMax + (padding === 0 ? (dataMax * 0.1 || 1) : padding);
  const min = dataMin - (padding === 0 ? (Math.abs(dataMin * 0.1) || 0) : padding);
  const range = max - min === 0 ? 1 : max - min;
  const points = valores.map((val, index) => {
    const x = (index / (valores.length - 1)) * 100;
    const y = 25 - ((val - min) / range) * 20;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-16 overflow-visible mt-2">
      <polyline
        fill="none" stroke={cor} strokeWidth="3" vectorEffect="non-scaling-stroke"
        strokeLinecap="round" strokeLinejoin="round" points={points}
        style={{ filter: `drop-shadow(0px 5px 8px ${cor}90)` }}
      />
    </svg>
  );
};

// ===================================================================
// 📊 GRÁFICO DE EVOLUÇÃO GRANDE — versão maior, com área preenchida
// (gradiente), pra dar mais peso visual à Visão Geral. Mesmo princípio
// de dados do GraficoEvolucao (usa o histórico real salvo), só que
// desenhado com mais destaque — pensado pra ser "a primeira coisa que
// o olho vê" no painel, como os gráficos grandes do Dashboard Consultor.
// ===================================================================
const GraficoEvolucaoGrande = ({ historico, campo, cor, idGradiente }) => {
  const historicoValido = (historico || []).filter(h => h[campo] !== undefined);
  if (historicoValido.length < 2) {
    return (
      <div className="w-full h-48 flex items-center justify-center">
        <p className="text-xs text-slate-600 text-center max-w-xs">
          Salve o diagnóstico mais uma vez pra começar a enxergar a evolução do seu lucro aqui.
        </p>
      </div>
    );
  }
  const valores = historicoValido.map(h => h[campo] || 0);
  const datas = historicoValido.map(h => h.date || '');
  const dataMax = Math.max(...valores);
  const dataMin = Math.min(...valores);
  const padding = (dataMax - dataMin) * 0.25;
  const max = dataMax + (padding === 0 ? (Math.abs(dataMax) * 0.15 || 1) : padding);
  const min = dataMin - (padding === 0 ? (Math.abs(dataMin) * 0.15 || 0) : padding);
  const range = max - min === 0 ? 1 : max - min;
  const pontos = valores.map((val, index) => {
    const x = (index / (valores.length - 1)) * 100;
    const y = 92 - ((val - min) / range) * 84;
    return { x, y };
  });
  const linha = pontos.map(p => `${p.x},${p.y}`).join(' ');
  const area = `0,100 ${linha} 100,100`;

  return (
    <div className="w-full">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-48 overflow-visible">
        <defs>
          <linearGradient id={idGradiente} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={cor} stopOpacity="0.35" />
            <stop offset="100%" stopColor={cor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill={`url(#${idGradiente})`} />
        <polyline
          fill="none" stroke={cor} strokeWidth="2.2" vectorEffect="non-scaling-stroke"
          strokeLinecap="round" strokeLinejoin="round" points={linha}
          style={{ filter: `drop-shadow(0px 4px 10px ${cor}70)` }}
        />
        {pontos.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="1.6" fill={cor} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div className="flex justify-between mt-2">
        <span className="text-[11px] text-slate-600 font-bold uppercase tracking-widest">{datas[0]}</span>
        <span className="text-[11px] text-slate-600 font-bold uppercase tracking-widest">{datas[datas.length - 1]}</span>
      </div>
    </div>
  );
};

// ===================================================================
// ✏️ FORMULÁRIO DE EDIÇÃO — reaproveitado tanto no modal de edição
// quanto seria fácil de adaptar pro cadastro de uma 2ª empresa no
// futuro. Só lida com os campos financeiros essenciais.
// ===================================================================
const CampoInput = ({ label, value, onChange, placeholder, type = 'text', inputMode, tooltip, tooltipAlign }) => (
  <div>
    <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold flex items-center mb-2">
      {label}
      {tooltip && <InfoTooltip texto={tooltip} align={tooltipAlign} />}
    </label>
    <input
      type={type}
      inputMode={inputMode}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
    />
  </div>
);

// 🔥 Converte o parecer REAL da IA (texto corrido, com "## títulos") no
// mesmo formato {t, d} que gerarInsights sempre produziu — assim ele
// entra no PDF usando exatamente o mesmo layout já pronto e testado, sem
// precisar redesenhar nenhuma página. Cai pra gerarInsights (estático)
// só quando ainda não existe parecer de IA gerado pra esse cliente.
function parecerIAParaBlocosPdf(texto) {
  if (!texto) return null;
  const linhas = texto.split('\n').map(l => l.trim()).filter(l => l && l !== '---');
  const blocos = [];
  let atual = null;
  linhas.forEach((l) => {
    if (l.startsWith('## ')) {
      if (atual && atual.d) blocos.push(atual);
      atual = { t: l.replace(/^##\s*/, '').replace(/\*\*/g, ''), d: '' };
    } else if (l.startsWith('# ')) {
      // título de seção maior — não vira bloco próprio, só é ignorado
      // aqui (o layout do PDF já tem seus próprios títulos de página).
    } else if (atual) {
      const textoLimpo = l.replace(/\*\*/g, '');
      atual.d = atual.d ? `${atual.d} ${textoLimpo}` : textoLimpo;
    }
  });
  if (atual && atual.d) blocos.push(atual);
  return blocos.length > 0 ? blocos : null;
}

export default function GestorDashboardInterface({ onSwitchMode, onLogout, onSessionExpired }) {
  const [activeTab, setActiveTab] = useState('visao');
  const [dossies, setDossies] = useState([]); // TODOS os relatórios do Gestor (não só o mais recente)
  const [empresaAtivaId, setEmpresaAtivaId] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================================
  // 🚪 EFEITO DE LOGOUT — mesma sequência animada de 3 passos que já
  // existe no Dashboard Consultor (Shield → Trash2 → CheckCircle2),
  // reforçando a sensação de segurança ao sair. Antes o botão "Sair"
  // chamava onLogout() direto, sem nenhuma transição.
  // ==========================================
  const [logoutState, setLogoutState] = useState({ isLoggingOut: false, step: 0 });

  // ==========================================
  // 💳 CHECKOUT EMBUTIDO — antes, "Fazer Upgrade" mandava a pessoa de
  // volta pro app (onSwitchMode('upgrade')) pra completar a compra lá.
  // Agora abre um modal com o checkout Asaas direto aqui no Dashboard,
  // sem sair da tela. planoSugeridoId é o próximo nível acima do atual
  // (ex: empresa no Tactical → sugere Plus), mas a pessoa pode trocar
  // dentro do próprio modal.
  // ==========================================
  const [checkoutAberto, setCheckoutAberto] = useState(false);
  const [planoSugeridoCheckout, setPlanoSugeridoCheckout] = useState(null);
  const [mostrarCheckoutConfig, setMostrarCheckoutConfig] = useState(false);

  const abrirCheckout = (planoSugeridoId) => {
    setPlanoSugeridoCheckout(planoSugeridoId || null);
    setCheckoutAberto(true);
  };

  const handleSecureLogout = () => {
    setLogoutState({ isLoggingOut: true, step: 1 });
    setTimeout(() => setLogoutState({ isLoggingOut: true, step: 2 }), 800);
    setTimeout(() => setLogoutState({ isLoggingOut: true, step: 3 }), 1600);
    setTimeout(() => {
      setLogoutState({ isLoggingOut: false, step: 0 });
      if (onLogout) onLogout();
    }, 2400);
  };

  // 🔥 Excluir conta de verdade — mesmo comportamento do app e do Dashboard
  // Consultor (mesma conta, mesmo Firebase Auth): apaga TODOS os dossiês
  // do usuário, tanto de Gestor quanto de Consultor, o perfil, e por fim a
  // conta no Firebase Auth.
  // 🔥 CORREÇÃO IMPORTANTE que fiz por conta própria: antes de apagar a
  // conta, agora CANCELA a assinatura ativa no Asaas primeiro. Sem isso,
  // excluir a conta apagava o login mas a cobrança recorrente continuava
  // rodando pra sempre no Asaas — e a pessoa perderia o acesso à própria
  // conta pra conseguir cancelar, sem nenhum jeito de parar de ser cobrada.
  const [confirmandoExclusaoConta, setConfirmandoExclusaoConta] = useState(false);
  const [excluindoConta, setExcluindoConta] = useState(false);
  const excluirContaDefinitivamente = async () => {
    if (!auth.currentUser) return;
    setExcluindoConta(true);
    try {
      const uid = auth.currentUser.uid;
      const perfilSnap = await getDoc(doc(db, 'usuarios', uid));
      const dadosPerfil = perfilSnap.exists() ? perfilSnap.data() : {};
      if (dadosPerfil.consultorAsaasSubscriptionId) {
        await cancelarAssinaturaAsaas({ subscriptionId: dadosPerfil.consultorAsaasSubscriptionId, imediato: true }).catch(e =>
          console.error('[PHOENIX] Erro ao cancelar assinatura Consultor no Asaas (excluindo mesmo assim):', e)
        );
      }
      if (dadosPerfil.gestorAsaasSubscriptionId) {
        await cancelarAssinaturaAsaas({ subscriptionId: dadosPerfil.gestorAsaasSubscriptionId, imediato: true }).catch(e =>
          console.error('[PHOENIX] Erro ao cancelar assinatura Gestor no Asaas (excluindo mesmo assim):', e)
        );
      }
      const q = query(collection(db, 'cenarios'), where('userId', '==', uid));
      const snapshot = await getDocs(q);
      await Promise.all(snapshot.docs.map((d) => deleteDoc(doc(db, 'cenarios', d.id))));
      await deleteDoc(doc(db, 'usuarios', uid)).catch(() => { });
      await deleteUser(auth.currentUser);
      setConfirmandoExclusaoConta(false);
      if (typeof onLogout === 'function') onLogout();
    } catch (e) {
      console.error('[PHOENIX] Erro ao excluir conta:', e);
      if (e?.code === 'auth/requires-recent-login') {
        alert('Por segurança, saia e faça login novamente antes de excluir sua conta.');
      } else {
        alert('Não foi possível excluir a conta agora. Tente novamente.');
      }
    } finally {
      setExcluindoConta(false);
    }
  };


  // 🔒 Lê TODOS os dossiês do usuário logado (userId) que NÃO são do perfil
  // Consultor — exatamente o mesmo filtro que o app usa pra montar a lista
  // "Meus Dossiês" na aba Gestor. Antes o Dashboard só pegava o mais recente
  // e escondia o resto; agora a pessoa pode ter mais de uma empresa/cenário
  // salvo e escolher qual visualizar, igual no app.
  useEffect(() => {
    if (!auth.currentUser) {
      if (typeof onSessionExpired === 'function') onSessionExpired();
      return;
    }

    let tentouDeNovo = false;
    let unsubscribeAtual = () => { };

    const assinarDossies = (uid) => {
      const q = query(collection(db, "cenarios"), where("userId", "==", uid));
      return onSnapshot(q,
        (snapshot) => {
          const higienizarNumero = (valor) => {
            if (!valor && valor !== 0) return 0;
            if (typeof valor === 'number') return valor;
            const valorLimpo = valor.toString().replace(/\./g, '').replace(',', '.');
            return Number(valorLimpo) || 0;
          };

          const dossiesGestor = snapshot.docs
            .map((docSnap) => {
              const dados = docSnap.data();
              const userType = String(dados.userType || dados.snapshot?.userType || 'Gestor').toLowerCase();
              if (userType === 'consultor') return null; // Dossiês Consultor não aparecem aqui

              const planoRaw = String(dados.plan || 'entry').toUpperCase();

              return {
                id: docSnap.id,
                name: dados.companyName || dados.name || 'Empresa Sem Nome',
                cnpj: dados.cnpj || '00.000.000/0000-00',
                sector: dados.segment || 'Não Informado',
                niche: dados.niche || '-',
                revenue: higienizarNumero(dados.revenue),
                costs: higienizarNumero(dados.costs),
                debt: higienizarNumero(dados.debt),
                custoNaoRecorrente: higienizarNumero(dados.custoNaoRecorrente),
                custoDivida: higienizarNumero(dados.custoDivida),
                dividaCurtoPrazo: higienizarNumero(dados.dividaCurtoPrazo),
                prazoRecebimento: dados.prazoRecebimento,
                prazoPagamento: dados.prazoPagamento,
                proLabore: higienizarNumero(dados.proLabore),
                plan: planoRaw,
                dataCriacao: dados.dataCriacao || null,
                historico: dados.historico || [],
                dueDiligenceChecklist: dados.dueDiligenceChecklist || { auditoria: false, acordoSocios: false },
                metaFinanceira: dados.metaFinanceira || null,
                expertise: dados.expertise || '',
                // 🤖 Textos já gerados pela IA no app — o Dashboard só LÊ,
                // nunca reprocessa (evita custo duplicado de chamada à IA).
                parecerIA: dados.parecerIA || null,
                leituraProjecaoCurto: dados.leituraProjecaoCurto || null,
                leituraProjecaoMedio: dados.leituraProjecaoMedio || null,
                leituraProjecaoLongo: dados.leituraProjecaoLongo || null,
                parecerIAInputSnapshot: dados.parecerIAInputSnapshot || null,
                oportunidadesIA: dados.oportunidadesIA || null,
              };
            })
            .filter(Boolean)
            .sort((a, b) => new Date(b.dataCriacao || 0) - new Date(a.dataCriacao || 0));

          setDossies(dossiesGestor);
          // Mantém a empresa que a pessoa já estava vendo, se ela ainda existir
          // na lista atualizada. Senão, cai pro mais recente (comportamento
          // anterior, preservado como padrão).
          setEmpresaAtivaId((prevId) => {
            if (prevId && dossiesGestor.some(d => d.id === prevId)) return prevId;
            // 🔥 ESCALONAMENTO: Entry não acessa o Dashboard — se a pessoa tiver
            // pelo menos uma empresa Tactical+ na carteira, prioriza ela como
            // padrão em vez de cair direto numa Entry bloqueada.
            const primeiraElegivel = dossiesGestor.find(d => (planLevelMap[d.plan] || 1) >= 2);
            return primeiraElegivel?.id || dossiesGestor[0]?.id || null;
          });
          setLoading(false);
        },
        (error) => {
          // 🔥 CORRIGIDO — CAUSA RAIZ DO "MISSING OR INSUFFICIENT PERMISSIONS":
          // mesmo atraso raro do Firebase descrito nos outros pontos deste
          // arquivo — 1 segundo de espera e tenta de novo, uma única vez.
          if (error?.code === 'permission-denied' && !tentouDeNovo) {
            tentouDeNovo = true;
            setTimeout(() => {
              unsubscribeAtual = assinarDossies(auth.currentUser.uid);
            }, 1000);
          } else {
            console.error("[PHOENIX] Erro na sincronização do Dashboard Gestor:", error.code, '|', error.message);
            setLoading(false);
          }
        }
      );
    };

    unsubscribeAtual = assinarDossies(auth.currentUser.uid);
    return () => unsubscribeAtual();
  }, []);

  const empresa = dossies.find(d => d.id === empresaAtivaId) || null;

  // 🔥 UPGRADE AUTOMÁTICO (mesmo princípio do app): se o plano dessa
  // empresa libera um prazo cujo bloco ainda não existe (ex: acabou de
  // fazer upgrade pra Plus pelo próprio Dashboard e o bloco "médio" nunca
  // foi gerado), completa sozinho — sem regerar parecer nem os blocos que
  // já existem. Só tenta uma vez por empresa+bloco por sessão.
  const blocosJaTentadosDashboardRef = useRef(new Set());
  useEffect(() => {
    if (!empresa || !empresa.parecerIA || atualizandoParecerIA) return;
    const horizontesLiberados = HORIZONTES_POR_PLANO_GESTOR[String(planoGestorReal || '').toLowerCase()] || [];
    const camposPorBloco = { curto: 'leituraProjecaoCurto', medio: 'leituraProjecaoMedio', longo: 'leituraProjecaoLongo' };
    const blocosLiberados = [];
    if (horizontesLiberados.includes(3) || horizontesLiberados.includes(6)) blocosLiberados.push('curto');
    if (horizontesLiberados.includes(12)) blocosLiberados.push('medio');
    if (horizontesLiberados.includes(24) || horizontesLiberados.includes(36)) blocosLiberados.push('longo');
    const blocosFaltantes = blocosLiberados
      .filter(b => !empresa[camposPorBloco[b]])
      .filter(b => !blocosJaTentadosDashboardRef.current.has(`${empresa.id}:${b}`));

    if (blocosFaltantes.length === 0) return;
    blocosFaltantes.forEach(b => blocosJaTentadosDashboardRef.current.add(`${empresa.id}:${b}`));

    (async () => {
      setAtualizandoParecerIA(true);
      try {
        const core = calculateCoreMetrics(empresa.revenue, empresa.costs, empresa.debt, empresa.sector, {
          custoNaoRecorrente: empresa.custoNaoRecorrente, custoDivida: empresa.custoDivida
        });
        const projecao = calculateStochasticProjection({ ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado, currentMargin: core.currentMargin }, empresa.revenue);
        await gerarParecerIA({
          dossieId: String(empresa.id),
          tipoUsuario: 'Gestor',
          nicho: ((empresa.niche && empresa.niche !== '-') ? `${empresa.niche} (setor: ${empresa.sector || 'não informado'})` : (empresa.sector || 'negócio geral')),
          metricas: {
            receita: empresa.revenue, custos: empresa.costs, divida: empresa.debt,
            ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado, currentMargin: core.currentMargin, alavancagem: core.alavancagem
          },
          projecao,
          inputSnapshot: empresa.parecerIAInputSnapshot || null,
          blocosProjecao: blocosFaltantes,
          apenasComplementar: true
        });
      } catch (e) {
        console.error('[PHOENIX] Erro ao completar blocos de projeção após upgrade (Dashboard):', e);
      } finally {
        setAtualizandoParecerIA(false);
      }
    })();
  }, [empresa?.id, empresa?.plan, empresa?.parecerIA]);

  // ==========================================
  // 🎨 TEMA CLARO/ESCURO — mesmo padrão do Dashboard Consultor: salva no
  // navegador (rápido) e no Firestore (sobrevive a troca de aparelho).
  // ==========================================
  const [tema, setTema] = useState(() => localStorage.getItem('phoenix_gestor_dashboard_theme') || 'dark');
  // Status de conexão — avisa o usuário se a internet cair, já que o
  // painel depende do Firestore em tempo real pra tudo.
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const ficouOnline = () => setOnline(true);
    const ficouOffline = () => setOnline(false);
    window.addEventListener('online', ficouOnline);
    window.addEventListener('offline', ficouOffline);
    return () => {
      window.removeEventListener('online', ficouOnline);
      window.removeEventListener('offline', ficouOffline);
    };
  }, []);
  useEffect(() => {
    localStorage.setItem('phoenix_gestor_dashboard_theme', tema);
    if (auth.currentUser) {
      setDoc(doc(db, 'usuarios', auth.currentUser.uid), { gestorDashboardTheme: tema }, { merge: true }).catch(() => { });
    }
  }, [tema]);

  // 🔒 Plano REAL do Gestor — vem SÓ de 'usuarios/{uid}.plan', que é
  // gravado exclusivamente pelo servidor (webhookAsaas) depois do
  // pagamento confirmado. Antes o acesso era decidido pelo carimbo dentro
  // do dossiê ('cenarios'), que o próprio usuário pode editar — bastava o
  // console do navegador pra destravar plano pago sem pagar.
  const [planoGestorReal, setPlanoGestorReal] = useState(null);
  // 🔥 Mesmo princípio do app e do Dashboard Consultor: guarda o valor
  // anterior do plano pra saber se ele MUDOU agora (assinatura acabou de
  // ser confirmada) — sem isso não dá pra distinguir de "só abri a tela".
  const planoAnteriorGestorRef = useRef({ valor: null, inicializado: false });
  const [avisoAssinaturaConfirmada, setAvisoAssinaturaConfirmada] = useState(null);
  const [cancelamentoGestorDoc, setCancelamentoGestorDoc] = useState(null); // { acessoAte } — cancelamento agendado, só leitura
  const [pagamentoAtrasado, setPagamentoAtrasado] = useState(null); // { tipo, desde } — vem direto do Firestore, só leitura
  const [acessoSuspensoPorAtraso, setAcessoSuspensoPorAtraso] = useState(null); // { tipo } — idem, só depois das 48h
  const [temConsultorAtivoTambem, setTemConsultorAtivoTambem] = useState(false); // só pra decidir se mostra "(Gestor)" no aviso ou não

  // 🤖 TEASER "AGENTES DE IA" (Em Breve) — mesmo princípio do app e do
  // Dashboard Consultor: sem funcionalidade real, só antecipa a feature e
  // mede interesse com um clique só (sem e-mail, sem conta separada).
  const [contadorInteresseAgentes, setContadorInteresseAgentes] = useState(null);
  const [jaRegistrouInteresseAgentes, setJaRegistrouInteresseAgentes] = useState(
    () => localStorage.getItem('phoenix_interesse_agentes_ia') === '1'
  );
  const [registrandoInteresseAgentes, setRegistrandoInteresseAgentes] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'metricas', 'interesseAgentesIA'), (snap) => {
      if (snap.exists()) setContadorInteresseAgentes(snap.data());
    }, () => { });
    return () => unsubscribe();
  }, []);

  const registrarInteresseAgentesIA = async () => {
    if (jaRegistrouInteresseAgentes || registrandoInteresseAgentes) return;
    setRegistrandoInteresseAgentes(true);
    try {
      const functionsInstance = getFunctions(app, 'southamerica-east1');
      const chamar = httpsCallable(functionsInstance, 'registrarInteresseAgentesIA');
      await chamar({ perfil: 'gestor' });
      localStorage.setItem('phoenix_interesse_agentes_ia', '1');
      setJaRegistrouInteresseAgentes(true);
    } catch (e) {
      console.error('[PHOENIX] Erro ao registrar interesse em Agentes de IA:', e);
    } finally {
      setRegistrandoInteresseAgentes(false);
    }
  };
  useEffect(() => {
    // 🔥 CORRIGIDO — CAUSA RAIZ DO "MISSING OR INSUFFICIENT PERMISSIONS":
    // atraso raro, mas conhecido, do Firebase — o token de login novo pode
    // levar uma fração de segundo a mais pra "destravar" de vez no canal de
    // conexão com o banco. Se a primeira tentativa falhar por permissão,
    // espera 1 segundo e tenta de novo uma única vez.
    let tentouDeNovo = false;
    const assinar = (uid) => onSnapshot(doc(db, 'usuarios', uid), (snap) => {
      if (!snap.exists()) { setPlanoGestorReal(null); return; }
      const dados = snap.data();
      const plano = dados.plan || dados.gestorPlan || null;
      const planoNormalizado = plano ? String(plano).toUpperCase() : null;
      setPlanoGestorReal(planoNormalizado);
      setPagamentoAtrasado(dados.pagamentoAtrasado || null);
      setCancelamentoGestorDoc(dados.cancelamentoGestor || null);
      setAcessoSuspensoPorAtraso(dados.acessoSuspensoPorAtraso || null);
      setTemConsultorAtivoTambem(['advisor', 'boutique', 'enterprise'].includes(String(dados.consultorPlan || '').toLowerCase()));

      const anterior = planoAnteriorGestorRef.current;
      if (anterior.inicializado && planoNormalizado && planoNormalizado !== anterior.valor) {
        const nomesPlano = { TACTICAL: 'Protocolo Tactical', PLUS: 'Protocolo Plus', TOTAL: 'Protocolo Total' };
        setAvisoAssinaturaConfirmada(nomesPlano[planoNormalizado] || planoNormalizado);
      }
      planoAnteriorGestorRef.current = { valor: planoNormalizado, inicializado: true };
    }, (e) => {
      if (e?.code === 'permission-denied' && !tentouDeNovo) {
        tentouDeNovo = true;
        setTimeout(() => assinar(uid), 1000);
      } else {
        console.error('[PHOENIX] Erro ao escutar plano do Gestor:', e);
      }
    });

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) { setPlanoGestorReal(null); return; }
      const unsubscribeDoc = assinar(user.uid);
      return () => unsubscribeDoc();
    });
    return () => unsubscribeAuth();
  }, []);

  // Carregamento único e confiável do tema salvo — espera a confirmação
  // real de login (onAuthStateChanged) antes de buscar, senão o Firebase
  // Auth ainda pode estar vazio no primeiro milissegundo da página.
  useEffect(() => {
    const buscarTema = (uid, jaTentouDeNovo) => {
      getDoc(doc(db, 'usuarios', uid)).then(snap => {
        if (!snap.exists()) return;
        const dados = snap.data();
        if (dados.gestorDashboardTheme) setTema(dados.gestorDashboardTheme);
      }).catch(e => {
        // 🔥 CORRIGIDO — mesma causa do erro de permissão no plano do Gestor
        // (ver comentário acima): 1 segundo de espera e tenta de novo.
        if (e?.code === 'permission-denied' && !jaTentouDeNovo) {
          setTimeout(() => buscarTema(uid, true), 1000);
        } else {
          console.error('[PHOENIX] Erro ao carregar tema do Gestor:', e);
        }
      });
    };
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      buscarTema(user.uid, false);
    });
    return () => unsubscribe();
  }, []);

  // ==========================================
  // ✏️ EDIÇÃO DE DADOS DA EMPRESA — agora dá pra atualizar direto pelo
  // Dashboard, sem precisar abrir o app. Grava com o MESMO padrão do
  // Dashboard Consultor: setDoc com merge (nunca cria documento duplicado)
  // e acrescenta um retrato no `historico` pra alimentar o gráfico real.
  // ==========================================
  const [editando, setEditando] = useState(false);
  const [formEdicao, setFormEdicao] = useState(null);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  // Mesma checagem ao vivo, agora pro formulário de edição.
  const [checagemEdicaoEmpresa, setChecagemEdicaoEmpresa] = useState({});
  useEffect(() => {
    if (!editando || !formEdicao || !empresa) { setChecagemEdicaoEmpresa({}); return; }
    setChecagemEdicaoEmpresa(validarDuplicidadeEmpresa(
      formEdicao.companyName, formEdicao.cnpj, formEdicao.segment, empresa.id, dossies
    ));
  }, [editando, formEdicao?.companyName, formEdicao?.cnpj, formEdicao?.segment, empresa, dossies]);

  const abrirEdicao = () => {
    if (!empresa) return;
    setFormEdicao({
      companyName: empresa.name,
      cnpj: empresa.cnpj === '00.000.000/0000-00' ? '' : empresa.cnpj,
      segment: empresa.sector,
      niche: empresa.niche && empresa.niche !== '-' ? empresa.niche : '',
      expertise: empresa.expertise === 'Avançado' ? 'Avançado' : 'Executivo',
      revenue: empresa.revenue ? formatCurrency(empresa.revenue) : '',
      costs: empresa.costs ? formatCurrency(empresa.costs) : '',
      debt: empresa.debt ? formatCurrency(empresa.debt) : '',
      custoNaoRecorrente: empresa.custoNaoRecorrente ? formatCurrency(empresa.custoNaoRecorrente) : '',
      custoDivida: empresa.custoDivida ? String(empresa.custoDivida).replace('.', ',') : '',
      dividaCurtoPrazo: empresa.dividaCurtoPrazo ? formatCurrency(empresa.dividaCurtoPrazo) : '',
      prazoRecebimento: empresa.prazoRecebimento || '',
      prazoPagamento: empresa.prazoPagamento || '',
      proLabore: empresa.proLabore ? formatCurrency(empresa.proLabore) : '',
    });
    setEditando(true);
  };

  const handleClickSalvarEdicao = () => {
    if (!empresa || !formEdicao) return;
    const validacao = validarDuplicidadeEmpresa(formEdicao.companyName, formEdicao.cnpj, formEdicao.segment, empresa.id, dossies);
    if (validacao.bloqueado) {
      setBloqueioDuplicidade({ empresaConflitante: validacao.empresaConflitante });
      return;
    }
    if (validacao.avisoSegmento) {
      setAvisoSegmentoIgual({ origem: 'edicao', empresaConflitante: validacao.empresaConflitante });
      return;
    }
    executarSalvarEdicao();
  };

  // ==========================================
  // 🤖 REGERAR PARECER APÓS EDIÇÃO NO DASHBOARD — antes, editar dados
  // aqui só gravava no Firestore e NUNCA chamava a IA de novo, então o
  // parecer ficava travado no diagnóstico antigo pra sempre (bug
  // reportado: "atualizei os valores mas o texto não mudou"). Agora usa
  // a MESMA Cloud Function e a MESMA trava de economia que o app usa em
  // saveScenario/gerarParecerComIA: só gasta uma chamada de IA se os
  // dados financeiros relevantes realmente mudaram.
  // ==========================================
  const [atualizandoParecerIA, setAtualizandoParecerIA] = useState(false);

  const _regerarParecerAposEdicao = async (dossieId, dadosNumericos, segment, niche) => {
    try {
      const { revNum, costsNum, debtNum, custoNaoRecorrenteNum, custoDividaNum, dividaCurtoPrazoNum, proLaboreNum } = dadosNumericos;
      // 🔥 CORREÇÃO: prazoRecebimento/prazoPagamento chegavam como TEXTO
      // (direto do input) — daí o retrato nunca batia com o antigo (que
      // tinha sido salvo como número pelo app), gerando chamada de IA à
      // toa mesmo sem nada financeiro mudar. Normaliza pra número aqui,
      // igual já é feito no app (MobileInterface.jsx).
      const prazoRecebimento = Number(dadosNumericos.prazoRecebimento) || 0;
      const prazoPagamento = Number(dadosNumericos.prazoPagamento) || 0;

      const core = calculateCoreMetrics(revNum, costsNum, debtNum, segment, {
        custoNaoRecorrente: custoNaoRecorrenteNum, custoDivida: custoDividaNum
      });
      const healthCalc = calculateHealthStatus({ ebitda: core.ebitdaAjustado, alavancagem: core.alavancagem, deb: debtNum, rev: revNum, sector: segment });
      const extendedCalc = calculateExtendedMetrics({
 sector: segment,
        rev: revNum, cos: costsNum, ebitda: core.ebitdaAjustado, alavancagem: core.alavancagem, currentMargin: core.currentMargin,
        prazoRecebimento, prazoPagamento, dividaCurtoPrazo: dividaCurtoPrazoNum, proLabore: proLaboreNum
      });
      const projecao = calculateStochasticProjection({ ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado, currentMargin: core.currentMargin }, revNum);

      // 🔥 Só manda pra IA os horizontes que o plano DESSA empresa já
      // libera — mesmo princípio do app. `empresa` é a empresa ativa no
      // Dashboard no momento (component-wide), com `.plan` em maiúsculas
      // (ex: 'TACTICAL').
      const horizontesLiberados = HORIZONTES_POR_PLANO_GESTOR[String(planoGestorReal || '').toLowerCase()] || [];
      const projecaoLiberada = projecao.filter(p => horizontesLiberados.includes(p.months));

      const metricas = {
        receita: revNum, custos: costsNum, divida: debtNum,
        ebitda: core.ebitda, ebitdaAjustado: core.ebitdaAjustado,
        currentMargin: core.currentMargin, alavancagem: core.alavancagem,
        coberturaJuros: core.coberturaJuros, ruptureRisk: core.ruptureRisk,
        statusSaude: healthCalc.label,
        ncg: extendedCalc.ncg, reservaEmergencia: extendedCalc.emergencyReserve,
        dividendosSeguros: extendedCalc.safeDividends, scoreMA: extendedCalc.maScore,
        coberturaCurtoPrazo: extendedCalc.coberturaCurtoPrazo, caixaMinimo: extendedCalc.caixaMinimo
      };

      // Mesma "impressão digital" dos INPUTS que o app usa — se for igual
      // à última salva, não gasta chamada de IA à toa. Inclui os
      // horizontes liberados: um upgrade de plano dispara uma chamada
      // nova de propósito (conteúdo novo de verdade, não é desperdício).
      const retratoAtual = JSON.stringify({
        rev: revNum, cos: costsNum, deb: debtNum, segment,
        custoNaoRecorrente: custoNaoRecorrenteNum, custoDivida: custoDividaNum,
        prazoRecebimento, prazoPagamento,
        dividaCurtoPrazo: dividaCurtoPrazoNum, proLabore: proLaboreNum,
        horizontesLiberados,
        metodo: METODO_PARECER
      });

      const docAtual = await getDoc(doc(db, 'cenarios', String(dossieId)));
      const retratoSalvo = docAtual.exists() ? docAtual.data().parecerIAInputSnapshot : null;

      if (retratoSalvo === retratoAtual) {
        return;
      }

      // 🔍 DIAGNÓSTICO: se a IA disparar de novo sem mudança financeira real,
      // este log mostra os dois retratos lado a lado — compare campo por
      // campo pra achar exatamente qual difere (formato, tipo, etc).

      // 🔥 Converte horizontes liberados (meses) em blocos — mesmo
      // princípio do app: só pede pra IA escrever os blocos que o plano
      // dessa empresa já libera, nunca gera prazo que ninguém pagou.
      const blocosProjecao = [];
      if (horizontesLiberados.includes(3) || horizontesLiberados.includes(6)) blocosProjecao.push('curto');
      if (horizontesLiberados.includes(12)) blocosProjecao.push('medio');
      if (horizontesLiberados.includes(24) || horizontesLiberados.includes(36)) blocosProjecao.push('longo');
      const incluirOportunidades = String(empresa?.plan || '').toUpperCase() === 'TOTAL';

      await gerarParecerIA({
        dossieId: String(dossieId),
        tipoUsuario: 'Gestor',
        nicho: (niche ? `${niche} (setor: ${segment || 'não informado'})` : (segment || 'negócio geral')),
        metricas,
        projecao,
        inputSnapshot: retratoAtual,
        blocosProjecao,
        incluirOportunidades
      });
    } catch (e) {
      console.error('[PHOENIX] Erro ao regerar parecer da IA após edição no Dashboard:', e);
    }
  };

  // 🔒 FILA (2026-10-05): duas edições seguidas disparavam duas gerações ao mesmo tempo, e a mais
  // ANTIGA podia terminar por último e sobrescrever o parecer novo (PDF saía com texto dos dados
  // velhos). Agora as gerações de cada empresa rodam uma por vez, em ordem; "atualizando" só apaga quando
  // a última acaba (e o botão de exportar PDF fica travado até lá).
  const filaRegeracaoRef = useRef(Promise.resolve());
  const regeracoesPendentesRef = useRef(0);
  const regerarParecerAposEdicao = (...args) => {
    regeracoesPendentesRef.current += 1;
    setAtualizandoParecerIA(true);
    const p = filaRegeracaoRef.current.then(() => _regerarParecerAposEdicao(...args));
    filaRegeracaoRef.current = p.catch(() => {});
    return p.finally(() => {
      regeracoesPendentesRef.current -= 1;
      if (regeracoesPendentesRef.current <= 0) { regeracoesPendentesRef.current = 0; setAtualizandoParecerIA(false); }
    });
  };

  // 🔥 GERAÇÃO NA HORA (2026-10-04): empresa criada AQUI no Dashboard (ou sem parecer por
  // qualquer outro motivo) ficava eternamente em "Salve o diagnóstico no app" e com a
  // projeção em "Gerando…", porque só a EDIÇÃO chamava a IA. Agora toda empresa sem parecer
  // gera o parecer completo sozinha, venha de onde vier. Uma tentativa por empresa por sessão
  // (a trava de impressão digital também evita cobrança repetida).
  const geracaoInicialTentadaRef = useRef(new Set());
  useEffect(() => {
    if (!empresa || atualizandoParecerIA || !planoGestorReal) return;
    const snapGuardado = String(empresa.parecerIAInputSnapshot || '');
    const parecerAtual = !!empresa.parecerIA && snapGuardado.includes('"metodo":"' + METODO_PARECER + '"');
    if (parecerAtual) return; // já gerado com a metodologia vigente
    if (geracaoInicialTentadaRef.current.has(empresa.id)) return;
    geracaoInicialTentadaRef.current.add(empresa.id);
    regerarParecerAposEdicao(empresa.id, {
      revNum: Number(empresa.revenue) || 0, costsNum: Number(empresa.costs) || 0, debtNum: Number(empresa.debt) || 0,
      custoNaoRecorrenteNum: Number(empresa.custoNaoRecorrente) || 0, custoDividaNum: Number(empresa.custoDivida) || 0,
      dividaCurtoPrazoNum: Number(empresa.dividaCurtoPrazo) || 0, proLaboreNum: Number(empresa.proLabore) || 0,
      prazoRecebimento: empresa.prazoRecebimento, prazoPagamento: empresa.prazoPagamento
    }, empresa.sector, empresa.niche && empresa.niche !== '-' ? empresa.niche : '').catch(e => console.error('[PHOENIX] Erro ao gerar parecer inicial (Dashboard):', e));
  }, [empresa?.id, empresa?.parecerIA, empresa?.parecerIAInputSnapshot, planoGestorReal]);

  const executarSalvarEdicao = async () => {
    if (!empresa || !formEdicao) return;
    setSalvandoEdicao(true);
    try {
      const revNum = parseValorBR(formEdicao.revenue);
      const costsNum = parseValorBR(formEdicao.costs);
      const debtNum = parseValorBR(formEdicao.debt);
      const custoNaoRecorrenteNum = parseValorBR(formEdicao.custoNaoRecorrente);
      const custoDividaNum = parseValorBR(formEdicao.custoDivida);
      const dividaCurtoPrazoNum = parseValorBR(formEdicao.dividaCurtoPrazo);
      const proLaboreNum = parseValorBR(formEdicao.proLabore);

      const dadosParaSalvar = {
        companyName: formEdicao.companyName,
        cnpj: formEdicao.cnpj,
        segment: formEdicao.segment,
        niche: formEdicao.niche || '',
        expertise: formEdicao.expertise,
        revenue: revNum,
        costs: costsNum,
        debt: debtNum,
        custoNaoRecorrente: custoNaoRecorrenteNum,
        custoDivida: custoDividaNum,
        dividaCurtoPrazo: dividaCurtoPrazoNum,
        prazoRecebimento: formEdicao.prazoRecebimento,
        prazoPagamento: formEdicao.prazoPagamento,
        proLabore: proLaboreNum,
      };

      // Mesmo padrão do Consultor: cada edição também guarda um retrato no
      // histórico, isolado em try/catch pra nunca travar o salvamento.
      try {
        const coreSnapshot = calculateCoreMetrics(revNum, costsNum, debtNum, formEdicao.segment, {
          custoNaoRecorrente: custoNaoRecorrenteNum, custoDivida: custoDividaNum
        });
        dadosParaSalvar.historico = arrayUnion({
          date: new Date().toLocaleDateString('pt-BR'),
          revenue: revNum, costs: costsNum, debt: debtNum,
          ebitda: coreSnapshot.ebitdaAjustado ?? 0,
          equityValue: coreSnapshot.equityValue ?? 0,
          alavancagem: typeof coreSnapshot.alavancagem === 'number' ? coreSnapshot.alavancagem : 0,
          currentMargin: coreSnapshot.currentMargin ?? 0
        });
      } catch (erroHistorico) {
        console.error('[PHOENIX] Não foi possível salvar o retrato do histórico:', erroHistorico);
      }

      await setDoc(doc(db, 'cenarios', empresa.id), dadosParaSalvar, { merge: true });
      setEditando(false);

      // 🤖 Dispara em segundo plano, não trava o fechamento do modal. Se
      // falhar, o parecer antigo continua visível — nunca deixa a tela
      // sem parecer nenhum.
      regerarParecerAposEdicao(empresa.id, {
        revNum, costsNum, debtNum, custoNaoRecorrenteNum, custoDividaNum, dividaCurtoPrazoNum, proLaboreNum,
        prazoRecebimento: formEdicao.prazoRecebimento, prazoPagamento: formEdicao.prazoPagamento
      }, formEdicao.segment, formEdicao.niche).catch(e => console.error('[PHOENIX] Erro (fora da trava) ao regerar parecer:', e));
    } catch (e) {
      console.error('[PHOENIX] Erro ao salvar edição da empresa:', e);
    } finally {
      setSalvandoEdicao(false);
    }
  };

  // ==========================================
  // ➕ NOVA EMPRESA — o Dashboard não tinha como cadastrar uma empresa do
  // zero (só editava a que já existia). Grava no MESMO formato "solto"
  // que o app usa em saveScenario (campos direto na raiz do documento,
  // não aninhados em snapshot.formData como o Consultor faz) — o próprio
  // app já sabe ler os dois formatos (`dados.snapshot?.formData || dados`),
  // então uma empresa criada aqui aparece certinho no app also.
  // ==========================================
  const [criandoEmpresa, setCriandoEmpresa] = useState(false);
  const [mostrandoSobre, setMostrandoSobre] = useState(false);

  // Checagem AO VIVO (a cada letra digitada), não só no clique de Salvar —
  // assim a pessoa já vê o bloqueio/aviso antes de preencher o formulário
  // inteiro, em vez de descobrir só no fim.
  const [checagemNovaEmpresa, setChecagemNovaEmpresa] = useState({});
  // Excluir empresa — ainda não existia no Dashboard. Confirmação em 2
  // etapas (mesmo padrão da exclusão de tarefa), pra evitar apagar algo
  // sem querer.
  const [empresaParaExcluir, setEmpresaParaExcluir] = useState(null);
  const [excluindoEmpresa, setExcluindoEmpresa] = useState(false);

  const excluirEmpresaConfirmada = async () => {
    if (!empresaParaExcluir) return;
    setExcluindoEmpresa(true);
    try {
      await deleteDoc(doc(db, 'cenarios', empresaParaExcluir.id));
      setEmpresaParaExcluir(null);
    } catch (e) {
      console.error('[PHOENIX] Erro ao excluir empresa:', e);
    } finally {
      setExcluindoEmpresa(false);
    }
  };

  const [formNovaEmpresa, setFormNovaEmpresa] = useState(null);
  const [salvandoNovaEmpresa, setSalvandoNovaEmpresa] = useState(false);

  useEffect(() => {
    if (!criandoEmpresa || !formNovaEmpresa) { setChecagemNovaEmpresa({}); return; }
    setChecagemNovaEmpresa(validarDuplicidadeEmpresa(
      formNovaEmpresa.companyName, formNovaEmpresa.cnpj, formNovaEmpresa.segment, null, dossies
    ));
  }, [criandoEmpresa, formNovaEmpresa?.companyName, formNovaEmpresa?.cnpj, formNovaEmpresa?.segment, dossies]);


  // ==========================================
  // 🏢 BLOQUEIO DE DUPLICIDADE — dispara ao salvar (nova empresa OU edição).
  // Nome igual ou CNPJ raiz igual: BLOQUEIA de vez (modal só com "Entendi",
  // sem opção de forçar) e explica que pra gerir matriz+filiais é preciso
  // o plano Consultor. Segmento igual sozinho: aviso leve, não bloqueia.
  // ==========================================
  const [bloqueioDuplicidade, setBloqueioDuplicidade] = useState(null); // { empresaConflitante }
  const [avisoSegmentoIgual, setAvisoSegmentoIgual] = useState(null); // { origem, empresaConflitante }
  const [processandoAvisoSegmento, setProcessandoAvisoSegmento] = useState(false);

  // Confirma que quer prosseguir mesmo com segmento igual (não é duplicidade real).
  const confirmarApesarDoSegmento = async () => {
    if (!avisoSegmentoIgual) return;
    setProcessandoAvisoSegmento(true);
    try {
      if (avisoSegmentoIgual.origem === 'nova') {
        await executarSalvarNovaEmpresa();
      } else {
        await executarSalvarEdicao();
      }
      setAvisoSegmentoIgual(null);
    } finally {
      setProcessandoAvisoSegmento(false);
    }
  };
  // Efeito "scanning" — mesmo princípio visual do Dashboard Consultor,
  // deixando claro pro Gestor que está sincronizando de verdade com o
  // Firestore (não é só um spinner genérico).
  const [progressoCadastroEmpresa, setProgressoCadastroEmpresa] = useState(0);

  const abrirNovaEmpresa = () => {
    setFormNovaEmpresa({
      companyName: '', cnpj: '', segment: '', niche: '',
      expertise: 'Executivo', // Mesmo padrão/valores do onboarding do app: 'Executivo' (claro) ou 'Avançado' (técnico)
      revenue: '', costs: '', debt: '',
      custoNaoRecorrente: '', custoDivida: '', dividaCurtoPrazo: '',
      prazoRecebimento: '', prazoPagamento: '', proLabore: ''
    });
    setProgressoCadastroEmpresa(0);
    setCriandoEmpresa(true);
  };

  const handleClickSalvarNovaEmpresa = () => {
    if (!formNovaEmpresa || !auth.currentUser) return;
    if (!formNovaEmpresa.companyName.trim() || !formNovaEmpresa.revenue || !formNovaEmpresa.costs) return;
    const validacao = validarDuplicidadeEmpresa(formNovaEmpresa.companyName, formNovaEmpresa.cnpj, formNovaEmpresa.segment, null, dossies);
    if (validacao.bloqueado) {
      setBloqueioDuplicidade({ empresaConflitante: validacao.empresaConflitante });
      return;
    }
    if (validacao.avisoSegmento) {
      setAvisoSegmentoIgual({ origem: 'nova', empresaConflitante: validacao.empresaConflitante });
      return;
    }
    executarSalvarNovaEmpresa();
  };

  const executarSalvarNovaEmpresa = async () => {
    if (!formNovaEmpresa || !auth.currentUser) return;
    if (!formNovaEmpresa.companyName.trim() || !formNovaEmpresa.revenue || !formNovaEmpresa.costs) return;
    setSalvandoNovaEmpresa(true);
    setProgressoCadastroEmpresa(0);

    // 🔥 Scanning mais lento de propósito — mesmo ajuste que já foi feito
    // no app (120ms por 1%, ~12s no total). Antes rodava tão rápido
    // (10% a cada 320ms) que quase não dava tempo de perceber. Desacoplado
    // do salvamento de verdade: mesmo que o Firestore responda em menos de
    // 1 segundo, a animação sempre roda o tempo inteiro antes de fechar —
    // reforça a sensação de que a análise está realmente sendo processada.
    const inicioAnimacao = Date.now();
    const duracaoTotalMs = 12000;
    const intervaloAnimacao = setInterval(() => {
      const decorrido = Date.now() - inicioAnimacao;
      setProgressoCadastroEmpresa(Math.min(99, Math.floor((decorrido / duracaoTotalMs) * 100)));
    }, 120);

    try {
      const revNum = parseValorBR(formNovaEmpresa.revenue);
      const costsNum = parseValorBR(formNovaEmpresa.costs);
      const debtNum = parseValorBR(formNovaEmpresa.debt);
      const custoNaoRecorrenteNum = parseValorBR(formNovaEmpresa.custoNaoRecorrente);
      const custoDividaNum = parseValorBR(formNovaEmpresa.custoDivida);
      const dividaCurtoPrazoNum = parseValorBR(formNovaEmpresa.dividaCurtoPrazo);
      const proLaboreNum = parseValorBR(formNovaEmpresa.proLabore);
      const novoId = gerarIdTarefa();

      const dadosParaSalvar = {
        companyName: formNovaEmpresa.companyName.trim(),
        cnpj: formNovaEmpresa.cnpj.trim() || '00.000.000/0000-00',
        segment: formNovaEmpresa.segment,
        niche: formNovaEmpresa.niche.trim(),
        expertise: formNovaEmpresa.expertise,
        revenue: revNum,
        costs: costsNum,
        debt: debtNum,
        custoNaoRecorrente: custoNaoRecorrenteNum,
        custoDivida: custoDividaNum,
        dividaCurtoPrazo: dividaCurtoPrazoNum,
        prazoRecebimento: formNovaEmpresa.prazoRecebimento,
        prazoPagamento: formNovaEmpresa.prazoPagamento,
        proLabore: proLaboreNum,
        userType: 'Gestor',
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        dataCriacao: new Date().toISOString(),
        status: 'processado',
        // Mesmo plano da conta — todos os dossiês do Gestor ficam no mesmo
        // nível (mesma lógica que já corrigimos na sincronia de planos).
        plan: empresa?.plan || 'TACTICAL'
      };

      try {
        const coreSnapshot = calculateCoreMetrics(revNum, costsNum, debtNum, formNovaEmpresa.segment, {
          custoNaoRecorrente: custoNaoRecorrenteNum, custoDivida: custoDividaNum
        });
        dadosParaSalvar.historico = arrayUnion({
          date: new Date().toLocaleDateString('pt-BR'),
          revenue: revNum, costs: costsNum, debt: debtNum,
          ebitda: coreSnapshot.ebitdaAjustado ?? 0,
          equityValue: coreSnapshot.equityValue ?? 0,
          alavancagem: typeof coreSnapshot.alavancagem === 'number' ? coreSnapshot.alavancagem : 0,
          currentMargin: coreSnapshot.currentMargin ?? 0
        });
      } catch (erroHistorico) {
        console.error('[PHOENIX] Não foi possível salvar o retrato inicial do histórico:', erroHistorico);
      }

      await setDoc(doc(db, 'cenarios', novoId), dadosParaSalvar, { merge: true });

      // Espera o tempo total da animação (mesmo que o Firestore já tenha
      // respondido antes) — é isso que garante a sensação de "análise
      // profunda" em vez de piscar e sumir.
      const decorrido = Date.now() - inicioAnimacao;
      const esperaRestante = Math.max(0, duracaoTotalMs - decorrido);
      await new Promise(resolve => setTimeout(resolve, esperaRestante));

      clearInterval(intervaloAnimacao);
      setProgressoCadastroEmpresa(100);
      setEmpresaAtivaId(novoId);

      // 🔥 CORREÇÃO: antes usava setTimeout "solto" pra fechar o modal, e o
      // "finally" logo abaixo rodava na mesma hora (sem esperar o timeout
      // terminar), desligando salvandoNovaEmpresa ANTES do modal fechar de
      // verdade — por 900ms a tela mostrava o formulário vazio por baixo,
      // antes de sumir. Agora espera o fechamento terminar de verdade antes
      // de liberar o estado de "salvando".
      await new Promise(resolve => setTimeout(resolve, 900)); // dá tempo de ver o "100% concluído" antes de fechar
      setCriandoEmpresa(false);
    } catch (e) {
      console.error('[PHOENIX] Erro ao criar nova empresa:', e);
      clearInterval(intervaloAnimacao);
    } finally {
      setSalvandoNovaEmpresa(false);
    }
  };

  // ==========================================
  // 🧮 SIMULADOR "E SE" — aba Operação. Não altera dado salvo, só simula
  // na tela usando o mesmo motor de projeção do app (calculateProjection).
  // ==========================================
  const [simRevenue, setSimRevenue] = useState(0);
  const [simCosts, setSimCosts] = useState(0);

  // ==========================================
  // 💰 CALCULADORA DE PRECIFICAÇÃO — ajuda a responder "quanto eu devo
  // cobrar por esse produto/serviço" considerando custo, impostos/taxas
  // e a margem de lucro que o dono quer ter. Fica dentro da aba Operação
  // (mesmo nível de plano, Tactical), como mais uma ferramenta prática —
  // não usa dado nenhum salvo da empresa, é uma conta independente.
  // ==========================================
  const [precoCusto, setPrecoCusto] = useState('');
  const [precoDespesas, setPrecoDespesas] = useState(8);
  const [precoMargem, setPrecoMargem] = useState(20);

  const calculoPreco = (() => {
    const custo = parseValorBR(precoCusto);
    const despesasPercent = Number(precoDespesas) || 0;
    const margemPercent = Number(precoMargem) || 0;
    const divisor = 1 - ((despesasPercent + margemPercent) / 100);
    if (custo <= 0 || divisor <= 0) {
      return { valido: false, precoSugerido: 0, lucroReais: 0, despesasReais: 0 };
    }
    const precoSugerido = custo / divisor;
    return {
      valido: true,
      precoSugerido,
      lucroReais: precoSugerido * (margemPercent / 100),
      despesasReais: precoSugerido * (despesasPercent / 100)
    };
  })();

  const metrics = empresa ? calculateCoreMetrics(empresa.revenue, empresa.costs, empresa.debt, empresa.sector, {
    custoNaoRecorrente: empresa.custoNaoRecorrente,
    custoDivida: empresa.custoDivida
  }) : null;

  // ==========================================
  // 🏦 SIMULADOR DE EMPRÉSTIMO — "esse empréstimo cabe no bolso da
  // empresa?". Compara a parcela mensal estimada com o Lucro do Mês
  // atual. Fica na aba CFO Virtual (Plus), junto das outras ferramentas
  // de planejamento financeiro — não usa dado salvo, é uma conta
  // independente que o Gestor testa quantas vezes quiser.
  // ==========================================
  const [emprestimoValor, setEmprestimoValor] = useState('');
  const [emprestimoJurosAno, setEmprestimoJurosAno] = useState(24);
  const [emprestimoPrazoMeses, setEmprestimoPrazoMeses] = useState(24);

  const calculoEmprestimo = (() => {
    const principal = parseValorBR(emprestimoValor);
    const taxaMensal = Math.pow(1 + (Number(emprestimoJurosAno) || 0) / 100, 1 / 12) - 1;
    const n = Number(emprestimoPrazoMeses) || 1;
    if (principal <= 0) return { valido: false };

    // Fórmula Price (parcelas fixas) — padrão de mercado pra empréstimos e financiamentos.
    const parcela = taxaMensal === 0
      ? principal / n
      : (principal * taxaMensal) / (1 - Math.pow(1 + taxaMensal, -n));
    const totalPago = parcela * n;
    const totalJuros = totalPago - principal;

    const lucroMes = metrics ? metrics.ebitdaAjustado : 0;
    const comprometimento = lucroMes > 0 ? (parcela / lucroMes) * 100 : null;

    // 🔥 CORREÇÃO: limites revisados — 24% do lucro numa parcela só sendo
    // chamado de "cabe com folga" era otimista demais pra fluxo de caixa
    // de empresa (mais instável que salário de pessoa física). Regra mais
    // prudente: até 15% é confortável, 15-35% pede atenção, acima de 35%
    // é crítico.
    let nivel = 'seguro';
    let veredito = 'A parcela cabe com folga dentro do lucro mensal atual da empresa.';
    if (comprometimento === null) {
      nivel = 'critico';
      veredito = 'A empresa está com lucro mensal zerado ou negativo — assumir uma parcela fixa nesse momento aumenta bastante o risco financeiro.';
    } else if (comprometimento > 35) {
      nivel = 'critico';
      veredito = 'A parcela compromete uma fatia grande do lucro mensal — risco alto de aperto no caixa em qualquer imprevisto. Vale reavaliar valor, prazo ou taxa antes de assumir.';
    } else if (comprometimento > 15) {
      nivel = 'atencao';
      veredito = 'A parcela tem peso relevante no lucro mensal. Cabe, mas com pouca margem de segurança — vale considerar um prazo mais longo.';
    }

    return { valido: true, parcela, totalPago, totalJuros, comprometimento, nivel, veredito };
  })();

  // ==========================================
  // 🎯 META FINANCEIRA — o Gestor define um alvo de faturamento ou lucro,
  // e acompanha o progresso com base no dado mais recente salvo. Fica na
  // Visão Geral (nível 1), junto do Lucro do Mês e Ponto de Equilíbrio.
  // Salvo direto no dossiê da empresa (campo metaFinanceira).
  // ==========================================
  const [editandoMeta, setEditandoMeta] = useState(false);
  const [metaTipoForm, setMetaTipoForm] = useState('faturamento');
  const [metaValorForm, setMetaValorForm] = useState('');
  const [salvandoMeta, setSalvandoMeta] = useState(false);

  const abrirEdicaoMeta = () => {
    setMetaTipoForm(empresa?.metaFinanceira?.tipo || 'faturamento');
    setMetaValorForm(empresa?.metaFinanceira?.valor ? formatCurrency(empresa.metaFinanceira.valor) : '');
    setEditandoMeta(true);
  };

  const salvarMeta = async () => {
    if (!empresa?.id) return;
    const valorNum = parseValorBR(metaValorForm);
    if (valorNum <= 0) return;
    setSalvandoMeta(true);
    try {
      await setDoc(doc(db, 'cenarios', empresa.id), {
        metaFinanceira: { tipo: metaTipoForm, valor: valorNum, criadaEm: new Date().toISOString() }
      }, { merge: true });
      setEditandoMeta(false);
    } catch (e) {
      console.error('[PHOENIX] Erro ao salvar meta financeira:', e);
    } finally {
      setSalvandoMeta(false);
    }
  };

  const progressoMeta = (() => {
    if (!empresa?.metaFinanceira || !metrics) return null;
    const meta = empresa.metaFinanceira;
    const atual = meta.tipo === 'lucro' ? metrics.ebitdaAjustado : empresa.revenue;
    const percentual = meta.valor > 0 ? Math.max(0, Math.min(100, (atual / meta.valor) * 100)) : 0;
    return { ...meta, atual, percentual };
  })();

  const extended = empresa && metrics ? calculateExtendedMetrics({
 sector: empresa.sector,
    rev: empresa.revenue, cos: empresa.costs, ebitda: metrics.ebitdaAjustado,
    alavancagem: metrics.alavancagem, currentMargin: metrics.currentMargin,
    prazoRecebimento: empresa.prazoRecebimento, prazoPagamento: empresa.prazoPagamento,
    dividaCurtoPrazo: empresa.dividaCurtoPrazo, proLabore: empresa.proLabore
  }) : null;

  const health = empresa && metrics ? calculateHealthStatus({
    ebitda: metrics.ebitdaAjustado, alavancagem: metrics.alavancagem,
    deb: empresa.debt, rev: empresa.revenue, sector: empresa.sector
  }) : null;

  // ==========================================
  // 📅 PROJEÇÃO ESTOCÁSTICA POR PRAZO — números calculados na hora
  // (mesma fórmula do app, sem custo de IA nenhum), um valor por
  // horizonte (3/6/12/24/36 meses), organizados em 3 blocos (Curto/
  // Médio/Longo). Cada bloco tem seu próprio texto de IA
  // (leituraProjecaoCurto/Medio/Longo) — nunca mais um texto único.
  // ==========================================
  const projecaoPorPrazo = empresa && metrics ? calculateStochasticProjection(metrics, empresa.revenue) : [];

  // ==========================================
  // 📊 SENSIBILIDADE DO VALUATION — mesmo cálculo do app: mostra como o
  // Patrimônio Líquido (Equity Value) reage a uma variação de 5 pontos
  // percentuais de margem, pra cima ou pra baixo.
  // ==========================================
  const sensibilidadeValuation = empresa && empresa.revenue ? (() => {
    const passo = empresa.revenue * 0.05;
    const cenarios = [
      { label: '-5% margem', costsSim: empresa.costs + passo },
      { label: 'Atual', costsSim: empresa.costs },
      { label: '+5% margem', costsSim: Math.max(0, empresa.costs - passo) }
    ].map(c => {
      const core = calculateCoreMetrics(empresa.revenue, c.costsSim, empresa.debt, empresa.sector, {
        custoNaoRecorrente: empresa.custoNaoRecorrente, custoDivida: empresa.custoDivida
      });
      return { ...c, equity: Math.max(0, core.equityValue) };
    });
    const maxEquity = Math.max(...cenarios.map(c => c.equity), 1);
    return { cenarios, maxEquity };
  })() : null;

  const projection = empresa && metrics ? calculateProjection(
    empresa.revenue, empresa.costs, metrics.ebitda, simRevenue, simCosts
  ) : null;

  // 🔒 Acesso decidido pelo plano do SERVIDOR (planoGestorReal), não pelo
  // carimbo do dossiê — que o usuário pode editar pelo navegador.
  // Cai pro carimbo do dossiê só quando o servidor ainda não respondeu,
  // e nesse caso limita ao nível 1 se não houver plano confirmado.
  const currentLevel = planoGestorReal
    ? (planLevelMap[planoGestorReal] || 1)
    : 1;
  const checkAccess = (requiredLevel) => currentLevel >= requiredLevel;

  // 🔥 Mesmo princípio do app: o checkout só oferece planos ACIMA do atual
  // (upgrade de verdade) — nunca o plano que a pessoa já tem, nem um
  // inferior. Evita a confusão de "comprar" um plano já ativo.
  const planosParaUpgrade = PLANOS_GESTOR.filter(p => (planLevelMap[p.id.toUpperCase()] || 1) > currentLevel);

  // ==========================================
  // ✅ CHECKLIST DUE DILIGENCE — mesmo campo do Firestore usado pelo app
  // (dueDiligenceChecklist), assim marcar no Dashboard reflete no app e
  // vice-versa. Grava na hora do clique, sem precisar de botão "Salvar".
  // ==========================================
  const toggleChecklistItem = (chave) => {
    if (!empresa) return;
    const atualizado = { ...empresa.dueDiligenceChecklist, [chave]: !empresa.dueDiligenceChecklist?.[chave] };
    setDoc(doc(db, 'cenarios', empresa.id), { dueDiligenceChecklist: atualizado }, { merge: true }).catch((e) => {
      console.error('[PHOENIX] Erro ao salvar checklist de Due Diligence:', e);
    });
  };

  // ==========================================
  // ✅📝 PLANO DE AÇÃO — TAREFAS E ANOTAÇÕES. Mesmo padrão de dados já
  // usado pelo Consultor (tarefasConsultor/notasConsultor), só que salvo
  // no próprio dossiê da empresa do Gestor (tarefasGestor/notasGestor).
  // Escuta em tempo real — se o Gestor tiver o Dashboard aberto em duas
  // abas, ou algum dia isso for espelhado no app, fica tudo sincronizado.
  // ==========================================
  const [tarefas, setTarefas] = useState([]);
  const [notas, setNotas] = useState([]);
  const [novaTarefaTexto, setNovaTarefaTexto] = useState('');
  const [novaTarefaPrazo, setNovaTarefaPrazo] = useState('');
  const [salvandoTarefa, setSalvandoTarefa] = useState(false);
  const [tarefaEditandoId, setTarefaEditandoId] = useState(null);
  const [textoEdicaoTarefa, setTextoEdicaoTarefa] = useState('');
  const [prazoEdicaoTarefa, setPrazoEdicaoTarefa] = useState('');
  const [novaNota, setNovaNota] = useState('');
  const [salvandoNota, setSalvandoNota] = useState(false);
  const [notaEditandoIndex, setNotaEditandoIndex] = useState(null);
  const [textoEdicaoNota, setTextoEdicaoNota] = useState('');

  useEffect(() => {
    if (!empresa?.id) {
      setTarefas([]);
      setNotas([]);
      return;
    }
    const unsubscribe = onSnapshot(doc(db, 'cenarios', empresa.id), (docSnap) => {
      if (docSnap.exists()) {
        setTarefas(docSnap.data().tarefasGestor || []);
        setNotas(docSnap.data().notasGestor || []);
      } else {
        setTarefas([]);
        setNotas([]);
      }
    });
    return () => unsubscribe();
  }, [empresa?.id]);

  const salvarTarefas = async (listaAtualizada) => {
    if (!empresa?.id) return;
    try {
      await setDoc(doc(db, 'cenarios', empresa.id), { tarefasGestor: listaAtualizada }, { merge: true });
    } catch (e) {
      console.error('[PHOENIX] Erro ao salvar tarefas:', e);
    }
  };

  const adicionarTarefa = async () => {
    const texto = novaTarefaTexto.trim();
    if (!texto || !empresa?.id) return;
    setSalvandoTarefa(true);
    const tarefa = { id: gerarIdTarefa(), texto, prazo: novaTarefaPrazo || null, concluida: false };
    await salvarTarefas([tarefa, ...tarefas]);
    setNovaTarefaTexto('');
    setNovaTarefaPrazo('');
    setSalvandoTarefa(false);
  };

  const alternarTarefaConcluida = (id) => {
    salvarTarefas(tarefas.map(t => t.id === id ? { ...t, concluida: !t.concluida } : t));
  };

  const editarTarefa = (id, novoTexto, novoPrazo) => {
    if (!novoTexto.trim()) return;
    salvarTarefas(tarefas.map(t => t.id === id ? { ...t, texto: novoTexto.trim(), prazo: novoPrazo || null } : t));
    setTarefaEditandoId(null);
  };

  const [tarefaParaExcluir, setTarefaParaExcluir] = useState(null);

  const excluirTarefa = (id) => {
    salvarTarefas(tarefas.filter(t => t.id !== id));
    setTarefaParaExcluir(null);
  };

  const adicionarNota = async () => {
    const texto = novaNota.trim();
    if (!texto || !empresa?.id) return;
    setSalvandoNota(true);
    try {
      const nota = { texto, data: new Date().toISOString(), autor: auth.currentUser?.email || 'Gestor' };
      await setDoc(doc(db, 'cenarios', empresa.id), { notasGestor: [nota, ...notas] }, { merge: true });
      setNovaNota('');
    } catch (e) {
      console.error('[PHOENIX] Erro ao salvar nota:', e);
    } finally {
      setSalvandoNota(false);
    }
  };

  const excluirNota = async (indexParaExcluir) => {
    if (!empresa?.id) return;
    const listaAtualizada = notas.filter((_, i) => i !== indexParaExcluir);
    try {
      await setDoc(doc(db, 'cenarios', empresa.id), { notasGestor: listaAtualizada }, { merge: true });
    } catch (e) {
      console.error('[PHOENIX] Erro ao excluir nota:', e);
    }
  };

  const editarNota = async (indexParaEditar, novoTexto) => {
    if (!empresa?.id || !novoTexto.trim()) return;
    const listaAtualizada = notas.map((n, i) => i === indexParaEditar ? { ...n, texto: novoTexto.trim(), editadoEm: new Date().toISOString() } : n);
    try {
      await setDoc(doc(db, 'cenarios', empresa.id), { notasGestor: listaAtualizada }, { merge: true });
    } catch (e) {
      console.error('[PHOENIX] Erro ao editar nota:', e);
    }
    setNotaEditandoIndex(null);
  };

  const tarefasAtrasadas = tarefas.filter(t => !t.concluida && t.prazo && new Date(t.prazo) < new Date(new Date().toDateString()));

  // ==========================================
  // 🎯 PONTO DE EQUILÍBRIO — benefício do plano Plus. Indica o faturamento
  // mínimo para não operar no prejuízo. Aproximação: como o financeEngine.js
  // ainda não separa custo fixo de custo variável, tratamos o custo total
  // atual como a referência de equilíbrio (faturamento = custo, lucro =
  // zero). É uma aproximação simples e honesta — não é o cálculo contábil
  // clássico (que exige margem de contribuição), mas já orienta bem o
  // pequeno empresário. Quando o app passar a coletar custo fixo/variável
  // separadamente, este cálculo fica mais preciso sem mudar a interface.
  // ==========================================
  const pontoEquilibrio = empresa ? {
    valor: empresa.costs,
    diferenca: Math.abs(empresa.revenue - empresa.costs),
    acimaOuAbaixo: empresa.revenue >= empresa.costs ? 'acima' : 'abaixo'
  } : null;

  // ==========================================
  // 📝 PARECER — mesmo motor e MESMA função do app (parecerEngine.js →
  // gerarInsights), pra sair idêntico nos dois lugares. Antes o Dashboard
  // usava gerarParecer (3 blocos fixos), enquanto o app usa gerarInsights
  // (6 blocos, formato pergunta-resposta) — texto diferente pro mesmo
  // diagnóstico. Corrigido pra usar exatamente a mesma função do app.
  //
  // 🔥 CORREÇÃO DE TOM: eu tinha fixado isConsultor=false sempre, achando
  // que o Gestor sempre lê em tom simples — só que o app deixa a própria
  // pessoa escolher o nível de expertise no onboarding (Iniciante/
  // Avançado), e usa tom técnico quando ela escolhe "Avançado"
  // (usaTomTecnico = expertise === 'Avançado', ver MobileInterface.jsx).
  // Isso ficava salvo no Firestore, mas o Dashboard ignorava e sempre
  // mostrava o tom simples — causando o mesmo relatório aparecer técnico
  // no app e simples no Dashboard. Agora os dois lêem o mesmo campo.
  // ==========================================
  const usaTomTecnico = empresa?.expertise === 'Avançado';

  const insights = empresa && metrics && health ? (
    parecerIAParaBlocosPdf(empresa.parecerIA) || gerarInsights(
      {
        ebitda: metrics.ebitda,
        currentMargin: metrics.currentMargin,
        alavancagem: typeof metrics.alavancagem === 'number' ? metrics.alavancagem.toFixed(2) : metrics.alavancagem,
        status: { label: health.label }
      },
      {
        niche: (empresa.niche && empresa.niche !== '-') ? empresa.niche : empresa.sector,
        segment: empresa.sector,
        companyName: empresa.name,
        expertise: empresa.expertise || ''
      },
      usaTomTecnico
    )
  ) : [];

  // ==========================================
  // 📄 EXPORTAÇÃO DE PDF — usa o MESMO ReportEngine.jsx do app e do
  // Dashboard Consultor, sempre com isConsultor=false (linguagem simples)
  // e isRealConsultor=false (nunca leva marca de consultor no PDF do Gestor).
  // ==========================================
  const [exportando, setExportando] = useState(false);
  const [confirmandoDownload, setConfirmandoDownload] = useState(false);
  const basicPdfRef = useRef(null); // Relatório Master simples (ENTRY)
  // 🔥 NOVO (2026-10-02): quem tem acesso ao 360º (Total) agora também pode baixar o
  // Relatório Master resumido, como no app. Antes o Dashboard só oferecia o 360º.
  const [tipoRelatorio, setTipoRelatorio] = useState('completo');
  const superPdfRef = useRef(null); // Super Relatório 360º — Tactical, Plus e Total

  // 📝 BLOCO DE NOTAS AVULSO — Tarefas e Anotações NÃO entram mais no
  // relatório oficial (que é uma fotografia do momento da empresa, e
  // reimprimir toda vez que uma tarefa muda não faz sentido). Em vez
  // disso, exportamos elas separadamente, como um documento de trabalho
  // "a fazer" que pode ser atualizado e baixado de novo quantas vezes
  // precisar, sem gerar confusão com o dossiê financeiro formal.
  const [exportandoNotas, setExportandoNotas] = useState(false);
  const blocoNotasRef = useRef(null);

  const exportarBlocoDeNotas = () => {
    if (!empresa || !blocoNotasRef.current) return;
    setExportandoNotas(true);
    const opt = {
      margin: 0,
      filename: `Phoenix_Bloco_Notas_${empresa.name.replace(/\s+/g, '_')}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true, scrollX: 0, scrollY: 0 },
      jsPDF: { unit: 'px', format: [800, 1131], orientation: 'portrait' }
    };
    html2pdf().set(opt).from(blocoNotasRef.current).save().then(() => setExportandoNotas(false)).catch((e) => {
      console.error('[PHOENIX] Erro ao exportar bloco de notas:', e);
      setExportandoNotas(false);
    });
  };

  const exportarPDF = () => {
    // 🔥 CORREÇÃO: só o plano Total tem o Super Relatório 360º grátis e
    // ilimitado. Tactical e Plus ficam no Relatório Master por aqui —
    // o Super 360 avulso (R$ 1.490, a partir do Plus) é comprado e baixado
    // dentro do próprio app, não pelo Dashboard, porque é uma compra pontual
    // sem registro persistente no dossiê da empresa.
    const ehTotal = empresa?.plan === 'TOTAL';
    const usarMaster = !ehTotal || tipoRelatorio === 'master';
    const refParaExportar = usarMaster ? basicPdfRef : superPdfRef;
    if (!refParaExportar.current || !empresa) return;
    setExportando(true);
    const opt = {
      margin: 0,
      filename: nomeArquivoRelatorio(usarMaster ? 'master' : '360', empresa.name),
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true, scrollX: 0, scrollY: 0 },
      jsPDF: { unit: 'px', format: [800, 1131], orientation: 'portrait' }
    };
    html2pdf().set(opt).from(refParaExportar.current).save().then(() => setExportando(false)).catch((e) => {
      console.error('[PHOENIX] Erro ao exportar PDF:', e);
      setExportando(false);
    });
  };

  const dadosParaPDF = empresa && metrics ? {
    companyName: empresa.name,
    segment: (empresa.niche && empresa.niche !== '-') ? `${empresa.sector} (${empresa.niche})` : empresa.sector,
    ebitda: metrics.ebitda,
    margin: metrics.currentMargin,
    alavancagem: typeof metrics.alavancagem === 'number' ? metrics.alavancagem.toFixed(2).replace('.', ',') : String(metrics.alavancagem ?? '0,00'),
    valuation: formatCurrency(metrics.enterpriseValue),
    equity: formatCurrency(metrics.equityValue),
    score: String(extended?.maScore ?? '0'),
    projectedEbitda: formatCurrency(metrics.ebitdaAjustado),
    simRevenue, simCosts,
    safeDividends: formatCurrency(extended?.safeDividends),
    emergencyReserve: formatCurrency(extended?.emergencyReserve),
    ruptureRisk: metrics.ruptureRisk,
    sector: empresa.sector,
    debt: empresa.debt,
    custoNaoRecorrente: empresa.custoNaoRecorrente,
    custoDivida: empresa.custoDivida,
    ebitdaAjustado: metrics.ebitdaAjustado,
    // 🔥 CORREÇÃO: o Super 360 só é baixado por quem já é Total (ou comprou
    // o avulso R$ 1.490) — dentro dele não faz sentido mostrar "disponível
    // no Plus", já que o acesso ao relatório inteiro É o gate. A trava
    // continua existindo na TELA do dashboard (mais acima), só não repete
    // aqui dentro do PDF.
    pontoEquilibrio: pontoEquilibrio?.valor ?? null,
    caixaMinimo: extended?.caixaMinimo ?? null,
    // 🔥 CORREÇÃO: esse campo nunca era mandado pro relatório — a Necessidade
    // de Capital de Giro sempre aparecia zerada no PDF do Dashboard, mesmo
    // com o valor real disponível e correto na tela (CFO Virtual).
    ncg: extended?.ncg ?? 0,
    // 🔥 CORREÇÃO: esses 3 também nunca eram mandados — a Cobertura da Dívida
    // de Curto Prazo e a comparação de ciclo com o setor sempre ficavam
    // incompletas no PDF do Dashboard, mesmo com o dado real disponível.
    dividaCurtoPrazo: empresa.dividaCurtoPrazo,
    prazoRecebimento: empresa.prazoRecebimento,
    prazoPagamento: empresa.prazoPagamento,
    metaFinanceira: empresa.metaFinanceira || null,
    tarefas, notas
  } : null;

  const renderPaywall = (moduleName, planRequired, mostrarAvulso = false) => (
    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#010208]/90 backdrop-blur-md rounded-[3rem] border border-white/5">
      <div className="w-20 h-20 bg-slate-900 border border-white/10 rounded-full flex items-center justify-center mb-6 shadow-[0_0_50px_rgba(0,0,0,0.6)]">
        <Lock size={32} className="text-slate-500" />
      </div>
      <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter mb-4 text-center">Módulo Bloqueado</h3>
      <p className="text-sm text-slate-400 max-w-md text-center leading-relaxed mb-8 px-4">
        O acesso ao painel de <span className="text-white font-bold">{moduleName}</span> exige o protocolo <span className="text-lime-400 font-bold">{planRequired}</span> ou superior.
      </p>
      <button
        onClick={() => abrirCheckout(String(planRequired).toLowerCase())}
        className="bg-lime-400 text-black px-10 py-5 rounded-full font-black uppercase tracking-widest text-xs shadow-[0_0_30px_rgba(190,242,100,0.3)] hover:scale-105 active:scale-95 transition-all"
      >
        Assinar Protocolo {planRequired}
      </button>

      {/* 🔥 O Super Relatório 360º avulso (compra única, sem assinatura)
          continua indo pro app por enquanto — o checkout embutido aqui
          cobre os planos recorrentes (Tactical/Plus/Total). */}
      {mostrarAvulso && (
        <>
          <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest mt-6 mb-2">ou, sem assinatura</p>
          <button
            onClick={() => { if (typeof onSwitchMode === 'function') onSwitchMode('avulso'); }}
            className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-white transition-colors border border-white/10 px-6 py-3 rounded-full hover:bg-white/5"
          >
            Comprar Relatório Avulso — R$ 1.490,00 (uma vez, via app)
          </button>
        </>
      )}
    </div>
  );

  const menuItems = [
    { id: 'visao', label: 'Visão Geral', icon: Activity, level: 1 },
    { id: 'operacao', label: 'Operação', icon: Sliders, level: 2 },
    { id: 'cfo', label: 'CFO Virtual', icon: Landmark, level: 3 },
    { id: 'ma', label: 'M&A / Boardroom', icon: Briefcase, level: 4 },
    { id: 'config', label: 'Configurações', icon: Settings, level: 1 },
  ];

  // Efeito de logout tem prioridade sobre qualquer outro estado da tela
  // (carregando, sem empresa, etc.) — sempre que acionado, cobre tudo.
  if (logoutState.isLoggingOut) {
    return (
      <div className="h-screen w-screen bg-[#010208] flex flex-col items-center justify-center font-dashboard">
        <GestorDashboardStyles />
        <div className="relative flex items-center justify-center">
          <div className="absolute w-40 h-40 bg-lime-400/20 blur-[50px] rounded-full"></div>
          {logoutState.step === 1 && <Shield className="text-lime-400 w-16 h-16 animate-pulse relative z-10" />}
          {logoutState.step === 2 && <Trash2 className="text-lime-400 w-16 h-16 animate-pulse relative z-10" />}
          {logoutState.step === 3 && <CheckCircle2 className="text-lime-400 w-16 h-16 relative z-10" />}
        </div>
        <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter mt-8 mb-2 text-center">
          {logoutState.step === 1 && "Encerrando Túneis de Criptografia..."}
          {logoutState.step === 2 && "Limpando Cache de Dados Locais..."}
          {logoutState.step === 3 && "Sessão Encerrada"}
        </h2>
        <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest text-center max-w-xs">
          Protocolo de Segurança Phoenix OS®
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-screen w-screen bg-[#010208] flex items-center justify-center font-dashboard">
        <GestorDashboardStyles />
        <div className="text-lime-400 font-black uppercase tracking-widest text-sm animate-pulse">Carregando painel...</div>
      </div>
    );
  }

  if (!empresa) {
    return (
      <div className="h-screen w-screen bg-[#010208] flex flex-col items-center justify-center font-dashboard text-center px-6">
        <GestorDashboardStyles />
        <div className="flex items-center gap-2 mb-8 absolute top-8 left-8">
          <button
            onClick={() => { if (typeof onSwitchMode === 'function') onSwitchMode(); }}
            className="flex items-center gap-2 text-slate-400 hover:text-lime-400 text-[11px] font-black uppercase tracking-widest transition-colors"
          >
            App Móvel
          </button>
        </div>
        <ShieldCheck size={48} className="text-slate-700 mb-6" />
        <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter mb-3">Nenhuma Empresa Encontrada</h2>
        <p className="text-sm text-slate-500 max-w-sm">
          Gere um diagnóstico no aplicativo móvel para visualizar os dados aqui no seu Painel Desktop.
        </p>
      </div>
    );
  }

  // 🔥 ESCALONAMENTO — regra do produto: Entry não acessa o Dashboard (só o
  // app). Se a única empresa disponível for Entry, mostra a tela de
  // upgrade em vez do painel — não deixa nem a Visão Geral passar.
  if (empresa && empresa.plan === 'ENTRY') {
    // Quem JÁ assina um plano de Gestor não precisa de convite de upgrade: precisa saber como levar este relatório
    // para o painel (gerar a versão do plano pelo app). O Entry continua salvo no app, pago ou não.
    const nomePlanoAtivo = ['TACTICAL', 'PLUS', 'TOTAL'].includes(String(planoGestorReal || '').toUpperCase())
      ? String(planoGestorReal).charAt(0).toUpperCase() + String(planoGestorReal).slice(1).toLowerCase()
      : null;
    if (nomePlanoAtivo) {
      const outroRelatorio = dossies.find(d => d.plan !== 'ENTRY');
      return (
        <div className="h-screen w-screen bg-[#010208] flex flex-col items-center justify-center font-dashboard text-center px-6">
          <GestorDashboardStyles />
          <div className="w-20 h-20 bg-slate-900 border border-white/10 rounded-full flex items-center justify-center mb-6 shadow-[0_0_50px_rgba(0,0,0,0.6)]">
            <Lock size={32} className="text-slate-500" />
          </div>
          <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter mb-3">Este relatório é do tipo Entry</h2>
          <p className="text-sm text-slate-400 max-w-md mb-4 leading-relaxed">
            O Painel Desktop abre os relatórios gerados pelo seu plano <span className="text-lime-400 font-bold">{nomePlanoAtivo}</span>. Os relatórios Entry ficam salvos e disponíveis no app.
          </p>
          <div className="max-w-md w-full bg-[#0a0a12] border border-lime-400/20 rounded-[2rem] p-6 mb-8 text-left">
            <p className="text-[11px] font-black text-lime-400 uppercase tracking-widest mb-3">Para ver esta empresa no painel</p>
            <ol className="text-sm text-slate-300 space-y-2 list-decimal pl-5 leading-relaxed">
              <li>Abra o <strong className="text-white">app</strong> e entre em <strong className="text-white">Meus Dossiês</strong>.</li>
              <li>Abra este relatório Entry.</li>
              <li>Toque em <strong className="text-white">"Gerar versão {nomePlanoAtivo.toUpperCase()}"</strong>.</li>
            </ol>
            <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">A versão gerada pelo plano aparece aqui no painel. O Entry original continua salvo.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {outroRelatorio && (
              <button
                onClick={() => setEmpresaAtivaId(outroRelatorio.id)}
                className="bg-lime-400 text-black px-6 py-3 rounded-full font-black uppercase text-[11px] tracking-widest hover:scale-105 transition-all"
              >
                Ver outro relatório
              </button>
            )}
            <button
              onClick={() => { if (typeof onSwitchMode === 'function') onSwitchMode(); }}
              className="bg-white/5 text-white px-6 py-3 rounded-full font-black uppercase text-[11px] tracking-widest border border-white/10 hover:bg-white/10 transition-all"
            >
              Abrir o app móvel
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="h-screen w-screen bg-[#010208] flex flex-col items-center justify-center font-dashboard text-center px-6">
        <GestorDashboardStyles />
        <div className="flex items-center gap-2 mb-8 absolute top-8 left-8">
          <button
            onClick={() => { if (typeof onSwitchMode === 'function') onSwitchMode(); }}
            className="flex items-center gap-2 text-slate-400 hover:text-lime-400 text-[11px] font-black uppercase tracking-widest transition-colors"
          >
            App Móvel
          </button>
        </div>
        <div className="w-20 h-20 bg-slate-900 border border-white/10 rounded-full flex items-center justify-center mb-6 shadow-[0_0_50px_rgba(0,0,0,0.6)]">
          <Lock size={32} className="text-slate-500" />
        </div>
        <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter mb-3">Dashboard não incluso no Entry</h2>
        <p className="text-sm text-slate-500 max-w-md mb-10 leading-relaxed">
          O relatório avulso (Entry) dá acesso ao diagnóstico completo no app. O Painel Desktop — com Operação, CFO Virtual e M&A — é liberado a partir do protocolo <span className="text-lime-400 font-bold">Tactical</span>.
        </p>
        <div className="w-full max-w-md bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 text-left">
          <CheckoutAsaasCard
            planos={planosParaUpgrade}
            planoSelecionadoId="tactical"
            tipoPerfil="Gestor"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen w-screen bg-[#010208] text-white font-dashboard flex overflow-hidden ${tema === 'light' ? 'theme-light' : ''}`}>
      <GestorDashboardStyles />

      {/* AVISO DE CONEXÃO — some sozinho quando a internet volta */}
      {!online && (
        <div className="fixed top-0 left-0 right-0 z-[200] bg-red-500/90 backdrop-blur-sm text-white text-center py-2 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest">
          <WifiOff size={14} /> Sem conexão com a internet — tentando reconectar automaticamente
        </div>
      )}

      {/* ATUALIZANDO PARECER — aparece enquanto a IA reprocessa o parecer
          depois de uma edição de dados pelo Dashboard (bug corrigido: antes
          a edição pelo Dashboard nunca chamava a IA de novo). */}
      {atualizandoParecerIA && (
        <div className="fixed bottom-6 right-6 z-[200] flex items-center gap-3 bg-[#0a0a12] border border-purple-500/30 rounded-full pl-4 pr-5 py-3 shadow-2xl">
          <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest">Atualizando parecer com IA...</p>
        </div>
      )}

      {/* 🎉 CONFIRMAÇÃO VISUAL DE ASSINATURA — mesmo princípio do app e do
          Dashboard Consultor: o listener de plano já sabia na hora que uma
          assinatura tinha sido confirmada, mas nunca avisava a pessoa
          disso. Agora aparece um aviso fechável. */}
      {avisoAssinaturaConfirmada && (
        <div className="fixed bottom-6 right-6 z-[300] flex items-start gap-3 bg-[#0a0a12] border border-lime-400/30 rounded-2xl pl-4 pr-3 py-4 shadow-2xl max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-300">
          <CheckCircle2 size={20} className="text-lime-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-black text-white uppercase tracking-wide">Assinatura confirmada!</p>
            <p className="text-[11px] text-slate-400 mt-1">Plano <strong className="text-lime-400">{avisoAssinaturaConfirmada}</strong> já está ativo.</p>
          </div>
          <button
            onClick={() => setAvisoAssinaturaConfirmada(null)}
            className="text-slate-500 hover:text-white transition-colors shrink-0"
            aria-label="Fechar aviso"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* SIDEBAR */}
      <aside className="w-72 border-r border-white/5 flex flex-col justify-between p-6 shrink-0 overflow-y-auto custom-scroll">
        <div>
          <div className="flex items-center gap-2 mb-8">
            <button
              onClick={() => { if (typeof onSwitchMode === 'function') onSwitchMode(); }}
              className="flex items-center gap-2 text-slate-400 hover:text-lime-400 text-[11px] font-black uppercase tracking-widest transition-colors"
            >
              App Móvel
            </button>
          </div>

          <h1 className="text-2xl font-black italic tracking-tighter mb-1">
            PHOENIX<span className="text-lime-400">OS</span>
          </h1>
          <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold mb-8">Painel de Controle</p>

          <nav className="space-y-1 mb-8">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isLocked = !checkAccess(item.level);
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl text-xs font-bold uppercase tracking-wide transition-all ${isActive ? 'bg-lime-400/10 text-lime-400 border border-lime-400/20' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={16} />
                    {item.label}
                  </div>
                  {isLocked && <Lock size={12} className="text-slate-600" />}
                </button>
              );
            })}
            {/* 🤖 TEASER — Agentes de IA (Em Breve). Sem funcionalidade
            real por trás, só antecipa a feature e mede interesse. Fica
            fora do menuItems de propósito: não é "bloqueado por plano",
            é "ainda não existe". */}
            <button
              onClick={() => setActiveTab('agentes-ia')}
              className={`w-full flex items-center justify-between gap-2 px-3 py-3 rounded-2xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'agentes-ia' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}
            >
              <div className="flex items-center gap-3 whitespace-nowrap">
                <Bot size={16} className="shrink-0" />
                Agentes de IA
              </div>
              <span className="whitespace-nowrap shrink-0 text-[11px] font-black uppercase tracking-wide bg-amber-500/15 text-amber-400 px-2 py-1 rounded-full border border-amber-500/30">Em Breve</span>
            </button>
          </nav>

          {/* MEUS RELATÓRIOS — lista de todos os dossiês do Gestor, igual o
              app mostra na aba "Meus Dossiês". Clique troca a empresa ativa.
              Cada item mostra uma bolinha colorida com o status de saúde
              financeira daquela empresa (calculado na hora, sem precisar de
              estado extra). Com muitas empresas, a lista rola em vez de
              esticar a barra lateral — na prática, incomum pro perfil
              Gestor (que administra normalmente 1 empresa só). */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold flex items-center gap-2">
                <FileText size={11} /> Meus Relatórios
              </p>
            </div>
            <button
              onClick={abrirNovaEmpresa}
              className="w-full flex items-center justify-center gap-2 bg-lime-400/10 hover:bg-lime-400/20 text-lime-400 px-4 py-2.5 rounded-xl border border-lime-400/20 text-[11px] font-black uppercase tracking-widest transition-all mb-3"
            >
              <Plus size={14} /> Nova Empresa
            </button>
            {/* 🔥 CORREÇÃO: antes só aparecia com 2+ empresas — mas mesmo com 1
                empresa só, faz sentido ela aparecer aqui, até pra dar
                visibilidade de que a lista existe e o botão "+" está ali
                pra cadastrar mais quando precisar. */}
            {dossies.length > 0 && (
              <div className="space-y-1 max-h-56 overflow-y-auto custom-scroll pr-1">
                {dossies.map((d) => {
                  const coreD = calculateCoreMetrics(d.revenue, d.costs, d.debt, d.sector, {
                    custoNaoRecorrente: d.custoNaoRecorrente, custoDivida: d.custoDivida
                  });
                  const healthD = calculateHealthStatus({
                    ebitda: coreD.ebitdaAjustado, alavancagem: coreD.alavancagem, deb: d.debt, rev: d.revenue, sector: d.sector
                  });
                  const corD = obterCorStatus(healthD.colorKey);
                  return (
                    <button
                      key={d.id}
                      onClick={() => setEmpresaAtivaId(d.id)}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-xl text-[11px] font-bold transition-all ${d.id === empresaAtivaId ? 'bg-white/10 text-white border border-white/10' : 'text-slate-500 hover:bg-white/5 hover:text-slate-300'}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${corD.bola}`} title={healthD.label}></span>
                      <span className="truncate">{d.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {/* PAINEL DE STATUS — mesma lógica visual do app (ícone + "Status
              da Operação" + texto grande, com cor conforme a saúde
              financeira), só que adaptado à largura da barra lateral do
              Dashboard. Antes esse status só aparecia como uma pilulazinha
              pequena — agora tem o mesmo peso visual que tem no app. */}
          {health && (() => {
            const corStatus = obterCorStatus(health.colorKey);
            const IconeStatus = corStatus.Icone;
            return (
              <div className={`p-5 rounded-[2rem] border flex flex-col items-center text-center gap-2 shadow-lg ${corStatus.bg} ${corStatus.border}`}>
                <div className="p-2.5 rounded-full bg-black/20 shrink-0">
                  <IconeStatus size={24} className={corStatus.texto} />
                </div>
                <div className="min-w-0 w-full">
                  <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1">Status da Operação</p>
                  <p className={`text-xl font-black uppercase tracking-tight leading-tight ${corStatus.texto}`}>{health.label}</p>
                </div>
              </div>
            );
          })()}

          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Empresa Ativa</p>
              <div className="flex items-center gap-2">
                <button onClick={abrirEdicao} className="text-slate-500 hover:text-lime-400 transition-colors" title="Editar dados da empresa">
                  <Pencil size={12} />
                </button>
                <button onClick={() => setEmpresaParaExcluir(empresa)} className="text-slate-500 hover:text-red-400 transition-colors" title="Excluir empresa">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
            <p className="text-sm font-black text-white truncate">{empresa.name}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="inline-block text-[11px] px-2 py-0.5 rounded-md bg-lime-400/10 text-lime-400 font-black uppercase border border-lime-400/20">
                {empresa.plan}
              </span>
            </div>
          </div>

          <button
            onClick={handleSecureLogout}
            className="w-full flex items-center justify-center gap-2 text-slate-500 hover:text-white text-[11px] font-black uppercase tracking-widest transition-colors py-2"
          >
            <LogOut size={14} /> Sair
          </button>
        </div>
      </aside>

      {/* CONTEÚDO PRINCIPAL */}
      <main className="flex-1 overflow-y-auto custom-scroll p-10">
        {pagamentoAtrasado && (
          <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '12px', padding: '14px 20px', marginBottom: '24px', textAlign: 'center' }}>
            <span style={{ color: '#f87171', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              ⚠️ Não conseguimos identificar seu pagamento. Verifique sua forma de pagamento nas próximas{' '}
              {Math.max(0, Math.ceil(48 - (Date.now() - new Date(pagamentoAtrasado.desde).getTime()) / 3600000))}h para continuar usando o Phoenix OS{!temConsultorAtivoTambem ? ' (Gestor)' : ''}.
            </span>
          </div>
        )}
        {acessoSuspensoPorAtraso && (
          <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '12px', padding: '14px 20px', marginBottom: '24px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '12px', textAlign: 'center' }}>
            <span style={{ color: '#f87171', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              ⚠️ Não conseguimos identificar seu pagamento{!temConsultorAtivoTambem ? ' do Gestor' : ''}. Atualize sua forma de pagamento pra continuar usando {temConsultorAtivoTambem ? 'o Phoenix OS' : 'esse perfil'}.
            </span>
            <button
              onClick={() => setActiveTab('config')}
              style={{ backgroundColor: '#f87171', color: '#000', fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 16px', borderRadius: '9999px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Atualizar pagamento
            </button>
          </div>
        )}
        <header className="flex justify-between items-center mb-10">
          <div>
            <h2 className="text-3xl font-black italic uppercase tracking-tighter">
              {menuItems.find(i => i.id === activeTab)?.label}
            </h2>
            {/* Empresa + status ficam escondidos na aba Configurações — é uma
                aba de conta/preferências, não específica de uma empresa. */}
            {activeTab !== 'config' && (
              <div className="flex items-center gap-3 mt-1">
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                  {empresa.name} • {empresa.sector}
                </p>
                {/* STATUS NO TOPO — visível em qualquer aba de empresa, mesmo
                    princípio do painel "Status da Operação" do topo do app. */}
                {health && (() => {
                  const corTopo = obterCorStatus(health.colorKey);
                  return (
                    <span className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-widest border ${corTopo.bg} ${corTopo.texto} ${corTopo.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${corTopo.bola}`}></span>
                      {health.label}
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            {activeTab !== 'config' && (
              <button
                onClick={abrirEdicao}
                className="flex items-center gap-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 px-5 py-3 rounded-full border border-blue-500/20 text-[11px] font-black uppercase tracking-widest transition-all"
              >
                <FileSignature size={14} /> Editar Dados
              </button>
            )}
            <button
              onClick={() => setTema(t => t === 'light' ? 'dark' : 'light')}
              title={tema === 'light' ? 'Mudar para tema escuro' : 'Mudar para tema claro'}
              className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-full border border-white/10 transition-all"
            >
              {tema === 'light' ? <Moon size={16} className="text-slate-300" /> : <Sun size={16} className="text-slate-300" />}
            </button>
            <button
              onClick={() => setConfirmandoDownload(true)}
              disabled={exportando || atualizandoParecerIA}
              className="flex items-center gap-2 bg-lime-400/10 hover:bg-lime-400/20 text-lime-400 px-5 py-3 rounded-full border border-lime-400/20 text-[11px] font-black uppercase tracking-widest transition-all disabled:opacity-50"
            >
              <Download size={14} /> {exportando ? 'Gerando...' : (atualizandoParecerIA ? 'Atualizando parecer…' : 'Exportar PDF')}
            </button>
            <div className="flex items-center gap-4 bg-white/5 px-4 py-2 rounded-full border border-white/10">
              <div className="w-8 h-8 bg-lime-400/20 rounded-full flex items-center justify-center border border-lime-400/30">
                <User size={14} className="text-lime-400" />
              </div>
              <div>
                <p className="text-xs font-black text-white truncate max-w-[140px]">{auth.currentUser?.email || 'Gestor'}</p>
                <p className="text-[11px] text-slate-500 uppercase tracking-widest">Acesso {empresa.plan}</p>
              </div>
            </div>
          </div>
        </header>

        {/* ABA: VISÃO GERAL (nível 1 — sempre liberada) */}
        {activeTab === 'visao' && (
          <div className="space-y-6">
            {/* ONBOARDING — aparece só quando a empresa ainda não tem
                histórico de diagnóstico salvo (recém-cadastrada). Explica
                em 1 frase o que fazer primeiro, pra reduzir abandono.
                Some sozinho assim que o primeiro diagnóstico é salvo, e
                também pode ser fechado manualmente (fica salvo por
                empresa no localStorage). */}
            {(!empresa.historico || empresa.historico.length === 0) &&
              localStorage.getItem(`phoenix_onboarding_fechado_${empresa.id}`) !== '1' && (
                <div className="bg-lime-400/10 border border-lime-400/20 rounded-[2rem] p-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-lime-400/20 flex items-center justify-center shrink-0">
                    <Zap size={18} className="text-lime-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-black text-white mb-1">Bem-vindo! Vamos ao primeiro passo.</p>
                    <p className="text-xs text-slate-400">
                      Clique em "Editar Dados" no topo e preencha faturamento, custos e dívida da empresa. Assim que salvar, seu diagnóstico e gráficos aparecem aqui automaticamente.
                    </p>
                  </div>
                  <button
                    onClick={(e) => { localStorage.setItem(`phoenix_onboarding_fechado_${empresa.id}`, '1'); e.currentTarget.closest('.bg-lime-400\\/10').style.display = 'none'; }}
                    className="text-slate-400 hover:text-white transition-colors shrink-0"
                    title="Fechar"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

            {/* GRÁFICO GRANDE DE EVOLUÇÃO — impacto visual imediato, mesmo
                princípio dos gráficos grandes do Dashboard Consultor. */}
            <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold">Evolução do Lucro</p>
                <span className="text-2xl font-black text-white">R$ {formatCurrency(metrics.ebitdaAjustado)}</span>
              </div>
              <p className="text-xs text-slate-500 mb-2">Como o lucro da empresa mudou a cada diagnóstico salvo.</p>
              <GraficoEvolucaoGrande historico={empresa.historico} campo="ebitda" cor="#bef264" idGradiente="gradienteLucroGestor" />
            </div>

            {/* PARECER DA IA — teaser em destaque na Visão Geral, mesmo
                padrão do teaser da Estocástica logo abaixo. O texto
                completo mora só na aba Operação (não duplica aqui). */}
            <div className="bg-slate-900/40 border border-white/5 p-8 rounded-[2rem] relative">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={16} className="text-lime-400" />
                <p className="text-sm font-black text-lime-400 uppercase tracking-widest">Parecer da IA</p>
              </div>
              <p className="text-xs text-slate-500 mb-4 max-w-2xl">
                {empresa.parecerIA
                  ? 'Diagnóstico completo da sua empresa, gerado pela IA a partir dos dados salvos.'
                  : 'O parecer completo da IA está sendo preparado para esta empresa.'}
              </p>
              <button
                onClick={() => setActiveTab('operacao')}
                className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-lime-400 hover:text-lime-300 transition-colors"
              >
                Ver parecer completo <Sparkles size={12} />
              </button>
            </div>

            {/* IA ESTOCÁSTICA — teaser em destaque na Visão Geral (home).
                Antes só aparecia escondida dentro da aba Operação; agora
                todo mundo vê essa ferramenta logo de cara, com o horizonte
                de projeção liberado pelo plano atual e um convite claro
                pra fazer upgrade quando bloqueado. */}
            <div className="bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-500/20 p-8 rounded-[2rem] relative">
              <div className="flex items-center gap-2 mb-2">
                <Zap size={16} className="text-purple-400" />
                <p className="text-sm font-black text-purple-400 uppercase tracking-widest">IA Estocástica — Previsão de Futuro</p>
                <span className="ml-auto text-[11px] font-black uppercase tracking-widest bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full border border-purple-500/30">
                  {checkAccess(4) ? '3, 6, 12, 24 e 36 meses' : checkAccess(3) ? '3, 6 e 12 meses' : checkAccess(2) ? '3 e 6 meses' : 'Bloqueado'}
                </span>
              </div>
              <p className="text-sm font-black text-purple-300 italic mb-2">Pra onde esse negócio está indo?</p>
              <p className="text-xs text-slate-500 mb-4">
                Não é só um retrato de hoje — é pra onde sua empresa está indo. A IA Estocástica projeta o futuro do seu negócio a partir do histórico salvo, com horizonte que cresce conforme seu plano.
              </p>
              {checkAccess(2) ? (
                <button
                  onClick={() => setActiveTab('operacao')}
                  className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-purple-300 hover:text-purple-200 transition-colors"
                >
                  Ver projeção completa <Zap size={12} />
                </button>
              ) : (
                <button
                  onClick={() => abrirCheckout('tactical')}
                  className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-purple-300 hover:text-purple-200 transition-colors"
                >
                  <Lock size={11} /> Liberar com o Tactical
                </button>
              )}
            </div>

            {/* RESUMO EM PORTUGUÊS SIMPLES — a pergunta que todo pequeno
                empresário faz primeiro: "quanto sobrou" e "quanto preciso
                vender pra não ficar no vermelho". Lucro sempre visível
                (nível 1); Ponto de Equilíbrio é benefício do Tactical. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Wallet size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-2">Lucro do Mês</p>
                <p className="text-xs text-slate-500 mb-4">Resultado que permanece após a dedução de todos os custos da operação no período.</p>
                <p className={`text-3xl font-black ${metrics.ebitdaAjustado >= 0 ? 'text-lime-400' : 'text-red-400'}`}>
                  R$ {formatCurrency(metrics.ebitdaAjustado)}
                </p>
              </div>

              <div className="relative bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 overflow-hidden group pr-20">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Scale size={60} className="text-slate-300" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-2">Ponto de Equilíbrio</p>
                <p className="text-xs text-slate-500 mb-4">Faturamento mínimo necessário para cobrir integralmente os custos da operação, sem gerar lucro ou prejuízo.</p>
                {checkAccess(3) ? (
                  <>
                    <p className="text-3xl font-black text-white">R$ {formatCurrency(pontoEquilibrio.valor)}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      {pontoEquilibrio.acimaOuAbaixo === 'acima'
                        ? `Faturamento atual R$ ${formatCurrency(pontoEquilibrio.diferenca)} acima desse patamar.`
                        : `Faturamento atual R$ ${formatCurrency(pontoEquilibrio.diferenca)} abaixo desse patamar.`}
                    </p>
                  </>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600 font-bold">Disponível no Plus</p>
                    <button
                      onClick={() => abrirCheckout('plus')}
                      className="flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-lime-400 hover:text-lime-300 transition-colors"
                    >
                      <Lock size={11} /> Liberar
                    </button>
                  </div>
                )}
              </div>

              {/* CAIXA MÍNIMO — mesma lógica de trava do Ponto de Equilíbrio:
                  o cálculo é automático (usa o pró-labore, que era coletado
                  no cadastro mas nunca usado), mas a liberação segue o
                  plano, já que é um dado derivado do mesmo nível de análise. */}
              <div className="relative bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 overflow-hidden group pr-20">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Wallet size={60} className="text-slate-300" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-2">Caixa Mínimo Mensal</p>
                <p className="text-xs text-slate-500 mb-4">Custos fixos somados ao pró-labore dos sócios — o piso de caixa que a operação precisa ter todo mês pra não sufocar.</p>
                {checkAccess(3) ? (
                  <>
                    <p className="text-3xl font-black text-white">R$ {formatCurrency(extended?.caixaMinimo)}</p>
                    {extended?.proLaboreNum > 0 && (
                      <p className="text-xs text-slate-500 mt-2">
                        Inclui R$ {formatCurrency(extended.proLaboreNum)} de pró-labore mensal informado.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600 font-bold">Disponível no Plus</p>
                    <button
                      onClick={() => abrirCheckout('plus')}
                      className="flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-lime-400 hover:text-lime-300 transition-colors"
                    >
                      <Lock size={11} /> Liberar
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { label: 'Faturamento Mensal', value: empresa.revenue, campo: 'revenue', color: '#3b82f6' },
                { label: 'Custos Totais', value: empresa.costs, campo: 'costs', color: '#ef4444' },
                { label: 'Dívida Bancária', value: empresa.debt, campo: 'debt', color: '#f97316' },
                { label: 'Lucro Operacional', value: metrics.ebitdaAjustado, campo: 'ebitda', color: '#bef264' },
              ].map((card, i) => (
                <div key={i} className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-6">
                  <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">{card.label}</p>
                  <p className="text-lg sm:text-xl xl:text-2xl font-black mt-2 leading-tight" style={{ color: card.color }}>R$ {formatCurrency(card.value)}</p>
                  <GraficoEvolucao historico={empresa.historico} campo={card.campo} cor={card.color} />
                </div>
              ))}

              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-6 col-span-2">
                <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Margem Operacional</p>
                <p className="text-3xl font-black text-white mt-2">{String(metrics.currentMargin).replace('.', ',')}%</p>
              </div>
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-6 col-span-2">
                <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Alavancagem (Dívida vs. Lucro)<InfoTooltip texto="Mostra quantos anos de lucro a empresa levaria para quitar toda a dívida atual, mantendo o ritmo de hoje. Quanto menor, mais saudável." /></p>
                <p className="text-3xl font-black text-white mt-2">{String(metrics.alavancagem).replace('.', ',')}x</p>
              </div>
            </div>

            {/* PLANO DE AÇÃO — tarefas com prazo + anotações livres + meta
                financeira. Fica embutido aqui na Visão Geral, LOGO ABAIXO
                do gráfico de evolução (o Parecer da IA agora mora só na
                aba Operação, junto com a Estocástica e as Oportunidades —
                pra não duplicar a mesma informação em dois lugares). */}
            <div className="flex items-center justify-between">
              <p className="text-xs font-black text-white uppercase tracking-widest">Acompanhamento</p>
              <button
                onClick={exportarBlocoDeNotas}
                disabled={exportandoNotas}
                className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-lime-400 transition-colors border border-white/10 px-4 py-2 rounded-full hover:bg-white/5 disabled:opacity-50"
              >
                <Download size={12} /> {exportandoNotas ? 'Gerando...' : 'Baixar Bloco de Notas'}
              </button>
            </div>

            {/* META FINANCEIRA — meta definida pelo próprio Gestor, com
                barra de progresso comparando com o dado mais recente. */}
            <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20">
              <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Target size={60} className="text-lime-400" /></div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold flex items-center">
                  Meta Financeira
                  <InfoTooltip texto="Defina um alvo de faturamento ou de lucro para a empresa, e acompanhe aqui o quanto falta pra chegar lá, com base no último diagnóstico salvo." />
                </p>
                <button onClick={abrirEdicaoMeta} className="text-slate-500 hover:text-lime-400 transition-colors">
                  <Pencil size={13} />
                </button>
              </div>

              {progressoMeta ? (
                <div>
                  <div className="flex items-baseline justify-between mb-3">
                    <p className="text-xs text-slate-500">
                      {progressoMeta.tipo === 'lucro' ? 'Meta de Lucro Mensal' : 'Meta de Faturamento Mensal'}
                    </p>
                    <p className="text-sm font-black text-white">
                      R$ {formatCurrency(progressoMeta.atual)} <span className="text-slate-600 font-normal">/ R$ {formatCurrency(progressoMeta.valor)}</span>
                    </p>
                  </div>
                  <div className="w-full bg-black/40 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-2.5 rounded-full bg-lime-400 transition-all"
                      style={{ width: `${progressoMeta.percentual}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">
                    {progressoMeta.percentual >= 100
                      ? 'Meta atingida com base no último diagnóstico salvo. 🎉'
                      : `${progressoMeta.percentual.toFixed(0)}% da meta alcançada.`}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-slate-600">Nenhuma meta definida ainda. Clique no lápis pra criar uma.</p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* COLUNA 1: TAREFAS */}
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><ClipboardList size={60} className="text-lime-400" /></div>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold">Tarefas</p>
                  {tarefasAtrasadas.length > 0 && (
                    <span className="text-[11px] px-2 py-1 rounded-md bg-red-500/10 text-red-400 font-black uppercase border border-red-500/20">
                      {tarefasAtrasadas.length} atrasada{tarefasAtrasadas.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                <div className="flex gap-2 mb-6">
                  <input
                    type="text"
                    value={novaTarefaTexto}
                    onChange={(e) => setNovaTarefaTexto(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') adicionarTarefa(); }}
                    placeholder="Nova tarefa..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
                  />
                  <input
                    type="date"
                    value={novaTarefaPrazo}
                    onChange={(e) => setNovaTarefaPrazo(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-xs text-white focus:outline-none focus:border-lime-400/50 transition-colors"
                  />
                  <button
                    onClick={adicionarTarefa}
                    disabled={salvandoTarefa || !novaTarefaTexto.trim()}
                    className="w-12 h-12 shrink-0 flex items-center justify-center bg-lime-400 text-black rounded-xl disabled:opacity-40 transition-all"
                  >
                    <Plus size={18} />
                  </button>
                </div>

                <div className="space-y-3 max-h-[480px] overflow-y-auto custom-scroll pr-1">
                  {tarefas.length === 0 && (
                    <p className="text-xs text-slate-600 text-center py-8">Nenhuma tarefa ainda. Que tal começar com uma meta pros próximos 90 dias?</p>
                  )}
                  {tarefas.map((t) => {
                    const atrasada = !t.concluida && t.prazo && new Date(t.prazo) < new Date(new Date().toDateString());
                    const emEdicao = tarefaEditandoId === t.id;
                    return (
                      <div key={t.id} className={`flex items-start gap-3 p-4 rounded-2xl border transition-all ${atrasada ? 'bg-red-500/5 border-red-500/20' : 'bg-white/5 border-white/5'}`}>
                        <button onClick={() => alternarTarefaConcluida(t.id)} className="shrink-0 mt-0.5">
                          {t.concluida ? <CheckCircle2 size={20} className="text-lime-400" /> : <Circle size={20} className="text-slate-600" />}
                        </button>

                        {emEdicao ? (
                          <div className="flex-1 flex flex-col gap-2">
                            <input
                              type="text"
                              value={textoEdicaoTarefa}
                              onChange={(e) => setTextoEdicaoTarefa(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-lime-400/50"
                            />
                            <div className="flex gap-2">
                              <input
                                type="date"
                                value={prazoEdicaoTarefa}
                                onChange={(e) => setPrazoEdicaoTarefa(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-lime-400/50"
                              />
                              <button onClick={() => editarTarefa(t.id, textoEdicaoTarefa, prazoEdicaoTarefa)} className="text-[11px] font-black uppercase text-lime-400 px-3">Salvar</button>
                              <button onClick={() => setTarefaEditandoId(null)} className="text-[11px] font-black uppercase text-slate-500 px-3">Cancelar</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold ${t.concluida ? 'text-slate-600 line-through' : 'text-white'}`}>{t.texto}</p>
                            {t.prazo && (
                              <p className={`text-[11px] mt-1 font-bold uppercase tracking-widest ${atrasada ? 'text-red-400' : 'text-slate-500'}`}>
                                Prazo: {new Date(t.prazo + 'T00:00:00').toLocaleDateString('pt-BR')}
                              </p>
                            )}
                          </div>
                        )}

                        {!emEdicao && (
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => { setTarefaEditandoId(t.id); setTextoEdicaoTarefa(t.texto); setPrazoEdicaoTarefa(t.prazo || ''); }}
                              className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-white transition-colors"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              onClick={() => setTarefaParaExcluir(t)}
                              className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-red-400 transition-colors"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* COLUNA 2: ANOTAÇÕES */}
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><StickyNote size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-6">Anotações</p>

                <div className="flex gap-2 mb-6">
                  <textarea
                    value={novaNota}
                    onChange={(e) => setNovaNota(e.target.value)}
                    placeholder="Escreva uma anotação sobre a empresa..."
                    rows={2}
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors resize-none"
                  />
                  <button
                    onClick={adicionarNota}
                    disabled={salvandoNota || !novaNota.trim()}
                    className="w-12 shrink-0 flex items-center justify-center bg-lime-400 text-black rounded-xl disabled:opacity-40 transition-all"
                  >
                    <Plus size={18} />
                  </button>
                </div>

                <div className="space-y-4 max-h-[480px] overflow-y-auto custom-scroll pr-1">
                  {notas.length === 0 && (
                    <p className="text-xs text-slate-600 text-center py-8">Nenhuma anotação ainda.</p>
                  )}
                  {notas.map((nota, i) => {
                    const emEdicao = notaEditandoIndex === i;
                    return (
                      <div key={i} className="border-l-2 border-lime-400/20 pl-4 py-1 group/nota">
                        {emEdicao ? (
                          <div className="flex flex-col gap-2">
                            <textarea
                              value={textoEdicaoNota}
                              onChange={(e) => setTextoEdicaoNota(e.target.value)}
                              rows={2}
                              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-lime-400/50 resize-none"
                            />
                            <div className="flex gap-3">
                              <button onClick={() => editarNota(i, textoEdicaoNota)} className="text-[11px] font-black uppercase text-lime-400">Salvar</button>
                              <button onClick={() => setNotaEditandoIndex(null)} className="text-[11px] font-black uppercase text-slate-500">Cancelar</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm text-slate-300 leading-relaxed">{nota.texto}</p>
                              <p className="text-[11px] text-slate-600 uppercase tracking-widest mt-1.5">
                                {new Date(nota.data).toLocaleString('pt-BR')}
                                {nota.editadoEm && <span className="italic text-slate-700"> • editado</span>}
                              </p>
                            </div>
                            <div className="flex gap-1.5 opacity-0 group-hover/nota:opacity-100 transition-opacity shrink-0">
                              <button onClick={() => { setNotaEditandoIndex(i); setTextoEdicaoNota(nota.texto); }} className="text-slate-500 hover:text-white transition-colors">
                                <Pencil size={13} />
                              </button>
                              <button onClick={() => excluirNota(i)} className="text-slate-500 hover:text-red-400 transition-colors">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ABA: OPERAÇÃO (nível 2 — Tactical) — simulador "e se eu vender mais / cortar custo" */}
        {activeTab === 'operacao' && (
          <div className="relative min-h-[400px]">
            {!checkAccess(2) && renderPaywall('Operação', 'Tactical')}
            <div className={!checkAccess(2) ? 'opacity-20 pointer-events-none' : 'space-y-6'}>
              {/* PARECER DA IA (presente) — mesmo texto exibido no app,
                  só lido daqui. Vem primeiro: é o diagnóstico do agora,
                  antes da projeção de futuro. */}
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={16} className="text-lime-400" />
                  <p className="text-sm font-black text-lime-400 uppercase tracking-widest">Parecer da IA</p>
                </div>
                {empresa.parecerIA ? (
                  <div className="bg-black/20 border border-white/5 rounded-2xl p-6">
                    {formatarParecerIA(empresa.parecerIA)}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 text-center py-8">
                    {atualizandoParecerIA ? 'Gerando o parecer com IA…' : 'O parecer está sendo preparado.'}
                  </p>
                )}
              </div>

              {/* 🤖 IA REAL + NÚMEROS CALCULADOS — reestruturado em 3
                  BLOCOS (Curto/Médio/Longo), mesmo padrão do app. Cada
                  bloco tem seus próprios números (calculados na hora, sem
                  custo de IA) E seu próprio texto de IA — nunca mais um
                  texto único comentando prazos que o plano não libera. */}
              <div className="bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-500/20 p-8 rounded-[2rem] relative">
                <div className="flex items-center gap-2 mb-2">
                  <Zap size={16} className="text-purple-400" />
                  <p className="text-sm font-black text-purple-400 uppercase tracking-widest">Projeção por IA Estocástica</p>
                  <span className="ml-auto text-[11px] font-black uppercase tracking-widest bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full border border-purple-500/30">
                    {checkAccess(4) ? 'Total' : checkAccess(3) ? 'Plus' : 'Tactical'}
                  </span>
                </div>
                <p className="text-sm font-black text-purple-300 italic mb-2">Pra onde esse negócio está indo?</p>
                <p className="text-[11px] text-slate-500 mb-5">Não é só um retrato de hoje — é pra onde sua empresa está indo, em 3 cenários.</p>

                <div className="space-y-4">
                  {[
                    { chave: 'curto', titulo: 'Curto Prazo', meses: [3, 6], liberado: checkAccess(2), texto: empresa.leituraProjecaoCurto, nomePlano: 'Tactical' },
                    { chave: 'medio', titulo: 'Médio Prazo', meses: [12], liberado: checkAccess(3), texto: empresa.leituraProjecaoMedio, nomePlano: 'Plus' },
                    { chave: 'longo', titulo: 'Longo Prazo', meses: [24, 36], liberado: checkAccess(4), texto: empresa.leituraProjecaoLongo, nomePlano: 'Total' },
                  ].map((grupo) => {
                    if (!grupo.liberado) {
                      return (
                        <button
                          key={grupo.chave}
                          onClick={() => abrirCheckout(grupo.nomePlano.toLowerCase())}
                          className="w-full flex items-center justify-between bg-black/20 border border-white/5 rounded-2xl px-5 py-4 hover:border-purple-500/30 transition-all"
                        >
                          <p className="text-[11px] font-black text-slate-600 uppercase tracking-widest flex items-center gap-1.5">
                            <Lock size={11} /> {grupo.titulo} ({grupo.meses.join(' e ')} meses)
                          </p>
                          <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest">Desbloquear no {grupo.nomePlano}</p>
                        </button>
                      );
                    }

                    return (
                      <div key={grupo.chave} className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-5">
                        <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest mb-4">{grupo.titulo} ({grupo.meses.join(' e ')} meses)</p>

                        <div className="space-y-3">
                          {grupo.meses.map((h) => {
                            const dado = projecaoPorPrazo.find(p => p.months === h);
                            if (!dado) return null;
                            return (
                              <div key={h} className="bg-black/20 rounded-xl p-4">
                                <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-3 text-center">{h} meses</p>
                                {/* 🔥 CORRIGIDO: valores em bilhões encostavam uns nos
                                    outros. Cada cenário agora tem seu próprio painel. */}
                                <div className="grid grid-cols-3 gap-4 text-center">
                                  <div className="bg-white/[0.03] rounded-lg px-2 py-2.5">
                                    <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide mb-1">Pessimista</p>
                                    <p className="text-[13px] leading-tight font-black text-red-400 break-words">R$ {formatCurrency(dado.pessimista)}</p>
                                  </div>
                                  <div className="bg-white/[0.03] rounded-lg px-2 py-2.5">
                                    <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide mb-1">Provável</p>
                                    <p className="text-[13px] leading-tight font-black text-white break-words">R$ {formatCurrency(dado.provavel)}</p>
                                  </div>
                                  <div className="bg-white/[0.03] rounded-lg px-2 py-2.5">
                                    <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide mb-1">Otimista</p>
                                    <p className="text-[13px] leading-tight font-black text-lime-400 break-words">R$ {formatCurrency(dado.otimista)}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Texto de IA DESSE bloco — nunca um texto único
                            fixo comentando outros prazos. Enquanto não
                            chegou, mostra carregando de verdade. */}
                        {grupo.texto ? (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            {formatarParecerIA(grupo.texto)}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
                            {atualizandoParecerIA && <div className="w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin shrink-0" />}
                            <p className="text-[11px] text-slate-500 italic">{atualizandoParecerIA ? 'Gerando a análise desse prazo com IA...' : 'A análise desse prazo será preparada em instantes. Se não aparecer, use Editar dados e salve para gerar de novo.'}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="text-[11px] text-slate-600 uppercase tracking-widest font-bold mt-5 flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-purple-400" /> Previsão gerada por IA
                </p>
              </div>

              {/* OPORTUNIDADES ESTRATÉGICAS — exclusivo Total (mesma regra
                  aplicada no app: gestorPlan === 'total'). Vem por último:
                  é o "próximo nível", depois do diagnóstico e da projeção. */}
              {checkAccess(4) && empresa.oportunidadesIA && (
                <div className="bg-gradient-to-br from-amber-900/10 to-transparent border border-amber-500/20 p-8 rounded-[2rem]">
                  <div className="flex items-center gap-2 mb-4">
                    <Crown size={16} className="text-amber-400" />
                    <p className="text-sm font-black text-amber-400 uppercase tracking-widest">Oportunidades Estratégicas</p>
                  </div>
                  <div className="bg-black/20 border border-amber-500/10 rounded-2xl p-6">
                    {formatarParecerIA(empresa.oportunidadesIA)}
                  </div>
                </div>
              )}

              {/* SIMULADOR "E SE" — ferramenta interativa (não é leitura da
                  IA, é conta na hora com os sliders). Fica depois dos 3
                  blocos de IA, não mais no topo — assim quem entra na aba
                  vê primeiro o diagnóstico/projeção/oportunidades, e só
                  depois a ferramenta de simulação manual. */}
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Sliders size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-6">Simulador "E Se" — Cenário Projetado</p>

                <div className="grid grid-cols-2 gap-8 mb-8">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-xs text-slate-400 font-bold uppercase">Expansão de Receita</span>
                      <span className="text-lime-400 font-black">+{simRevenue}%</span>
                    </div>
                    <input type="range" min="0" max="50" value={simRevenue}
                      onChange={(e) => setSimRevenue(Number(e.target.value))}
                      className="w-full accent-lime-400" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-xs text-slate-400 font-bold uppercase">Corte de Custos (OPEX)</span>
                      <span className="text-lime-400 font-black">-{simCosts}%</span>
                    </div>
                    <input type="range" min="0" max="30" value={simCosts}
                      onChange={(e) => setSimCosts(Number(e.target.value))}
                      className="w-full accent-lime-400" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-5">
                  <div className="bg-black/30 rounded-2xl p-5">
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Lucro Projetado</p>
                    <p className="text-xl font-black text-white mt-2">R$ {formatCurrency(projection.projectedEbitda)}</p>
                  </div>
                  <div className="bg-black/30 rounded-2xl p-5">
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Ganho Estimado</p>
                    <p className="text-xl font-black text-lime-400 mt-2">R$ {formatCurrency(projection.gains)}</p>
                  </div>
                  <div className="bg-black/30 rounded-2xl p-5">
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Margem Projetada</p>
                    <p className="text-xl font-black text-white mt-2">{String(projection.projectedMargin).replace('.', ',')}%</p>
                  </div>
                </div>
              </div>

              <div className="bg-lime-400/5 border border-lime-400/20 rounded-[2rem] p-8 text-center">
                <p className="text-[11px] text-lime-500 uppercase tracking-widest font-bold mb-3">Projeção Anualizada (12 Meses)</p>
                <p className="text-3xl font-black text-lime-400">R$ {formatCurrency(projection.gains * 12)}</p>
                <p className="text-[11px] text-slate-500 mt-3 max-w-md mx-auto">
                  Geração de caixa adicional estimada caso o cenário simulado seja mantido ao longo de um ano.
                </p>
              </div>

              {/* CALCULADORA DE PRECIFICAÇÃO */}
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Tag size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-1 flex items-center">
                  Calculadora de Precificação
                  <InfoTooltip texto="Ajuda a definir o preço de venda de um produto ou serviço, considerando o custo, os impostos/taxas envolvidos e a margem de lucro que você quer ter." />
                </p>
                <p className="text-xs text-slate-500 mb-6">Descubra quanto cobrar por um produto ou serviço pra ter a margem de lucro que você quer.</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Custo do Produto/Serviço (R$)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={precoCusto}
                      onChange={(e) => setPrecoCusto(aplicarMascaraMoeda(e.target.value))}
                      placeholder="0,00"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Impostos e Taxas</label>
                      <span className="text-lime-400 font-black text-xs">{precoDespesas}%</span>
                    </div>
                    <input type="range" min="0" max="40" value={precoDespesas}
                      onChange={(e) => setPrecoDespesas(Number(e.target.value))}
                      className="w-full accent-lime-400 mt-3" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Margem de Lucro Desejada</label>
                      <span className="text-lime-400 font-black text-xs">{precoMargem}%</span>
                    </div>
                    <input type="range" min="0" max="60" value={precoMargem}
                      onChange={(e) => setPrecoMargem(Number(e.target.value))}
                      className="w-full accent-lime-400 mt-3" />
                  </div>
                </div>

                {calculoPreco.valido ? (
                  <div className="grid grid-cols-3 gap-5">
                    <div className="bg-black/30 rounded-2xl p-5 text-center col-span-3 md:col-span-1">
                      <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Preço de Venda Sugerido</p>
                      <p className="text-2xl font-black text-lime-400 mt-2">R$ {formatCurrency(calculoPreco.precoSugerido)}</p>
                    </div>
                    <div className="bg-black/30 rounded-2xl p-5 text-center">
                      <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Lucro por Unidade</p>
                      <p className="text-xl font-black text-white mt-2">R$ {formatCurrency(calculoPreco.lucroReais)}</p>
                    </div>
                    <div className="bg-black/30 rounded-2xl p-5 text-center">
                      <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Impostos e Taxas</p>
                      <p className="text-xl font-black text-white mt-2">R$ {formatCurrency(calculoPreco.despesasReais)}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 text-center py-6 bg-black/20 rounded-2xl">
                    {precoCusto ? 'Impostos + Margem somam 100% ou mais — ajuste os percentuais para calcular um preço válido.' : 'Informe o custo do produto ou serviço para calcular o preço sugerido.'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ABA: CFO VIRTUAL (nível 3 — Plus) */}
        {activeTab === 'cfo' && (
          <div className="relative min-h-[400px]">
            {!checkAccess(3) && renderPaywall('CFO Virtual', 'Plus')}
            <div className={!checkAccess(3) ? 'opacity-20 pointer-events-none' : 'grid grid-cols-2 gap-5'}>
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Coins size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-4">Dividendos Seguros / Ano<InfoTooltip texto="Valor estimado que os sócios podem retirar por ano sem comprometer o caixa da operação nem o pagamento de dívidas." /></p>
                <p className="text-3xl font-black text-white">R$ {formatCurrency(extended.safeDividends)}</p>
                <p className="text-[11px] text-slate-500 mt-2">Limite estimado de retirada dos sócios sem comprometer o capital de giro e o pagamento de dívidas.</p>
              </div>
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><ShieldCheck size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-4">Reserva de Emergência (6 meses)<InfoTooltip texto="Quanto a empresa precisaria ter guardado em caixa para continuar pagando as contas por 6 meses, mesmo sem faturar nada." /></p>
                <p className="text-3xl font-black text-white">R$ {formatCurrency(extended.emergencyReserve)}</p>
                <p className="text-[11px] text-slate-500 mt-2">Caixa alvo para suportar 6 meses sem receita.</p>
              </div>
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20 col-span-2">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><RefreshCw size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-4">Necessidade de Capital de Giro<InfoTooltip texto="Dinheiro necessário para cobrir o intervalo entre pagar os fornecedores e receber dos clientes. É o 'combustível' do dia a dia da operação." /></p>
                <p className={`text-3xl font-black ${extended.ncg < 0 ? 'text-lime-400' : 'text-white'}`}>
                  {extended.ncg < 0 ? '+ ' : ''}R$ {formatCurrency(Math.abs(extended.ncg))}
                </p>
                <p className="text-[11px] text-slate-500 mt-2">
                  {extended.ncg < 0
                    ? 'Sobra de caixa: ciclo favorável, seus fornecedores financiam parte da operação.'
                    : 'Caixa necessário para cobrir o intervalo entre pagar fornecedores e receber dos clientes.'}
                </p>
              </div>
              <div className="bg-slate-900/40 border border-red-500/20 rounded-[2rem] p-8 relative overflow-hidden group pr-20 col-span-2 text-center">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><AlertTriangle size={60} className="text-red-400" /></div>
                <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold mb-4">Risco de Ruptura de Caixa (12 Meses)<InfoTooltip texto="Estimativa da chance da empresa ficar sem caixa suficiente para pagar suas contas nos próximos 12 meses, considerando dívida e margem atuais." /></p>
                <p className={`text-4xl font-black ${metrics.ruptureRisk > 50 ? 'text-red-500' : metrics.ruptureRisk > 30 ? 'text-orange-400' : 'text-lime-400'}`}>
                  {metrics.ruptureRisk}<span className="text-lg">%</span>
                </p>
                <div className="w-full bg-black/40 rounded-full h-2 overflow-hidden my-4">
                  <div className="h-2 rounded-full" style={{ width: `${metrics.ruptureRisk}%`, backgroundColor: metrics.ruptureRisk > 60 ? '#ef4444' : metrics.ruptureRisk > 30 ? '#f97316' : '#bef264' }} />
                </div>
                <p className="text-[11px] text-slate-500">
                  Probabilidade estimada de inadimplência com base na relação entre dívida e margem atual.
                  {metrics.ruptureRisk > 50 && ' Risco crítico — recomenda-se reduzir passivos.'}
                </p>
              </div>

              {/* SIMULADOR DE EMPRÉSTIMO */}
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20 col-span-2">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><CreditCard size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-1 flex items-center">
                  Simulador de Empréstimo
                  <InfoTooltip texto="Calcula a parcela mensal estimada de um empréstimo (fórmula Price, com parcelas fixas) e compara com o lucro mensal atual da empresa, pra ajudar a avaliar se cabe no orçamento." />
                </p>
                <p className="text-xs text-slate-500 mb-6">Simule se um empréstimo cabe no orçamento da empresa antes de assumir o compromisso.</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Valor do Empréstimo (R$)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={emprestimoValor}
                      onChange={(e) => setEmprestimoValor(aplicarMascaraMoeda(e.target.value))}
                      placeholder="0,00"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Taxa de Juros ao Ano</label>
                      <span className="text-lime-400 font-black text-xs">{emprestimoJurosAno}%</span>
                    </div>
                    <input type="range" min="0" max="80" value={emprestimoJurosAno}
                      onChange={(e) => setEmprestimoJurosAno(Number(e.target.value))}
                      className="w-full accent-lime-400 mt-3" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Prazo (meses)</label>
                      <span className="text-lime-400 font-black text-xs">{emprestimoPrazoMeses}</span>
                    </div>
                    <input type="range" min="1" max="60" value={emprestimoPrazoMeses}
                      onChange={(e) => setEmprestimoPrazoMeses(Number(e.target.value))}
                      className="w-full accent-lime-400 mt-3" />
                  </div>
                </div>

                {calculoEmprestimo.valido ? (
                  <>
                    <div className="grid grid-cols-3 gap-5 mb-5">
                      <div className="bg-black/30 rounded-2xl p-5 text-center">
                        <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Parcela Mensal</p>
                        <p className="text-xl font-black text-white mt-2">R$ {formatCurrency(calculoEmprestimo.parcela)}</p>
                      </div>
                      <div className="bg-black/30 rounded-2xl p-5 text-center">
                        <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Total de Juros</p>
                        <p className="text-xl font-black text-white mt-2">R$ {formatCurrency(calculoEmprestimo.totalJuros)}</p>
                      </div>
                      <div className="bg-black/30 rounded-2xl p-5 text-center">
                        <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Total Pago no Fim</p>
                        <p className="text-xl font-black text-white mt-2">R$ {formatCurrency(calculoEmprestimo.totalPago)}</p>
                      </div>
                    </div>
                    <div className={`rounded-2xl p-5 border text-center ${calculoEmprestimo.nivel === 'critico' ? 'bg-red-500/5 border-red-500/20' :
                      calculoEmprestimo.nivel === 'atencao' ? 'bg-orange-500/5 border-orange-500/20' :
                        'bg-lime-400/5 border-lime-400/20'
                      }`}>
                      {calculoEmprestimo.comprometimento !== null && (
                        <p className={`text-2xl font-black mb-2 ${calculoEmprestimo.nivel === 'critico' ? 'text-red-400' :
                          calculoEmprestimo.nivel === 'atencao' ? 'text-orange-400' : 'text-lime-400'
                          }`}>
                          {calculoEmprestimo.comprometimento.toFixed(0)}%<span className="text-xs text-slate-500 font-bold uppercase ml-2">do lucro mensal</span>
                        </p>
                      )}
                      <p className="text-xs text-slate-400 max-w-lg mx-auto">{calculoEmprestimo.veredito}</p>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-slate-500 text-center py-6 bg-black/20 rounded-2xl">
                    Informe o valor do empréstimo para simular a parcela e o impacto no orçamento.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ABA: M&A / BOARDROOM (nível 4 — Total) */}
        {activeTab === 'ma' && (
          <div className="relative min-h-[400px]">
            {!checkAccess(4) && renderPaywall('M&A / Boardroom', 'Total', true)}
            <div className={!checkAccess(4) ? 'opacity-20 pointer-events-none' : 'grid grid-cols-2 gap-5'}>
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20 col-span-2">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Briefcase size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-4">Patrimônio Líquido (Equity Value)<InfoTooltip texto="O que sobraria para os sócios se a empresa fosse vendida hoje, depois de pagar toda a dívida. É uma estimativa de 'quanto vale seu bolso' no negócio." /></p>
                <p className="text-3xl font-black text-white">R$ {formatCurrency(metrics.equityValue)}</p>
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/5">
                  <div>
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Múltiplo Base<InfoTooltip texto="Número usado pelo mercado para converter o lucro anual da empresa em valor de venda. Varia por setor — comércio e serviço costumam ter múltiplos diferentes de indústria." /></p>
                    <p className="text-lg font-black text-white">{metrics.multiple}x</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Dívida Deduzida</p>
                    <p className="text-lg font-black text-red-400">R$ {formatCurrency(empresa.debt)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20 col-span-2">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Building2 size={60} className="text-lime-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-4">Enterprise Value (EV)<InfoTooltip texto="Valor total do negócio em si (a operação), sem considerar dívidas ou caixa. É o número que fundos e investidores costumam olhar primeiro numa negociação." /></p>
                <p className="text-xs text-slate-500 mb-3">Valor total da operação (Lucro Projetado x Múltiplo do Setor), desconsiderando a forma como ela é financiada.</p>
                <p className="text-3xl font-black text-white">R$ {formatCurrency(metrics.enterpriseValue)}</p>
              </div>

              {sensibilidadeValuation && (
                <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20 col-span-2">
                  <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><BarChart3 size={60} className="text-lime-400" /></div>
                  <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-2">Sensibilidade do Valuation</p>
                  <p className="text-xs text-slate-500 mb-6">O quanto o Patrimônio Líquido (Equity Value) muda se a margem operacional variar 5 pontos percentuais para cima ou para baixo.</p>
                  <div className="space-y-4">
                    {sensibilidadeValuation.cenarios.map((c, i) => (
                      <div key={i}>
                        <div className="flex justify-between items-baseline mb-1.5">
                          <span className={`text-xs font-bold uppercase ${c.label === 'Atual' ? 'text-white' : 'text-slate-500'}`}>{c.label}</span>
                          <span className="text-xs font-black text-white">R$ {formatCurrency(c.equity)}</span>
                        </div>
                        <div className="w-full bg-black/40 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full ${c.label === 'Atual' ? 'bg-lime-400' : 'bg-slate-600'}`}
                            style={{ width: `${(c.equity / sensibilidadeValuation.maxEquity) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden group pr-20 col-span-2 text-center">
                <div className="phoenix-watermark-icon absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none"><Target size={60} className="text-purple-400" /></div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-4">M&A Readiness Score<InfoTooltip texto="Nota de 0 a 100 que resume o quão pronta a empresa está para uma venda, fusão ou entrada de sócio investidor, considerando saúde financeira e governança." /></p>
                <p className="text-4xl font-black text-white">{extended.maScore}<span className="text-lg text-slate-500">/100</span></p>
                <p className="text-[11px] text-slate-500 mt-4 max-w-sm mx-auto">
                  {extended.maScore > 70
                    ? 'Empresa com governança atrativa para fundos ou transição geracional segura.'
                    : 'Métricas sob risco — atenção redobrada em caso de sucessão familiar ou diligência de fundos.'}
                </p>
              </div>

              {/* CHECKLIST DUE DILIGENCE — mesmo conteúdo do app. Hoje é
                  informativo (não salva marcação), igual no app. Se
                  quisermos tornar funcional (marcar e persistir no
                  Firestore) no futuro, replicamos nos dois lugares juntos. */}
              <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 col-span-2 text-left">
                <div className="flex items-center gap-3 border-b border-white/5 pb-4 mb-5">
                  <Briefcase className="text-purple-400" size={20} />
                  <h4 className="text-xs font-black text-white uppercase tracking-widest">Checklist Due Diligence</h4>
                  <span className="ml-auto text-[11px] text-slate-500 font-bold">
                    {Object.values(empresa.dueDiligenceChecklist || {}).filter(Boolean).length}/2 concluído
                  </span>
                </div>
                <div className="space-y-5">
                  <button onClick={() => toggleChecklistItem('auditoria')} className="w-full flex items-start gap-4 text-left group">
                    <div className={`w-6 h-6 rounded border shrink-0 mt-0.5 flex items-center justify-center transition-all ${empresa.dueDiligenceChecklist?.auditoria ? 'bg-lime-400 border-lime-400' : 'bg-slate-800 border-white/10 group-hover:border-white/30 phoenix-caixa-check'}`}>
                      {empresa.dueDiligenceChecklist?.auditoria && <Check size={14} className="text-black" strokeWidth={3} />}
                    </div>
                    <div>
                      <p className={`text-[11px] font-black uppercase tracking-widest mb-1 ${empresa.dueDiligenceChecklist?.auditoria ? 'text-slate-500 line-through' : 'text-white'}`}>Auditoria Contábil Independente</p>
                      <p className="text-[11px] text-slate-500 leading-relaxed">Balanços dos últimos 3 anos livres de confusão patrimonial (separação Pessoa Física/Jurídica).</p>
                    </div>
                  </button>
                  <button onClick={() => toggleChecklistItem('acordoSocios')} className="w-full flex items-start gap-4 text-left group">
                    <div className={`w-6 h-6 rounded border shrink-0 mt-0.5 flex items-center justify-center transition-all ${empresa.dueDiligenceChecklist?.acordoSocios ? 'bg-lime-400 border-lime-400' : 'bg-slate-800 border-white/10 group-hover:border-white/30 phoenix-caixa-check'}`}>
                      {empresa.dueDiligenceChecklist?.acordoSocios && <Check size={14} className="text-black" strokeWidth={3} />}
                    </div>
                    <div>
                      <p className={`text-[11px] font-black uppercase tracking-widest mb-1 ${empresa.dueDiligenceChecklist?.acordoSocios ? 'text-slate-500 line-through' : 'text-white'}`}>Acordo de Sócios CVM</p>
                      <p className="text-[11px] text-slate-500 leading-relaxed">Cláusulas de sucessão e regras claras de saída de sócios revisadas juridicamente.</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB NOVA: TEASER "AGENTES DE IA" (Em Breve) — sem
        funcionalidade real por trás, só antecipa a feature e mede
        interesse. Não precisa de e-mail nem conta separada — só um
        clique, com contador compartilhado entre todos os Gestores. */}
        {activeTab === 'agentes-ia' && (
          <div className="animate-in fade-in duration-500 max-w-2xl mx-auto w-full flex-1 flex flex-col justify-center pb-12">
            <div className="relative bg-gradient-to-br from-amber-950/30 via-[#020617] to-[#020617] p-14 rounded-[3rem] border border-amber-500/20 overflow-hidden text-center shadow-[0_0_60px_rgba(245,158,11,0.08)]">
              <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #f59e0b 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
              <div className="absolute -top-16 -right-16 w-56 h-56 bg-amber-500/10 rounded-full blur-3xl" />

              <div className="relative z-10">
                <div className="w-24 h-24 mx-auto mb-8 rounded-[2rem] bg-gradient-to-br from-amber-400/20 to-amber-600/5 border border-amber-400/30 flex items-center justify-center shadow-[0_0_40px_rgba(245,158,11,0.25)]">
                  <Bot size={44} className="text-amber-400" />
                </div>

                <span className="inline-flex items-center gap-2 bg-amber-500/15 text-amber-400 px-5 py-2 rounded-full text-[11px] font-black uppercase tracking-[0.25em] border border-amber-500/30 mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> Em Breve
                </span>

                <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-[0.95] mb-5">
                  Agentes de <span className="text-amber-400">IA</span>
                </h2>
                <p className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto mb-10">
                  Sua empresa sendo monitorada sozinha: alertas automáticos assim que algo mudar, tarefas geradas pra você sem precisar pedir, e leitura de dados bagunçados (planilhas, extratos) — o Phoenix OS trabalhando por trás, não só quando você abre o painel.
                </p>

                <div className="grid sm:grid-cols-3 gap-4 max-w-lg mx-auto mb-10">
                  {[
                    { titulo: 'Monitoramento 24/7', desc: 'Sua operação acompanhada mesmo com o painel fechado' },
                    { titulo: 'Tarefas Automáticas', desc: 'Alertas e ações sugeridas geradas sozinhas' },
                    { titulo: 'Leitura Inteligente', desc: 'Entende planilhas e extratos bagunçados sozinho' },
                  ].map((item, i) => (
                    <div key={i} className="bg-black/20 border border-amber-500/10 rounded-2xl p-5 text-left">
                      <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-3">
                        <Check size={14} className="text-amber-400" />
                      </div>
                      <p className="text-xs font-black text-white uppercase tracking-widest mb-1.5">{item.titulo}</p>
                      <p className="text-[11px] text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>

                {jaRegistrouInteresseAgentes ? (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-full py-4 px-8 inline-flex items-center gap-2.5">
                    <CheckCircle2 size={18} className="text-amber-400" />
                    <p className="text-xs font-black text-amber-400 uppercase tracking-widest">Interesse registrado!</p>
                  </div>
                ) : (
                  <button
                    onClick={registrarInteresseAgentesIA}
                    disabled={registrandoInteresseAgentes}
                    className="bg-amber-400 hover:bg-amber-300 text-black py-4 px-10 rounded-full font-black uppercase text-xs tracking-widest active:scale-95 transition-all disabled:opacity-60 shadow-[0_0_30px_rgba(245,158,11,0.2)]"
                  >
                    {registrandoInteresseAgentes ? 'Registrando...' : 'Tenho Interesse'}
                  </button>
                )}

                {contadorInteresseAgentes?.totalGestor > 0 && (
                  <p className="text-xs text-slate-500 mt-5 font-bold">
                    <span className="text-amber-400 font-black">{contadorInteresseAgentes.totalGestor}</span> {contadorInteresseAgentes.totalGestor === 1 ? 'gestor já demonstrou' : 'gestores já demonstraram'} interesse
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ABA: CONFIGURAÇÕES (nível 1 — sempre liberada) */}
        {activeTab === 'config' && (() => {
          const PLANOS_GESTOR_INFO = {
            TACTICAL: { nome: 'Tactical', preco: 'R$ 189/mês', proximo: 'PLUS' },
            PLUS: { nome: 'Plus', preco: 'R$ 499/mês', proximo: 'TOTAL' },
            TOTAL: { nome: 'Total', preco: 'R$ 799/mês', proximo: null },
          };
          const infoPlanoAtual = PLANOS_GESTOR_INFO[empresa.plan] || null;
          const infoProximoPlano = infoPlanoAtual?.proximo ? PLANOS_GESTOR_INFO[infoPlanoAtual.proximo] : null;

          return (
            <div className="max-w-4xl mx-auto space-y-8">
              <div className="bg-slate-900/60 p-10 md:p-12 rounded-[3rem] border border-white/5 shadow-2xl">
                <div className="flex items-center gap-5 mb-10 border-b border-white/5 pb-8">
                  <Settings className="text-lime-400" size={36} />
                  <div>
                    <h3 className="text-2xl md:text-3xl font-black text-white uppercase italic tracking-tighter">Preferências do Painel</h3>
                    <p className="text-xs text-slate-500 uppercase font-bold tracking-widest mt-1">Aparência, conta e dados da sua empresa</p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="p-8 bg-black/20 rounded-[2rem] border border-white/5">
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Conta</p>
                      <p className="text-lg font-black text-white italic truncate">{auth.currentUser?.email || '—'}</p>
                    </div>
                    <div className="p-8 bg-black/20 rounded-[2rem] border border-white/5">
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Plano Ativo</p>
                      <p className="text-lg font-black text-lime-400 italic">{infoPlanoAtual?.nome || empresa.plan}</p>
                      {infoPlanoAtual && (
                        <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest mt-1">{infoPlanoAtual.preco}</p>
                      )}
                    </div>
                  </div>

                  {infoProximoPlano && (
                    <div className="p-8 bg-gradient-to-r from-lime-400/10 to-transparent border border-lime-400/20 rounded-[2.5rem] flex flex-col md:flex-row items-start md:items-center justify-between shadow-lg gap-6">
                      <div className="space-y-2">
                        <div className="bg-lime-400/20 text-lime-400 px-3 py-1 rounded text-[11px] font-black uppercase tracking-widest italic w-fit border border-lime-400/30">Upgrade Disponível</div>
                        <h4 className="text-lg font-black text-white uppercase italic tracking-tighter">Plano {infoProximoPlano.nome}</h4>
                        <p className="text-xs text-slate-400 max-w-xl leading-relaxed">Libere mais módulos de análise financeira por <strong className="text-white">{infoProximoPlano.preco}</strong>.</p>
                      </div>
                      <button
                        onClick={() => abrirCheckout(infoPlanoAtual.proximo.toLowerCase())}
                        className="bg-lime-400 text-black px-6 py-3 rounded-xl font-black uppercase text-[11px] tracking-widest shrink-0 hover:scale-105 transition-all"
                      >
                        Assinar Protocolo {infoProximoPlano.nome}
                      </button>
                    </div>
                  )}

                  {empresa.plan === 'TOTAL' && (
                    <div className="p-8 bg-lime-400/5 border border-lime-400/20 rounded-[2.5rem] flex items-center gap-4">
                      <ShieldCheck className="text-lime-400 shrink-0" size={28} />
                      <p className="text-xs text-lime-400/90 font-black tracking-normal">🏆 Você atingiu o nível máximo do protocolo Gestor</p>
                    </div>
                  )}

                  {/* GERENCIAR PAGAMENTO — checkout embutido sempre disponível
                      aqui, não só no card de upgrade sugerido. Permite trocar
                      de plano a qualquer momento sem sair do Dashboard. */}
                  <div className="p-8 bg-black/20 rounded-[2.5rem] border border-white/5">
                    <button
                      onClick={() => setMostrarCheckoutConfig(v => !v)}
                      className="w-full flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard size={18} className="text-lime-400" />
                        <span className="text-sm font-black text-white uppercase tracking-widest">Gerenciar Pagamento / Trocar de Plano</span>
                      </div>
                      <ChevronDown size={18} className={`text-slate-500 transition-transform ${mostrarCheckoutConfig ? 'rotate-180' : ''}`} />
                    </button>
                    {mostrarCheckoutConfig && (
                      <div className="mt-6 pt-6 border-t border-white/5">
                        {planosParaUpgrade.length > 0 ? (
                          <CheckoutAsaasCard
                            planos={planosParaUpgrade}
                            planoSelecionadoId={infoPlanoAtual?.proximo?.toLowerCase()}
                            tipoPerfil="Gestor"
                            onSuccess={() => setTimeout(() => setMostrarCheckoutConfig(false), 2500)}
                          />
                        ) : (
                          <p className="text-xs text-slate-500 text-center py-6">Você já está no plano máximo — não há upgrade disponível.</p>
                        )}
                      </div>
                    )}
                  </div>

                  {planoGestorReal && (
                    <BlocoCancelarAssinatura perfil="gestor" cancelamento={cancelamentoGestorDoc} />
                  )}

                  <div className="space-y-4 pt-6 border-t border-white/5">
                    <div className="flex flex-col md:flex-row md:items-center justify-between p-8 bg-black/20 rounded-[2rem] border border-white/5 gap-4">
                      <div>
                        <p className="text-sm font-black text-white uppercase">Tema do Painel</p>
                        <p className="text-[11px] text-slate-500 mt-2 font-bold tracking-widest">Escolha entre tela escura ou clara. Só afeta este Painel Desktop.</p>
                      </div>
                      <button
                        onClick={() => setTema(t => t === 'light' ? 'dark' : 'light')}
                        className="w-14 h-7 bg-lime-400 rounded-full relative cursor-pointer shadow-inner shrink-0 border border-lime-500 transition-all"
                      >
                        <div className={`w-6 h-6 bg-black rounded-full absolute top-0.5 transition-all flex items-center justify-center ${tema === 'light' ? 'right-0.5' : 'left-0.5'}`}>
                          {tema === 'light' ? <Sun size={12} className="text-lime-400" /> : <Moon size={12} className="text-lime-400" />}
                        </div>
                      </button>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between p-8 bg-black/20 rounded-[2rem] border border-white/5 cursor-pointer hover:bg-white/5 transition-colors gap-4" onClick={abrirEdicao}>
                      <div>
                        <p className="text-sm font-black text-white uppercase">Dados da Empresa</p>
                        <p className="text-[11px] text-slate-500 mt-2 font-bold tracking-widest">Nome, CNPJ, setor, faturamento, custos e dívidas — tudo editável direto por aqui.</p>
                      </div>
                      <span className="flex items-center gap-2 bg-lime-400/10 hover:bg-lime-400/20 text-lime-400 px-5 py-3 rounded-full border border-lime-400/20 text-xs font-black uppercase tracking-widest transition-all shrink-0">
                        <Pencil size={14} /> Editar
                      </span>
                    </div>

                    {/* SOBRE — dados institucionais/comerciais do Phoenix OS,
                        vindos de COMPANY_INFO (constants.js), a mesma fonte
                        usada no app e no Dashboard Consultor. */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between p-8 bg-black/20 rounded-[2rem] border border-white/5 cursor-pointer hover:bg-white/5 transition-colors gap-4" onClick={() => setMostrandoSobre(true)}>
                      <div>
                        <p className="text-sm font-black text-white uppercase">Sobre o Phoenix OS</p>
                        <p className="text-[11px] text-slate-500 mt-2 font-bold tracking-widest">Dados da empresa, contato de suporte, política de privacidade e termos de uso.</p>
                      </div>
                      <span className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-5 py-3 rounded-full border border-white/10 text-xs font-black uppercase tracking-widest transition-all shrink-0">
                        <Info size={14} /> Ver
                      </span>
                    </div>

                    {/* EXCLUIR CONTA — mesma função existente no app e no
                    Dashboard Consultor. Apaga TUDO (Gestor e Consultor,
                    já que é a mesma conta), então fica isolado no fim,
                    com aviso explícito sobre isso. */}
                    <div className="pt-4 border-t border-white/5">
                      <button
                        onClick={() => setConfirmandoExclusaoConta(true)}
                        className="w-full text-red-500 font-bold py-5 text-[11px] uppercase tracking-widest hover:text-red-400 transition-colors"
                      >
                        Excluir Minha Conta
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* CONFIRMAÇÃO DE EXCLUSÃO DE CONTA — passo extra obrigatório pra
            uma ação irreversível e séria como essa. Avisa explicitamente
            que também apaga o perfil Consultor, já que é a mesma conta. */}
        {confirmandoExclusaoConta && (
          <div className="fixed inset-0 z-[600] bg-[#010208]/95 backdrop-blur-xl flex items-center justify-center p-6 animate-in fade-in duration-200">
            <div className="bg-[#020617] border border-red-500/20 p-8 rounded-[3rem] shadow-2xl w-full max-w-sm text-center">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20 mb-6 mx-auto">
                <Trash2 size={28} className="text-red-500" />
              </div>
              <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-3">Excluir sua conta?</h3>
              <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-5 text-left">
                <p className="text-[11px] text-red-400 font-black uppercase tracking-widest mb-1.5">⚠️ Isso apaga a conta inteira, não só o Gestor</p>
                <p className="text-[11px] text-red-300/80 leading-relaxed">
                  Se essa mesma conta também tiver um plano <strong className="text-white">Consultor</strong> ativo, ele será apagado junto — é a mesma conta, um único login pros dois perfis.
                </p>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed mb-8">
                Todas as suas empresas, planos e histórico serão apagados permanentemente, no Dashboard e no app. <strong className="text-white">Sua assinatura ativa (se houver) será cancelada automaticamente no Asaas</strong> — você não continuará sendo cobrado. Essa ação não pode ser desfeita.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmandoExclusaoConta(false)}
                  disabled={excluindoConta}
                  className="flex-1 bg-white/5 text-white py-4 rounded-full font-black uppercase text-xs border border-white/10 hover:bg-white/10 transition-all disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={excluirContaDefinitivamente}
                  disabled={excluindoConta}
                  className="flex-1 bg-red-500 text-white py-4 rounded-full font-black uppercase text-xs hover:bg-red-600 active:scale-95 transition-all disabled:opacity-50"
                >
                  {excluindoConta ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RODAPÉ FIXO — aparece em QUALQUER aba (fica fora do bloco de
            cada aba de propósito, não precisa repetir em cada uma):
            1) Explica o Ecossistema Phoenix OS (app + Dashboard sincronizados)
            2) Lembra o Gestor de manter os dados atualizados
            3) Disclaimer legal, mesmo texto do app */}
        <div className="mt-10 pt-6 border-t border-white/5 space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-center sm:text-left max-w-4xl mx-auto">
            <div className="flex items-center gap-2.5 text-slate-500">
              <RefreshCw size={13} className="text-lime-400/70 shrink-0" />
              <p className="text-[11px] leading-relaxed">
                <strong className="text-slate-400">Ecossistema Phoenix OS:</strong> o app (celular) e este Dashboard (computador) ficam sincronizados em tempo real — acompanhe o dia a dia da empresa em qualquer um dos dois, os dados são os mesmos.
              </p>
            </div>
            <div className="flex items-center gap-2.5 text-slate-500">
              <FileSignature size={13} className="text-amber-400/70 shrink-0" />
              <p className="text-[11px] leading-relaxed">
                <strong className="text-slate-400">Lembrete:</strong> mantenha os valores atualizados. Use "Editar Dados" sempre que faturamento, custos ou dívida mudarem, pra manter o diagnóstico e a IA precisos.
              </p>
            </div>
          </div>
          <p className="text-[11px] text-slate-600 leading-relaxed text-center max-w-4xl mx-auto">
            {DISCLAIMER_FULL}
          </p>
        </div>
      </main>

      {/* MODAL DE CHECKOUT EMBUTIDO — compra/upgrade sem sair do Dashboard.
          Mesma Cloud Function e mesma lógica do app, só que embutida
          direto aqui. */}
      {checkoutAberto && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="phoenix-modal-dark bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scroll">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <CreditCard size={20} className="text-lime-400" />
                <h3 className="text-lg font-black text-white uppercase italic tracking-tighter">Assinar Plano</h3>
              </div>
              <button onClick={() => setCheckoutAberto(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <CheckoutAsaasCard
              planos={planosParaUpgrade}
              planoSelecionadoId={planoSugeridoCheckout}
              tipoPerfil="Gestor"
              onClose={() => setCheckoutAberto(false)}
              onSuccess={() => setTimeout(() => setCheckoutAberto(false), 2500)}
            />
          </div>
        </div>
      )}

      {/* MODAL DE META FINANCEIRA */}
      {editandoMeta && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="phoenix-modal-dark bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-black text-white uppercase italic tracking-tighter">Definir Meta Financeira</h3>
              <button onClick={() => setEditandoMeta(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Tipo de Meta</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMetaTipoForm('faturamento')}
                    className={`py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${metaTipoForm === 'faturamento' ? 'bg-lime-400 text-black' : 'bg-white/5 text-slate-400 border border-white/10'}`}
                  >
                    Faturamento
                  </button>
                  <button
                    onClick={() => setMetaTipoForm('lucro')}
                    className={`py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${metaTipoForm === 'lucro' ? 'bg-lime-400 text-black' : 'bg-white/5 text-slate-400 border border-white/10'}`}
                  >
                    Lucro
                  </button>
                </div>
              </div>
              <CampoInput
                label={`Valor Alvo (R$/mês)`}
                value={metaValorForm}
                onChange={(e) => setMetaValorForm(aplicarMascaraMoeda(e.target.value))}
                placeholder="0,00"
                inputMode="numeric"
              />
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setEditandoMeta(false)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10"
              >
                Cancelar
              </button>
              <button
                onClick={salvarMeta}
                disabled={salvandoMeta || !metaValorForm}
                className="flex-1 flex items-center justify-center gap-2 bg-lime-400 text-black py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all disabled:opacity-50"
              >
                <Save size={14} /> {salvandoMeta ? 'Salvando...' : 'Salvar Meta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SOBRE — dados institucionais/comerciais, mesma fonte
          (COMPANY_INFO) usada no app e no Dashboard Consultor. */}
      {mostrandoSobre && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="bg-[#020617] border border-white/10 rounded-[2rem] p-8 w-full max-w-lg max-h-[85vh] overflow-y-auto custom-scroll">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Info size={20} className="text-lime-400" />
                <h3 className="text-lg font-black text-white uppercase italic tracking-tighter">Sobre o {COMPANY_INFO.nomeFantasia}</h3>
              </div>
              <button onClick={() => setMostrandoSobre(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-5">
              <div className="bg-white/5 rounded-2xl p-5 border border-white/5">
                <p className="text-sm text-slate-300 leading-relaxed">{COMPANY_INFO.descricaoCurta}</p>
              </div>

              <div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-3">Dados da Empresa</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-black/20 rounded-xl p-4">
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Operado por</p>
                    <p className="text-sm text-white font-bold mt-1">{COMPANY_INFO.nomeEmpresa}</p>
                  </div>
                  <div className="bg-black/20 rounded-xl p-4">
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Razão Social</p>
                    <p className="text-sm text-white font-bold mt-1">{COMPANY_INFO.razaoSocial}</p>
                  </div>
                  <div className="bg-black/20 rounded-xl p-4">
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">CNPJ</p>
                    <p className="text-sm text-white font-bold mt-1">{COMPANY_INFO.cnpj}</p>
                  </div>
                  <div className="bg-black/20 rounded-xl p-4 md:col-span-2">
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Endereço</p>
                    <p className="text-sm text-white font-bold mt-1">{COMPANY_INFO.endereco}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-3">Suporte</p>
                <a href={`mailto:${COMPANY_INFO.emailSuporte}`} className="flex items-center gap-3 bg-black/20 rounded-xl p-4 text-sm text-lime-400 font-bold hover:bg-black/30 transition-colors w-fit">
                  <FileText size={14} /> {COMPANY_INFO.emailSuporte}
                </a>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <a href={COMPANY_INFO.linkPoliticaPrivacidade} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-xs font-black text-white uppercase tracking-widest hover:bg-white/10 transition-colors">
                  Política de Privacidade
                </a>
                <a href={COMPANY_INFO.linkTermosDeUso} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-xs font-black text-white uppercase tracking-widest hover:bg-white/10 transition-colors">
                  Termos de Uso
                </a>
              </div>

              <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest text-center pt-2">
                © {COMPANY_INFO.anoFundacao} {COMPANY_INFO.nomeEmpresa} — {COMPANY_INFO.nomeFantasia} • Versão {COMPANY_INFO.versaoApp}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMAÇÃO DE DOWNLOAD DO RELATÓRIO */}
      {confirmandoDownload && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="phoenix-modal-dark bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-sm text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-lime-400/10 border border-lime-400/20 flex items-center justify-center">
              <Download size={22} className="text-lime-400" />
            </div>
            <h3 className="text-base font-black text-white uppercase italic tracking-tighter mb-2">Baixar Relatório?</h3>
            <p className="text-sm text-slate-400 mb-6">
              Vamos gerar o PDF de "{empresa?.name}" com os dados mais recentes salvos.
            </p>
            {empresa?.plan === 'TOTAL' && (
            <div className="grid grid-cols-1 gap-2 mb-6 text-left">
              <button
                type="button"
                onClick={() => setTipoRelatorio('completo')}
                className={`rounded-2xl border p-4 transition-all ${tipoRelatorio === 'completo' ? 'border-lime-400/60 bg-lime-400/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
              >
                <p className="text-xs font-black text-white uppercase tracking-widest">Dossiê Estratégico 360º</p>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">Relatório completo: valuation, análise técnica, projeção por IA, oportunidades e veredito.</p>
              </button>
              <button
                type="button"
                onClick={() => setTipoRelatorio('master')}
                className={`rounded-2xl border p-4 transition-all ${tipoRelatorio === 'master' ? 'border-lime-400/60 bg-lime-400/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
              >
                <p className="text-xs font-black text-white uppercase tracking-widest">Relatório Master (resumo)</p>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">Versão curta e direta, a mesma do app: status, indicadores e parecer em 2 páginas.</p>
              </button>
            </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmandoDownload(false)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10"
              >
                Cancelar
              </button>
              <button
                onClick={() => { setConfirmandoDownload(false); exportarPDF(); }}
                disabled={exportando}
                className="flex-1 bg-lime-400 hover:bg-lime-500 text-black py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all disabled:opacity-50"
              >
                {exportando ? 'Gerando...' : 'Baixar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMAÇÃO DE EXCLUSÃO DE EMPRESA */}
      {/* BLOQUEIO DE DUPLICIDADE — nome igual ou CNPJ (8 primeiros dígitos)
          igual a uma empresa já cadastrada. Não dá pra prosseguir por aqui:
          isso é matriz+filial, que é conceito exclusivo do plano Consultor. */}
      {bloqueioDuplicidade && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="phoenix-modal-dark bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-sm text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <AlertTriangle size={22} className="text-red-400" />
            </div>
            <h3 className="text-base font-black text-white uppercase italic tracking-tighter mb-2">Empresa já cadastrada</h3>
            <p className="text-sm text-slate-400 mb-4">
              O nome ou o CNPJ é igual ao de <strong className="text-white">"{bloqueioDuplicidade.empresaConflitante.name}"</strong>, já cadastrada na sua conta.
            </p>
            <p className="text-xs text-slate-500 mb-8">
              O perfil Gestor é pra empresas independentes. Se isso é uma matriz com filiais (ou uma filial dessa empresa), quem gerencia esse tipo de estrutura é o plano <strong className="text-lime-400">Consultor</strong>, com tela própria de matriz + filiais.
            </p>
            <button
              onClick={() => setBloqueioDuplicidade(null)}
              className="w-full bg-lime-400 text-black py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all"
            >
              Entendi
            </button>
          </div>
        </div>
      )}

      {/* AVISO LEVE DE SEGMENTO IGUAL — não bloqueia, só confirma, já que
          duas empresas independentes podem legitimamente ser do mesmo
          segmento (ex: dois restaurantes de donos diferentes). */}
      {avisoSegmentoIgual && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="phoenix-modal-dark bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-sm text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-amber-400/10 border border-amber-400/20 flex items-center justify-center">
              <AlertTriangle size={22} className="text-amber-400" />
            </div>
            <h3 className="text-base font-black text-white uppercase italic tracking-tighter mb-2">Mesmo segmento de outra empresa sua</h3>
            <p className="text-sm text-slate-400 mb-8">
              Você já tem <strong className="text-white">"{avisoSegmentoIgual.empresaConflitante.name}"</strong> no mesmo segmento. Se forem empresas diferentes, pode seguir normal.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setAvisoSegmentoIgual(null)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10"
              >
                Revisar
              </button>
              <button
                onClick={confirmarApesarDoSegmento}
                disabled={processandoAvisoSegmento}
                className="flex-1 bg-lime-400 text-black py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all disabled:opacity-50"
              >
                {processandoAvisoSegmento ? 'Salvando...' : 'Continuar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {empresaParaExcluir && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="phoenix-modal-dark bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-sm text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <Trash2 size={22} className="text-red-400" />
            </div>
            <h3 className="text-base font-black text-white uppercase italic tracking-tighter mb-2">Excluir Empresa?</h3>
            <p className="text-sm text-slate-400 mb-2">
              "{empresaParaExcluir.name}" e todo o histórico salvo dela (diagnósticos, tarefas, anotações) serão removidos permanentemente.
            </p>
            <p className="text-xs text-red-400/80 mb-8 font-bold">Essa ação também remove a empresa do app — não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setEmpresaParaExcluir(null)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10"
              >
                Cancelar
              </button>
              <button
                onClick={excluirEmpresaConfirmada}
                disabled={excluindoEmpresa}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all disabled:opacity-50"
              >
                {excluindoEmpresa ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMAÇÃO DE EXCLUSÃO DE TAREFA */}
      {tarefaParaExcluir && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="phoenix-modal-dark bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-sm text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <Trash2 size={22} className="text-red-400" />
            </div>
            <h3 className="text-base font-black text-white uppercase italic tracking-tighter mb-2">Excluir Tarefa?</h3>
            <p className="text-sm text-slate-400 mb-8">
              "{tarefaParaExcluir.texto}" será removida permanentemente. Essa ação não pode ser desfeita.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setTarefaParaExcluir(null)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10"
              >
                Cancelar
              </button>
              <button
                onClick={() => excluirTarefa(tarefaParaExcluir.id)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3.5 rounded-full font-black uppercase text-xs tracking-widest transition-all"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE NOVA EMPRESA — grava no formato "solto" que o app usa,
          pra aparecer certinho lá também. Mesmo aviso de "1 empresa por
          Gestor" que já existe no fluxo de criação de conta do app. */}
      {criandoEmpresa && formNovaEmpresa && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="phoenix-modal-dark bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto custom-scroll">
            {salvandoNovaEmpresa ? (
              /* SCANNING — mesmo efeito visual do Dashboard Consultor, deixa
                 claro que está sincronizando de verdade com o Firestore. */
              <div className="flex flex-col items-center justify-center py-10 px-4">
                <div className="relative w-40 h-40 flex items-center justify-center mb-8">
                  <div className="absolute inset-0 bg-lime-400/5 rounded-full blur-2xl animate-pulse"></div>
                  <svg className="w-full h-full transform -rotate-90 drop-shadow-[0_0_20px_rgba(190,242,100,0.3)]" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="54" fill="none" stroke="#1e293b" strokeWidth="4" />
                    <circle cx="60" cy="60" r="54" fill="none" stroke="#bef264" strokeWidth="4"
                      strokeDasharray={2 * Math.PI * 54}
                      strokeDashoffset={2 * Math.PI * 54 - (progressoCadastroEmpresa / 100) * (2 * Math.PI * 54)}
                      strokeLinecap="round" className="transition-all duration-300 ease-out" />
                  </svg>
                  <div className="absolute flex flex-col items-center justify-center">
                    <span className="text-3xl font-black text-white italic tracking-tighter">{progressoCadastroEmpresa}%</span>
                  </div>
                </div>
                <h4 className="text-white font-black text-base uppercase italic tracking-tighter leading-none mb-2">
                  {progressoCadastroEmpresa >= 100 ? 'Empresa Cadastrada!' : 'Sincronizando com o Phoenix OS...'}
                </h4>
                <p className="text-slate-500 text-[11px] font-black uppercase tracking-[0.3em] mb-8">
                  {progressoCadastroEmpresa >= 100 ? 'Disponível no app e no dashboard' : formNovaEmpresa.companyName}
                </p>
                <div className="w-full space-y-2.5">
                  {["Matrix Engine v4.2", "Solvency Analytics", "Risk Mapping AI", "Fiscal Compliance Core", "EBITDA Predictor"].map((eng, idx) => {
                    const isActive = progressoCadastroEmpresa >= (idx * 20) && progressoCadastroEmpresa < ((idx + 1) * 20);
                    const isDone = progressoCadastroEmpresa >= ((idx + 1) * 20);
                    return (
                      <div key={idx} className={`p-3 rounded-xl border transition-all duration-500 flex items-center gap-3 ${isDone ? 'bg-lime-400/5 border-lime-400/20' : isActive ? 'bg-white/5 border-white/20 scale-[1.02]' : 'bg-transparent border-transparent opacity-20'}`}>
                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                          {isDone ? (
                            <CheckCircle2 size={16} className="text-lime-400" />
                          ) : isActive ? (
                            <div className="w-3.5 h-3.5 border-2 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-600"></div>
                          )}
                        </div>
                        <span className={`text-[11px] font-black uppercase tracking-widest ${isDone ? 'text-lime-400' : isActive ? 'text-white' : 'text-slate-500'}`}>
                          {eng}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Building2 size={20} className="text-lime-400" />
                    <h3 className="text-lg font-black text-white uppercase italic tracking-tighter">Nova Empresa</h3>
                  </div>
                  <button onClick={() => setCriandoEmpresa(false)} className="text-slate-500 hover:text-white transition-colors">
                    <X size={20} />
                  </button>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-3 mb-5">
                  <p className="text-[11px] text-amber-400 font-bold leading-relaxed">
                    ⚠️ O perfil Gestor foi pensado pra gerenciar <strong>1 empresa</strong>. Se você precisa acompanhar filiais ou múltiplos CNPJs, o perfil <strong>Consultor</strong> é mais indicado.
                  </p>
                </div>

                <div className="space-y-5">
                  {/* PROFUNDIDADE DA ANÁLISE — mesma escolha que o app oferece
                      no onboarding (Executivo=claro / Avançado=técnico). Sem
                      isso, toda empresa criada pelo Dashboard saía sempre no
                      tom claro, sem chance de escolher. */}
                  <div>
                    <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Profundidade da Análise</label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: 'Executivo', title: 'Visão Executiva', desc: 'Clara e Direta' },
                        { id: 'Avançado', title: 'Visão Avançada', desc: 'Técnica / Profunda' },
                      ].map(lvl => (
                        <button
                          key={lvl.id}
                          type="button"
                          onClick={() => setFormNovaEmpresa(f => ({ ...f, expertise: lvl.id }))}
                          className={`p-3.5 rounded-2xl border-2 transition-all text-center ${formNovaEmpresa.expertise === lvl.id ? 'bg-lime-400/10 border-lime-400' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                        >
                          <span className={`block font-black uppercase text-xs ${formNovaEmpresa.expertise === lvl.id ? 'text-lime-400' : 'text-white'}`}>{lvl.title}</span>
                          <span className="block text-[11px] text-slate-500 font-bold uppercase tracking-widest mt-1">{lvl.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* IDENTIFICAÇÃO — Nome / CNPJ / Setor lado a lado */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <CampoInput
                      label="Nome da Empresa"
                      value={formNovaEmpresa.companyName}
                      onChange={(e) => setFormNovaEmpresa(f => ({ ...f, companyName: e.target.value }))}
                      placeholder="Nome da empresa"
                    />
                    <CampoInput
                      label="CNPJ"
                      value={formNovaEmpresa.cnpj}
                      onChange={(e) => setFormNovaEmpresa(f => ({ ...f, cnpj: e.target.value }))}
                      placeholder="00.000.000/0000-00"
                    />
                    <div>
                      <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Setor</label>
                      <select
                        value={formNovaEmpresa.segment}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, segment: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
                      >
                        <option value="" className="bg-[#0a0a12]">Selecione...</option>
                        {SETORES.map(s => <option key={s} value={s} className="bg-[#0a0a12]">{s}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Aviso/bloqueio AO VIVO — aparece assim que detecta, sem
                      esperar o clique de Salvar. */}
                  {checagemNovaEmpresa.bloqueado && (
                    <div className="mt-3 flex items-start gap-2.5 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                      <AlertTriangle size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-300">
                        {checagemNovaEmpresa.motivo === 'filial' ? (
                          <>Esse CNPJ é de outra <strong>unidade da mesma empresa</strong> que <strong>"{checagemNovaEmpresa.empresaConflitante.name}"</strong> (mesma raiz de CNPJ, ou seja, matriz e filial). Pra acompanhar matriz + filiais e comparar unidades, é preciso o plano <strong className="text-lime-400">Consultor</strong>.</>
                        ) : (
                          <>Nome ou CNPJ igual a <strong>"{checagemNovaEmpresa.empresaConflitante.name}"</strong>, já cadastrada. Pra gerenciar matriz + filiais, é preciso o plano <strong className="text-lime-400">Consultor</strong>.</>
                        )}
                      </p>
                    </div>
                  )}
                  {checagemNovaEmpresa.avisoSegmento && (
                    <div className="mt-3 flex items-start gap-2.5 bg-amber-400/10 border border-amber-400/20 rounded-xl px-4 py-3">
                      <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-200">
                        Mesmo segmento de <strong>"{checagemNovaEmpresa.empresaConflitante.name}"</strong>. Se forem empresas diferentes, pode seguir normal.
                      </p>
                    </div>
                  )}

                  <CampoInput
                    label="Nicho (seja específico)"
                    value={formNovaEmpresa.niche}
                    onChange={(e) => setFormNovaEmpresa(f => ({ ...f, niche: e.target.value }))}
                    placeholder="Ex: restaurante vegano, e-commerce de moda..."
                  />

                  {/* DADOS FINANCEIROS — 3 colunas, cabe quase tudo sem rolar */}
                  <div className="pt-2 border-t border-white/5">
                    <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-4">Dados Financeiros</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <CampoInput
                        label={formNovaEmpresa.segment === 'Holding / Grupo' ? 'Receita Operacional Mensal (R$)' : 'Faturamento Mensal (R$)'}
                        tooltip={formNovaEmpresa.segment === 'Holding / Grupo'
                          ? 'Dividendos, aluguéis e JCP recebidos das controladas por mês — não é "faturamento" no sentido tradicional.'
                          : 'Tudo que entra na empresa por mês, antes de descontar qualquer custo — a receita bruta.'}
                        value={formNovaEmpresa.revenue}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, revenue: aplicarMascaraMoeda(e.target.value) }))}
                        placeholder="0,00"
                        inputMode="numeric"
                      />
                      <CampoInput
                        label={formNovaEmpresa.segment === 'Holding / Grupo' ? 'Custos Administrativos (R$)' : 'Custos Totais (R$)'}
                        tooltip={formNovaEmpresa.segment === 'Holding / Grupo'
                          ? 'Despesas de gestão, contábeis e financeiras da própria holding — não inclui o custo operacional das controladas.'
                          : 'Soma de todos os gastos mensais da empresa: fixos e variáveis, incluindo fornecedores, folha, aluguel etc.'}
                        value={formNovaEmpresa.costs}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, costs: aplicarMascaraMoeda(e.target.value) }))}
                        placeholder="0,00"
                        inputMode="numeric"
                      />
                      <CampoInput
                        label="Dívida Bancária (R$)"
                        tooltip={formNovaEmpresa.segment === 'Holding / Grupo'
                          ? 'Inclua também dívidas das controladas garantidas pela holding — risco de "cross-default" (uma controlada em default pode acionar a dívida da holding inteira).'
                          : 'Saldo total devedor em empréstimos e financiamentos bancários, de qualquer prazo.'}
                        tooltipAlign="end"
                        value={formNovaEmpresa.debt}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, debt: aplicarMascaraMoeda(e.target.value) }))}
                        placeholder="0,00"
                        inputMode="numeric"
                      />
                      <CampoInput
                        label="Dívida Curto Prazo (R$)"
                        tooltip="Parte da dívida bancária que vence nos próximos 12 meses — usado pra calcular o risco de aperto no caixa."
                        value={formNovaEmpresa.dividaCurtoPrazo}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, dividaCurtoPrazo: aplicarMascaraMoeda(e.target.value) }))}
                        placeholder="0,00"
                        inputMode="numeric"
                      />
                      <CampoInput
                        label="Custo Não Recorrente (R$)"
                        tooltip="Gastos pontuais que não se repetem todo mês (ex: uma reforma, uma multa, uma compra única de equipamento)."
                        value={formNovaEmpresa.custoNaoRecorrente}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, custoNaoRecorrente: aplicarMascaraMoeda(e.target.value) }))}
                        placeholder="0,00"
                        inputMode="numeric"
                      />
                      <AvisoCustoNaoRecorrente className="col-span-full" custos={formNovaEmpresa.costs} naoRecorrente={formNovaEmpresa.custoNaoRecorrente} />
                      <CampoInput
                        label="Custo da Dívida (% a.a.)"
                        tooltip="Taxa de juros média que a empresa paga nas dívidas bancárias, ao ano."
                        tooltipAlign="end"
                        value={formNovaEmpresa.custoDivida}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, custoDivida: e.target.value }))}
                        placeholder="0,00"
                      />
                      <CampoInput
                        label="Prazo de Recebimento (dias)"
                        tooltip="Em média, quantos dias a empresa demora pra receber dos clientes depois de uma venda."
                        value={formNovaEmpresa.prazoRecebimento}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, prazoRecebimento: e.target.value }))}
                        placeholder="Ex: 30"
                        type="number"
                      />
                      <CampoInput
                        label="Prazo de Pagamento (dias)"
                        tooltip="Em média, quantos dias a empresa leva pra pagar seus fornecedores depois de uma compra."
                        value={formNovaEmpresa.prazoPagamento}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, prazoPagamento: e.target.value }))}
                        placeholder="Ex: 30"
                        type="number"
                      />
                      <CampoInput
                        label="Pró-labore Mensal (R$)"
                        tooltip="Salário que o(s) sócio(s) retira(m) da empresa todo mês pelo trabalho, separado do lucro do negócio."
                        tooltipAlign="end"
                        value={formNovaEmpresa.proLabore}
                        onChange={(e) => setFormNovaEmpresa(f => ({ ...f, proLabore: aplicarMascaraMoeda(e.target.value) }))}
                        placeholder="0,00"
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <button
                    onClick={() => setCriandoEmpresa(false)}
                    className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-4 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleClickSalvarNovaEmpresa}
                    disabled={!formNovaEmpresa.companyName.trim() || !formNovaEmpresa.revenue || !formNovaEmpresa.costs || checagemNovaEmpresa.bloqueado}
                    className="flex-1 flex items-center justify-center gap-2 bg-lime-400 text-black py-4 rounded-full font-black uppercase text-xs tracking-widest transition-all disabled:opacity-50"
                  >
                    <Save size={14} /> Salvar Empresa
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* MODAL DE EDIÇÃO — atualiza a empresa direto pelo Firestore, sem
          precisar abrir o app. Mesmo formato de dados que o app grava.
          🔥 REDESENHADO: era uma coluna única, obrigando rolar bastante
          pra ver todos os campos. Agora é mais largo (retângulo
          horizontal) com os campos organizados em grade de 3 colunas —
          dá pra ver o formulário quase inteiro de uma vez. */}
      {editando && formEdicao && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <div className="phoenix-modal-dark bg-[#0a0a12] border border-white/10 rounded-[2rem] p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto custom-scroll">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Building2 size={20} className="text-lime-400" />
                <h3 className="text-lg font-black text-white uppercase italic tracking-tighter">Editar Empresa</h3>
              </div>
              <button onClick={() => setEditando(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-5">
              {/* PROFUNDIDADE DA ANÁLISE — mesma escolha do onboarding do app.
                  Permite corrigir aqui pelo Dashboard caso o parecer esteja
                  saindo no tom errado (ex: sempre "claro" quando deveria ser
                  "técnico", ou vice-versa). */}
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Profundidade da Análise</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'Executivo', title: 'Visão Executiva', desc: 'Clara e Direta' },
                    { id: 'Avançado', title: 'Visão Avançada', desc: 'Técnica / Profunda' },
                  ].map(lvl => (
                    <button
                      key={lvl.id}
                      type="button"
                      onClick={() => setFormEdicao(f => ({ ...f, expertise: lvl.id }))}
                      className={`p-3.5 rounded-2xl border-2 transition-all text-center ${formEdicao.expertise === lvl.id ? 'bg-lime-400/10 border-lime-400' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                    >
                      <span className={`block font-black uppercase text-xs ${formEdicao.expertise === lvl.id ? 'text-lime-400' : 'text-white'}`}>{lvl.title}</span>
                      <span className="block text-[11px] text-slate-500 font-bold uppercase tracking-widest mt-1">{lvl.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* IDENTIFICAÇÃO — Nome / CNPJ / Setor lado a lado */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <CampoInput
                  label="Nome da Empresa"
                  value={formEdicao.companyName}
                  onChange={(e) => setFormEdicao(f => ({ ...f, companyName: e.target.value }))}
                  placeholder="Nome da empresa"
                />
                <CampoInput
                  label="CNPJ"
                  value={formEdicao.cnpj}
                  onChange={(e) => setFormEdicao(f => ({ ...f, cnpj: e.target.value }))}
                  placeholder="00.000.000/0000-00"
                />
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Setor</label>
                  <select
                    value={formEdicao.segment}
                    onChange={(e) => setFormEdicao(f => ({ ...f, segment: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-lime-400/50 transition-colors"
                  >
                    {SETORES.map(s => <option key={s} value={s} className="bg-[#0a0a12]">{s}</option>)}
                  </select>
                </div>
              </div>

              {checagemEdicaoEmpresa.bloqueado && (
                <div className="mt-3 flex items-start gap-2.5 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                  <AlertTriangle size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-300">
                    {checagemEdicaoEmpresa.motivo === 'filial' ? (
                      <>Esse CNPJ é de outra <strong>unidade da mesma empresa</strong> que <strong>"{checagemEdicaoEmpresa.empresaConflitante.name}"</strong> (mesma raiz de CNPJ, ou seja, matriz e filial). Pra acompanhar matriz + filiais e comparar unidades, é preciso o plano <strong className="text-lime-400">Consultor</strong>.</>
                    ) : (
                      <>Nome ou CNPJ igual a <strong>"{checagemEdicaoEmpresa.empresaConflitante.name}"</strong>, já cadastrada. Pra gerenciar matriz + filiais, é preciso o plano <strong className="text-lime-400">Consultor</strong>.</>
                    )}
                  </p>
                </div>
              )}
              {checagemEdicaoEmpresa.avisoSegmento && (
                <div className="mt-3 flex items-start gap-2.5 bg-amber-400/10 border border-amber-400/20 rounded-xl px-4 py-3">
                  <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-200">
                    Mesmo segmento de <strong>"{checagemEdicaoEmpresa.empresaConflitante.name}"</strong>. Se forem empresas diferentes, pode seguir normal.
                  </p>
                </div>
              )}

              <CampoInput
                label="Nicho (seja específico)"
                value={formEdicao.niche}
                onChange={(e) => setFormEdicao(f => ({ ...f, niche: e.target.value }))}
                placeholder="Ex: restaurante vegano, e-commerce de moda..."
              />

              {/* DADOS FINANCEIROS — 3 colunas, tooltip alinhado conforme a
                  posição (esquerda/meio "start", direita "end") pra nunca
                  estourar pra fora do modal. */}
              <div className="pt-2 border-t border-white/5">
                <p className="text-[11px] text-lime-400 uppercase tracking-widest font-bold mb-4">Dados Financeiros</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <CampoInput
                    label={formEdicao.segment === 'Holding / Grupo' ? 'Receita Operacional Mensal (R$)' : 'Faturamento Mensal (R$)'}
                    tooltip={formEdicao.segment === 'Holding / Grupo'
                      ? 'Dividendos, aluguéis e JCP recebidos das controladas por mês — não é "faturamento" no sentido tradicional.'
                      : 'Tudo que entra na empresa por mês, antes de descontar qualquer custo — a receita bruta.'}
                    value={formEdicao.revenue}
                    onChange={(e) => setFormEdicao(f => ({ ...f, revenue: aplicarMascaraMoeda(e.target.value) }))}
                    placeholder="0,00"
                    inputMode="numeric"
                  />
                  <CampoInput
                    label={formEdicao.segment === 'Holding / Grupo' ? 'Custos Administrativos (R$)' : 'Custos Totais (R$)'}
                    tooltip={formEdicao.segment === 'Holding / Grupo'
                      ? 'Despesas de gestão, contábeis e financeiras da própria holding — não inclui o custo operacional das controladas.'
                      : 'Soma de todos os gastos mensais da empresa: fixos e variáveis, incluindo fornecedores, folha, aluguel etc.'}
                    value={formEdicao.costs}
                    onChange={(e) => setFormEdicao(f => ({ ...f, costs: aplicarMascaraMoeda(e.target.value) }))}
                    placeholder="0,00"
                    inputMode="numeric"
                  />
                  <CampoInput
                    label="Dívida Bancária (R$)"
                    tooltip={formEdicao.segment === 'Holding / Grupo'
                      ? 'Inclua também dívidas das controladas garantidas pela holding — risco de "cross-default" (uma controlada em default pode acionar a dívida da holding inteira).'
                      : 'Saldo total devedor em empréstimos e financiamentos bancários, de qualquer prazo.'}
                    tooltipAlign="end"
                    value={formEdicao.debt}
                    onChange={(e) => setFormEdicao(f => ({ ...f, debt: aplicarMascaraMoeda(e.target.value) }))}
                    placeholder="0,00"
                    inputMode="numeric"
                  />
                  <CampoInput
                    label="Dívida Curto Prazo (R$)"
                    tooltip="Parte da dívida bancária que vence nos próximos 12 meses — usado pra calcular o risco de aperto no caixa."
                    value={formEdicao.dividaCurtoPrazo}
                    onChange={(e) => setFormEdicao(f => ({ ...f, dividaCurtoPrazo: aplicarMascaraMoeda(e.target.value) }))}
                    placeholder="0,00"
                    inputMode="numeric"
                  />
                  <CampoInput
                    label="Custo Não Recorrente (R$)"
                    tooltip="Gastos pontuais que não se repetem todo mês (ex: uma reforma, uma multa, uma compra única de equipamento)."
                    value={formEdicao.custoNaoRecorrente}
                    onChange={(e) => setFormEdicao(f => ({ ...f, custoNaoRecorrente: aplicarMascaraMoeda(e.target.value) }))}
                    placeholder="0,00"
                    inputMode="numeric"
                  />
                      <AvisoCustoNaoRecorrente className="col-span-full" custos={formEdicao.costs} naoRecorrente={formEdicao.custoNaoRecorrente} />
                  <CampoInput
                    label="Custo da Dívida (% a.a.)"
                    tooltip="Taxa de juros média que a empresa paga nas dívidas bancárias, ao ano."
                    tooltipAlign="end"
                    value={formEdicao.custoDivida}
                    onChange={(e) => setFormEdicao(f => ({ ...f, custoDivida: e.target.value }))}
                    placeholder="0,00"
                  />
                  <CampoInput
                    label="Prazo de Recebimento (dias)"
                    tooltip="Em média, quantos dias a empresa demora pra receber dos clientes depois de uma venda."
                    value={formEdicao.prazoRecebimento}
                    onChange={(e) => setFormEdicao(f => ({ ...f, prazoRecebimento: e.target.value }))}
                    placeholder="Ex: 30"
                    type="number"
                  />
                  <CampoInput
                    label="Prazo de Pagamento (dias)"
                    tooltip="Em média, quantos dias a empresa leva pra pagar seus fornecedores depois de uma compra."
                    value={formEdicao.prazoPagamento}
                    onChange={(e) => setFormEdicao(f => ({ ...f, prazoPagamento: e.target.value }))}
                    placeholder="Ex: 30"
                    type="number"
                  />
                  <CampoInput
                    label="Pró-labore Mensal (R$)"
                    tooltip="Salário que o(s) sócio(s) retira(m) da empresa todo mês pelo trabalho, separado do lucro do negócio."
                    tooltipAlign="end"
                    value={formEdicao.proLabore}
                    onChange={(e) => setFormEdicao(f => ({ ...f, proLabore: aplicarMascaraMoeda(e.target.value) }))}
                    placeholder="0,00"
                    inputMode="numeric"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setEditando(false)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-4 rounded-full font-black uppercase text-xs tracking-widest transition-all border border-white/10"
              >
                Cancelar
              </button>
              <button
                onClick={handleClickSalvarEdicao}
                disabled={salvandoEdicao || !formEdicao.companyName || checagemEdicaoEmpresa.bloqueado}
                className="flex-1 flex items-center justify-center gap-2 bg-lime-400 text-black py-4 rounded-full font-black uppercase text-xs tracking-widest transition-all disabled:opacity-50"
              >
                <Save size={14} /> {salvandoEdicao ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MOTOR INVISÍVEL DO BLOCO DE NOTAS — página simples de Tarefas +
          Anotações, no mesmo estilo visual do relatório oficial, mas sem
          o peso de todo o motor ReportEngine (não precisa de EBITDA,
          valuation, etc). Fica escondida, só usada pelo html2pdf. */}
      {empresa && (
        <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '800px', zIndex: -1 }}>
          <div ref={blocoNotasRef}>
            <div style={{
              width: '800px', minHeight: '1131px', backgroundColor: '#ffffff', boxSizing: 'border-box',
              padding: '60px 80px', fontFamily: 'Arial, sans-serif', color: '#0f172a',
              display: 'flex', flexDirection: 'column'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <div style={{ width: '34px', height: '34px', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: '16px', height: '16px', border: '2px solid #ffffff' }}></div>
                </div>
                <span style={{ fontSize: '18px', fontWeight: '900', letterSpacing: '-0.5px' }}>
                  PHOENIX<span style={{ color: '#65a30d' }}>OS</span>
                </span>
              </div>
              <p style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 40px 0' }}>
                Bloco de Notas de Trabalho
              </p>

              <div style={{ borderBottom: '2px solid #0f172a', paddingBottom: '14px', marginBottom: '24px' }}>
                <h1 style={{ fontSize: '28px', fontWeight: '900', margin: '0 0 4px 0', textTransform: 'uppercase' }}>{empresa.name}</h1>
                <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>
                  Gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>

              <h3 style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 14px 0', color: '#0f172a' }}>Tarefas</h3>
              {tarefas.length === 0 ? (
                <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '30px' }}>Nenhuma tarefa registrada.</p>
              ) : (
                <div style={{ marginBottom: '30px' }}>
                  {tarefas.map((t, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{
                        width: '14px', height: '14px', border: `2px solid ${t.concluida ? '#65a30d' : '#cbd5e1'}`,
                        borderRadius: '3px', backgroundColor: t.concluida ? '#65a30d' : 'transparent', flexShrink: 0, marginTop: '1px'
                      }}></div>
                      <p style={{ fontSize: '12px', margin: 0, flex: 1, color: t.concluida ? '#94a3b8' : '#0f172a', textDecoration: t.concluida ? 'line-through' : 'none' }}>
                        {t.texto}
                      </p>
                      {t.prazo && (
                        <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{new Date(t.prazo).toLocaleDateString('pt-BR')}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <h3 style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 14px 0', color: '#0f172a' }}>Anotações</h3>
              {notas.length === 0 ? (
                <p style={{ fontSize: '11px', color: '#94a3b8' }}>Nenhuma anotação registrada.</p>
              ) : (
                <div>
                  {notas.map((n, i) => (
                    <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <p style={{ fontSize: '12px', margin: '0 0 3px 0', color: '#334155', lineHeight: '1.5' }}>{n.texto}</p>
                      {n.data && <p style={{ fontSize: '9px', color: '#94a3b8', margin: 0 }}>{new Date(n.data).toLocaleString('pt-BR')}</p>}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 'auto', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Phoenix OS® — Bloco de Notas (documento de trabalho, não faz parte do dossiê financeiro oficial)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MOTOR INVISÍVEL DO PDF — mesmo ReportEngine.jsx do app mobile e do Dashboard Consultor.
          🔥 CORREÇÃO: antes passava insights={[]} (vazio — a seção de parecer saía em branco
          no PDF) e isConsultor={false} fixo (tom sempre simples, mesmo quando o dossiê é
          "Avançado"). Agora usa os mesmos insights e o mesmo tom calculado pra tela. */}
      {dadosParaPDF && (
        <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '800px', zIndex: -1 }}>
          <ReportEngine
            data={dadosParaPDF}
            userPlan={empresa.plan}
            basicRef={basicPdfRef}
            superRef={superPdfRef}
            insights={insights}
            status={health}
            isConsultor={usaTomTecnico}
            isRealConsultor={false}
            projecaoEstocastica={projecaoPorPrazo}
            leituraCurto={empresa.leituraProjecaoCurto}
            leituraMedio={empresa.leituraProjecaoMedio}
            leituraLongo={empresa.leituraProjecaoLongo}
            horizontesLiberados={HORIZONTES_POR_PLANO_GESTOR[String(planoGestorReal || '').toLowerCase()] || []}
            oportunidadesIA={empresa.oportunidadesIA}
          />
        </div>
      )}
    </div>
  );
}
